import * as THREE from "three"
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js"
import {
  cablingPlanSettingsEqual,
  cloneCablingPlanSettings,
  cloneViewerSettings,
  findReferenceLayerById,
  initialReferenceLayerId,
  normalizeReferenceLayer,
  referenceLayersFromDataset,
  syncLegacyViewerDatasetFromConfig,
  viewerSettingsEqual,
  viewerSettingsFromDataset,
  viewerSettingsFromPayload
} from "./plan_viewer/config"
import { exportGlb, fetchCachedArrayBuffer, parseGltfFromArrayBuffer } from "./plan_viewer/model_io"
import { createPopupController } from "./plan_viewer/popup"
import { createSearchController } from "./plan_viewer/search"
import { createViewerCameraController } from "./plan_viewer/viewer_camera"
import { createViewerMappingPanelController } from "./plan_viewer/viewer_mapping_panel"
import { createViewerMenusController } from "./plan_viewer/viewer_menus"
import { createViewerModesController } from "./plan_viewer/viewer_modes"
import { createViewerMutationsController } from "./plan_viewer/viewer_mutations"
import { createViewerSelectionController } from "./plan_viewer/viewer_selection"
import { createViewerSettingsController } from "./plan_viewer/viewer_settings"
import {
  buildNodeKey,
  buildTransparentCablingPlanTextureFromImage,
  buildTransparentCablingPlanTexture,
  defaultSceneFraming,
  frameObject,
  hasUsefulNodeName,
  measureGroundSnapOffsetY,
  resolveNavigationPointRoot,
  resolvePlacementRoot,
  resolveSelectableObject,
  normalizeNavigationPoint,
  snapModelToGround
} from "./plan_viewer/scene_helpers"
import {
  clampNumber,
  createOverlay,
  csrfToken,
  escapeHtml,
  fitRectWithinRect,
  formatPosition,
  is2DLikeMode,
  isOrthographic2DMode,
  isPerspective2DMode,
  normalizePlacementVerticalMode,
  normalizeThingKind,
  parseBooleanOr,
  parseFloatOr,
  slugify
} from "./plan_viewer/shared"
import {
  buildNavigationPointVisual,
  buildPlacementVisual,
  BUILT_IN_PLACEMENT_OBJECT_TYPES,
  placementKindIconDefinition,
  placementKindIconTexture,
  placementKindIconMarkup,
  placementObjectTypeLabel,
  placementVisualUnitSize,
  renderLiveDetailBlocks
} from "./plan_viewer/visuals"
import { createViewerEmbedController } from "./plan_viewer/viewer_embed"

export const renderPlanViewer = (element) => {
  const { mode: viewerMode, capabilities: viewerCapabilities } = syncLegacyViewerDatasetFromConfig(element)
  let viewerConfig = {}
  try {
    viewerConfig = JSON.parse(element.dataset.viewerConfig || "{}")
  } catch (_error) {
    viewerConfig = {}
  }
  const shell = element.closest(".plan-viewer-shell")
  const stage = element.closest(".plan-viewer-stage")
  const toolbar = stage?.querySelector(".plan-viewer-toolbar")
  const panel = shell?.querySelector(".plan-viewer-panel")
  const mappingPanel = panel?.querySelector('[data-viewer-panel-content="mapping"]')
  const cablingPlanPanel = panel?.querySelector('[data-viewer-panel-content="viewer-settings"]')
  const labelsLayer = element.querySelector("[data-viewer-hotspots]")
  const popup = element.querySelector("[data-viewer-popup]")
  const popupKicker = popup?.querySelector("[data-popup-kicker]")
  const popupTitle = popup?.querySelector("[data-popup-title]")
  const popupMeta = popup?.querySelector("[data-popup-meta]")
  const popupAttributes = popup?.querySelector("[data-popup-attributes]")
  const popupClose = popup?.querySelector("[data-popup-close]")
  const popupRefresh = popup?.querySelector("[data-popup-refresh]")
  const searchRoot = toolbar?.querySelector("[data-viewer-search]")
  const labelsMenu = toolbar?.querySelector("[data-viewer-labels-menu]")
  const labelsPanel = toolbar?.querySelector("[data-viewer-labels-panel]")
  const labelsMarkersList = toolbar?.querySelector("[data-viewer-labels-markers]")
  const labelsObjectTypesList = toolbar?.querySelector("[data-viewer-labels-object-types]")
  const labelsThingTypesList = toolbar?.querySelector("[data-viewer-labels-thing-types]")
  const labelsIntegrationsList = toolbar?.querySelector("[data-viewer-labels-integrations]")
  const labelsIntegrationObjectTypesList = toolbar?.querySelector("[data-viewer-labels-integration-object-types]")
  const labelsShowAllButton = toolbar?.querySelector("[data-viewer-labels-show-all]")
  const labelsHideAllButton = toolbar?.querySelector("[data-viewer-labels-hide-all]")
  const referenceLayerMenu = toolbar?.querySelector("[data-viewer-reference-layer-menu]")
  const referenceLayerPanel = toolbar?.querySelector("[data-viewer-reference-layer-panel]")
  const referenceLayerMenuList = toolbar?.querySelector("[data-viewer-reference-layer-list]")
  const exportMenu = toolbar?.querySelector("[data-viewer-export-menu]")
  const exportPanel = toolbar?.querySelector("[data-viewer-export-panel]")
  const exportButtons = Array.from(toolbar?.querySelectorAll("[data-viewer-export-format]") || [])
  const searchInput = searchRoot?.querySelector("[data-viewer-search-input]")
  const searchResults = searchRoot?.querySelector("[data-viewer-search-results]")
  const feedbackEl = panel?.querySelector("[data-mapping-feedback]")
  const viewerSettingsFeedbackEl = panel?.querySelector("[data-viewer-settings-feedback]")
  const viewerSettingsForm = panel?.querySelector("[data-viewer-settings-form]")
  const viewerSettingsSaveButton = panel?.querySelector("[data-viewer-settings-save]")
  const viewerSettingsResetButton = panel?.querySelector("[data-viewer-settings-reset]")
  const viewerSet3DCurrentButton = panel?.querySelector("[data-viewer-set-3d-current]")
  const viewerSet2DCurrentButton = panel?.querySelector("[data-viewer-set-2d-current]")
  const viewerSetWalkthroughCurrentButton = panel?.querySelector("[data-viewer-set-walkthrough-current]")
  const viewerSettingsAccordionButtons = Array.from(panel?.querySelectorAll("[data-viewer-settings-accordion]") || [])
  const viewerSettingsAccordionPanels = Array.from(panel?.querySelectorAll("[data-viewer-settings-accordion-panel]") || [])
  const viewerSettingsSubsectionButtons = Array.from(panel?.querySelectorAll("[data-viewer-settings-subsection]") || [])
  const viewerSettingsSubsectionPanels = Array.from(panel?.querySelectorAll("[data-viewer-settings-subsection-panel]") || [])
  const viewerSettingInputs = {
    objectIconScale: panel?.querySelector('[data-viewer-setting="objectIconScale"]'),
    defaultViewMode: panel?.querySelector('[data-viewer-setting="defaultViewMode"]'),
    defaultYawDegrees: panel?.querySelector('[data-viewer-setting="defaultYawDegrees"]'),
    defaultPitchDegrees: panel?.querySelector('[data-viewer-setting="defaultPitchDegrees"]'),
    defaultZoomMultiplier: panel?.querySelector('[data-viewer-setting="defaultZoomMultiplier"]'),
    default2DZoomMultiplier: panel?.querySelector('[data-viewer-setting="default2DZoomMultiplier"]'),
    default2DOffsetX: panel?.querySelector('[data-viewer-setting="default2DOffsetX"]'),
    default2DOffsetZ: panel?.querySelector('[data-viewer-setting="default2DOffsetZ"]'),
    defaultVerticalBias: panel?.querySelector('[data-viewer-setting="defaultVerticalBias"]'),
    walkthroughOffsetX: panel?.querySelector('[data-viewer-setting="walkthroughOffsetX"]'),
    walkthroughOffsetZ: panel?.querySelector('[data-viewer-setting="walkthroughOffsetZ"]'),
    walkthroughYawDegrees: panel?.querySelector('[data-viewer-setting="walkthroughYawDegrees"]'),
    walkthroughPitchDegrees: panel?.querySelector('[data-viewer-setting="walkthroughPitchDegrees"]'),
    walkthroughZoomMultiplier: panel?.querySelector('[data-viewer-setting="walkthroughZoomMultiplier"]'),
    walkthroughEyeHeightRatio: panel?.querySelector('[data-viewer-setting="walkthroughEyeHeightRatio"]'),
    modelOffsetY: panel?.querySelector('[data-viewer-setting="modelOffsetY"]'),
    labelsVisible: panel?.querySelector('[data-viewer-setting="labelsVisible"]'),
    snapToGround: panel?.querySelector('[data-viewer-setting="snapToGround"]')
  }
  const referenceLayerCreateForm = panel?.querySelector("[data-reference-layer-create-form]")
  const referenceLayerCreateNameInput = panel?.querySelector("[data-reference-layer-create-name]")
  const referenceLayerCreateFileInput = panel?.querySelector("[data-reference-layer-create-file]")
  const referenceLayerCreateSubmitButton = panel?.querySelector("[data-reference-layer-create-submit]")
  const referenceLayerList = panel?.querySelector("[data-reference-layer-list]")
  const editorModeHintEl = panel?.querySelector("[data-editor-mode-hint]")
  const editorModeLink = panel?.querySelector("[data-editor-mode-link]")
  const guidedSharedStepLabel = panel?.querySelector("[data-guided-shared-step-label]")
  const guidedHeaderEl = panel?.querySelector(".plan-guided-header")
  const guidedTitleEl = panel?.querySelector("[data-guided-title]")
  const selectedNameEl = panel?.querySelector("[data-selected-node-name]")
  const selectedKeyEl = panel?.querySelector("[data-selected-node-key]")
  const selectedTagEl = panel?.querySelector("[data-selected-node-tag]")
  const existingThingForm = panel?.querySelector("[data-existing-thing-form]")
  const existingThingSelect = panel?.querySelector("[data-existing-thing-select]")
  const thingForm = panel?.querySelector("[data-thing-form]")
  const thingNameInput = panel?.querySelector("[data-thing-name]")
  const thingKindInput = panel?.querySelector("[data-thing-kind]")
  const thingDescriptionInput = panel?.querySelector("[data-thing-description]")
  const thingAttributesInput = panel?.querySelector("[data-thing-attributes]")
  const thingSubmitButton = panel?.querySelector("[data-thing-submit]")
  const removeMappingButton = panel?.querySelector("[data-remove-mapping]")
  const mappingList = panel?.querySelector("[data-mapping-list]")
  const nodeMappingForm = panel?.querySelector("[data-node-mapping-form]")
  const nodeMappingIntegrationSelect = panel?.querySelector("[data-node-mapping-integration]")
  const nodeMappingObjectTypeSelect = panel?.querySelector("[data-node-mapping-object-type]")
  const nodeMappingExternalObjectSelect = panel?.querySelector("[data-node-mapping-external-object]")
  const nodeMappingSubmitButton = panel?.querySelector("[data-node-mapping-submit]")
  const nodeMappingsList = panel?.querySelector("[data-node-mappings-list]")
  const placementNodeMappingForm = panel?.querySelector("[data-placement-node-mapping-form]")
  const placementNodeMappingIntegrationSelect = panel?.querySelector("[data-placement-node-mapping-integration]")
  const placementNodeMappingObjectTypeSelect = panel?.querySelector("[data-placement-node-mapping-object-type]")
  const placementNodeMappingExternalObjectSelect = panel?.querySelector("[data-placement-node-mapping-external-object]")
  const placementNodeMappingSubmitButton = panel?.querySelector("[data-placement-node-mapping-submit]")
  const placementNodeMappingsList = panel?.querySelector("[data-placement-node-mappings-list]")
  const panelModeButtons = Array.from(panel?.querySelectorAll("[data-panel-mode]") || [])
  const advancedPanelModeSwitcher = panel?.querySelector("[data-advanced-mode-switcher]")
  const advancedPanelModeButtons = Array.from(panel?.querySelectorAll("[data-advanced-panel-mode]") || [])
  const panelModePanels = Array.from(panel?.querySelectorAll("[data-panel-mode-panel]") || [])
  const advancedTagAccordionButtons = Array.from(panel?.querySelectorAll("[data-advanced-tag-accordion]") || [])
  const advancedTagAccordionPanels = Array.from(panel?.querySelectorAll("[data-advanced-tag-accordion-panel]") || [])
  const advancedPlaceAccordionButtons = Array.from(panel?.querySelectorAll("[data-advanced-place-accordion]") || [])
  const advancedPlaceAccordionPanels = Array.from(panel?.querySelectorAll("[data-advanced-place-accordion-panel]") || [])
  const placementSelectionNameEl = panel?.querySelector("[data-placement-selection-name]")
  const placementSelectionPositionEl = panel?.querySelector("[data-placement-selection-position]")
  const placementSelectionThingEl = panel?.querySelector("[data-placement-selection-thing]")
  const placementThingForm = panel?.querySelector("[data-placement-thing-form]")
  const placementThingNameInput = panel?.querySelector("[data-placement-thing-name]")
  const placementThingKindInput = panel?.querySelector("[data-placement-thing-kind]")
  const placementThingDescriptionInput = panel?.querySelector("[data-placement-thing-description]")
  const placementThingAttributesInput = panel?.querySelector("[data-placement-thing-attributes]")
  const placementThingSubmitButton = panel?.querySelector("[data-placement-thing-submit]")
  const removePlacementButton = panel?.querySelector("[data-remove-placement]")
  const placementVerticalModeButtons = Array.from(panel?.querySelectorAll("[data-placement-vertical-mode]") || [])
  const placementExistingThingForm = panel?.querySelector("[data-placement-existing-thing-form]")
  const placementExistingThingSelect = panel?.querySelector("[data-placement-existing-thing-select]")
  const placementList = panel?.querySelector("[data-placement-list]")
  const placementTabButtons = Array.from(panel?.querySelectorAll("[data-placement-tab]") || [])
  const placementTabPanels = Array.from(panel?.querySelectorAll("[data-placement-tab-panel]") || [])
  const placementPresetButtons = Array.from(panel?.querySelectorAll("[data-placement-kind-preset]") || [])
  const navigationPointSelectionNameEl = panel?.querySelector("[data-navigation-point-selection-name]")
  const navigationPointSelectionPositionEl = panel?.querySelector("[data-navigation-point-selection-position]")
  const navigationPointCountEl = panel?.querySelector("[data-navigation-point-count]")
  const navigationPointList = panel?.querySelector("[data-navigation-point-list]")
  const removeNavigationPointButton = panel?.querySelector("[data-remove-navigation-point]")
  const captureAlignmentForm = panel?.querySelector("[data-capture-alignment-form]")
  const captureAlignmentSessionSelect = panel?.querySelector("[data-capture-alignment-session]")
  const captureAlignmentStatusEl = panel?.querySelector("[data-capture-alignment-status]")
  const captureAlignmentTransformInputs = {
    scale: panel?.querySelector('[data-capture-alignment-transform="scale"]'),
    rotation_degrees: panel?.querySelector('[data-capture-alignment-transform="rotation_degrees"]'),
    offset_x: panel?.querySelector('[data-capture-alignment-transform="offset_x"]'),
    offset_z: panel?.querySelector('[data-capture-alignment-transform="offset_z"]'),
    elevation_y: panel?.querySelector('[data-capture-alignment-transform="elevation_y"]')
  }
  const captureAlignmentPublishButton = panel?.querySelector("[data-capture-alignment-publish]")
  const captureAlignmentDeleteButton = panel?.querySelector("[data-capture-alignment-delete]")
  const captureAlignmentList = panel?.querySelector("[data-capture-alignment-list]")
  const mappingPanelClose = panel?.querySelector("[data-mapping-panel-close]")
  const viewerSettingsClose = panel?.querySelector("[data-viewer-settings-close]")
  const saveModelButton = panel?.querySelector("[data-save-model]")
  const tabButtons = Array.from(panel?.querySelectorAll("[data-mapping-tab]") || [])
  const tabPanels = Array.from(panel?.querySelectorAll("[data-mapping-tab-panel]") || [])
  const guidedEntry = panel?.querySelector("[data-guided-entry]")
  const guidedNav = panel?.querySelector("[data-guided-nav]")
  const guidedPrevButton = panel?.querySelector("[data-guided-prev]")
  const guidedStepNextButton = panel?.querySelector("[data-guided-step-next]")
  const guidedFinishButton = panel?.querySelector("[data-guided-finish]")
  const tagSelectionSection = panel?.querySelector('[data-tag-section="selection"]')
  const tagThingSection = panel?.querySelector('[data-tag-section="thing"]')
  const tagIntegrationSection = panel?.querySelector('[data-tag-section="integration"]')
  const tagListSection = panel?.querySelector('[data-tag-section="list"]')
  const placeSelectionSection = panel?.querySelector('[data-place-section="selection"]')
  const placeThingSection = panel?.querySelector('[data-place-section="thing"]')
  const placeIntegrationSection = panel?.querySelector('[data-place-section="integration"]')
  const placeListSection = panel?.querySelector('[data-place-section="list"]')
  const navigateSelectionSection = panel?.querySelector('[data-navigate-section="selection"]')
  const navigateListSection = panel?.querySelector('[data-navigate-section="list"]')
  const guidedStepLabels = Array.from(panel?.querySelectorAll("[data-guided-step-label]") || [])
  const advancedNavigateAccordionButtons = Array.from(panel?.querySelectorAll("[data-advanced-navigate-accordion]") || [])
  const advancedNavigateAccordionPanels = Array.from(panel?.querySelectorAll("[data-advanced-navigate-accordion-panel]") || [])
  const advancedCaptureAccordionButtons = Array.from(panel?.querySelectorAll("[data-advanced-capture-accordion]") || [])
  const advancedCaptureAccordionPanels = Array.from(panel?.querySelectorAll("[data-advanced-capture-accordion-panel]") || [])
  const loadingPreview = element.dataset.previewImageUrl ? document.createElement("div") : null
  if (loadingPreview) {
    loadingPreview.className = "plan-viewer-loading-preview"
    loadingPreview.style.backgroundImage = `url("${element.dataset.previewImageUrl.replaceAll("\"", "\\\"")}")`
    element.appendChild(loadingPreview)
  }
  const loading = createOverlay(element, "plan-viewer-loading", "Preparing 3D workspace...")
  const updateLoadingMessage = (message) => {
    if (!loading.isConnected) return
    loading.textContent = message
  }
  const removeLoadingPreview = () => {
    loadingPreview?.remove()
  }
  const removeLoadingState = () => {
    loading.remove()
    removeLoadingPreview()
  }

  const scene = new THREE.Scene()
  scene.background = new THREE.Color("#eef4fb")

  const perspectiveCamera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000)
  const orthographicCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 2000)
  orthographicCamera.up.set(0, 0, -1)
  let camera = perspectiveCamera
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.outputColorSpace = THREE.SRGBColorSpace
  element.appendChild(renderer.domElement)
  const photo360TextureLoader = new THREE.TextureLoader()
  const photo360MinimumLoadingMs = 260
  const photo360DefaultFov = 70
  const photo360MinFov = 24
  const photo360MaxFov = 92

  const controls = new OrbitControls(camera, renderer.domElement)
  controls.enableDamping = true
  controls.dampingFactor = 0.06
  controls.screenSpacePanning = true
  const defaultMouseButtons = {
    LEFT: THREE.MOUSE.ROTATE,
    MIDDLE: THREE.MOUSE.DOLLY,
    RIGHT: THREE.MOUSE.PAN
  }
  const defaultTouchBindings = {
    ONE: THREE.TOUCH.ROTATE,
    TWO: THREE.TOUCH.DOLLY_PAN
  }
  const twoDMouseButtons = {
    LEFT: THREE.MOUSE.PAN,
    MIDDLE: THREE.MOUSE.DOLLY,
    RIGHT: THREE.MOUSE.PAN
  }
  const twoDTouchBindings = {
    ONE: THREE.TOUCH.PAN,
    TWO: THREE.TOUCH.DOLLY_PAN
  }

  const hemiLight = new THREE.HemisphereLight("#ffffff", "#9cb3d1", 1.35)
  const dirLight = new THREE.DirectionalLight("#ffffff", 1.4)
  dirLight.position.set(8, 14, 10)
  scene.add(hemiLight, dirLight)

  const grid = new THREE.GridHelper(20, 20, "#9bb2cc", "#cfdae8")
  grid.position.y = -0.01

  const raycaster = new THREE.Raycaster()
  const pointer = new THREE.Vector2()
  const initialReferenceLayers = referenceLayersFromDataset(element)
  const selectedInitialReferenceLayerId = initialReferenceLayerId(initialReferenceLayers)
  const selectedInitialReferenceLayer = findReferenceLayerById(initialReferenceLayers, selectedInitialReferenceLayerId)
  const initiallyVisibleReferenceLayerIds = new Set(
    initialReferenceLayers
      .filter((referenceLayer) => referenceLayer.processed && referenceLayer.defaultVisible)
      .map((referenceLayer) => referenceLayer.id)
  )
  const initialSavedReferenceLayersById = new Map(
    initialReferenceLayers.map((referenceLayer) => [
      referenceLayer.id,
      {
        name: referenceLayer.name,
        defaultVisible: referenceLayer.defaultVisible,
        settings: cloneCablingPlanSettings(referenceLayer.settings || {})
      }
    ])
  )
  const state = {
    mode: viewerMode,
    capabilities: viewerCapabilities,
    model: null,
    framing: null,
    viewMode: "3d",
    sidePanelMode: null,
    viewerSettingsSection: "viewer",
    referenceLayers: initialReferenceLayers,
    savedReferenceLayersById: initialSavedReferenceLayersById,
    activeReferenceLayerId: selectedInitialReferenceLayerId,
    referenceLayerEditorOpenId: selectedInitialReferenceLayerId,
    referenceLayerOverlaysById: new Map(),
    visibleReferenceLayerIds: initiallyVisibleReferenceLayerIds,
    viewerSettings: viewerSettingsFromDataset(element),
    savedViewerSettings: viewerSettingsFromDataset(element),
    cablingPlanSettings: cloneCablingPlanSettings(selectedInitialReferenceLayer?.settings || {}),
    cablingPlanSavedSettings: cloneCablingPlanSettings(selectedInitialReferenceLayer?.settings || {}),
    cablingPlanName: selectedInitialReferenceLayer?.name || "",
    cablingPlanSavedName: selectedInitialReferenceLayer?.name || "",
    cablingPlanDefaultVisible: selectedInitialReferenceLayer?.defaultVisible ?? false,
    cablingPlanSavedDefaultVisible: selectedInitialReferenceLayer?.defaultVisible ?? false,
    cablingPlanVisible: Boolean(selectedInitialReferenceLayer?.id && initiallyVisibleReferenceLayerIds.has(selectedInitialReferenceLayer.id)),
    cablingPlanSaving: false,
    autoRotate: false,
    orbitLock: true,
    labelsVisible: viewerSettingsFromDataset(element).labelsVisible,
    markersVisible: true,
    modelBasePositionY: 0,
    modelGroundSnapOffsetY: 0,
    labelsMenuOpen: false,
    referenceLayerMenuOpen: false,
    exportMenuOpen: false,
    hiddenLabelObjectTypes: new Set(),
    hiddenLabelThingTypes: new Set(),
    hiddenLabelIntegrationIds: new Set(),
    hiddenLabelIntegrationObjectTypes: new Set(),
    mappingMode: false,
    panelMode: "tag",
    editorMode: "guided",
    preferAdvancedEditor: false,
    guidedStep: 1,
    advancedTagSection: null,
    advancedPlaceSection: null,
    advancedNavigateSection: null,
    advancedCaptureSection: "sessions",
    savingModel: false,
    modelDirty: false,
    mappingTab: "create-edit",
    placementTab: "create-edit",
    things: [],
    mappingsByNodeKey: new Map(),
    nodeMappingsById: new Map(),
    nodeMappingsByNodeKey: new Map(),
    nodeMappingsByPlacementId: new Map(),
    integrations: [],
    integrationCollections: new Map(),
    nodesByKey: new Map(),
    nodeMappingFormNodeKey: null,
    placementNodeMappingFormPlacementId: null,
    placementsById: new Map(),
    placementObjectsById: new Map(),
    navigationPointsById: new Map(),
    navigationPointObjectsById: new Map(),
    captureAlignmentSessions: [],
    activeCaptureSessionId: null,
    captureAlignmentDraft: null,
    captureAlignmentObjects: [],
    captureAlignmentDragging: null,
    selectedObject: null,
    selectedPlacement: null,
    selectedPlacementObject: null,
    selectedPlacementPoint: null,
    selectedNavigationPoint: null,
    selectedNavigationPointObject: null,
    selectionHelper: null,
    selectionMarker: null,
    selectedPoint: null,
    hoveredObject: null,
    hoveredNavigationPointObject: null,
    hoverHelper: null,
    hoverMarker: null,
    hoveredPoint: null,
    photo360: null,
    photo360Loading: false,
    photo360LoadToken: null,
    photo360Overlay: null,
    photo360LoadingOverlay: null,
    photo360LoadingMessage: null,
    photo360HotspotElementsById: new Map(),
    photo360MinimapRegions: [],
    popupEntry: null,
    popupActiveTab: "status",
    popupTabEntryKey: null,
    popupSelectedAction: "",
    popupActionFeedback: null,
    popupActionValues: {},
    popupMaintainDraft: {},
    popupMaintainFiles: [],
    liveDetailsByMappingId: new Map(),
    searchEntries: [],
    activeSearchResultId: null,
    hotspotElementsById: new Map(),
    feedbackTimer: null,
    pointerDown: null,
    pointerDragged: false,
    cameraAnimation: null,
    ortho2DZoom: null
  }

  let closePopup = () => {}
  let loadPopupLiveDetails = () => {}
  let refreshPopupLiveDetails = () => {}
  let executePopupAction = async () => {}
  let renderPopup = () => {}
  let hideSearchResults = () => {}
  let filteredSearchEntries = () => []
  let renderSearchResults = () => {}
  let rebuildSearchEntries = () => {}
  let focusSearchEntry = () => {}
  let embedSnapshot = () => ({})
  let notifyEmbedStateChanged = () => {}
  let notifyEmbedSelectionChanged = () => {}
  let saveSelectedThing = async () => {}
  let savePlacedThing = async () => {}
  let attachExistingThing = async () => {}
  let saveSelectedNodeMapping = async () => {}
  let saveSelectedPlacementNodeMapping = async () => {}
  let removeNodeMapping = async () => {}
  let attachExistingPlacedThing = async () => {}
  let createNavigationPoint = async () => {}
  let removeNavigationPoint = async () => {}
  let saveCaptureAlignment = async () => {}
  let publishCaptureAlignment = async () => {}
  let removeCaptureAlignmentPublishedObjects = async () => {}
  let switchVisibleCaptureSession = async () => {}
  let removeSelectedMapping = async () => {}
  let removePlacement = async () => {}
  let removeSelectedPlacement = async () => {}
  let confirmAndRemovePlacement = async () => {}
  let renderCablingPlanEditor = () => {}
  let setMappingTab = () => {}
  let setPlacementTab = () => {}
  let setPanelMode = () => {}
  let guidedMaxStep = () => 0
  let guidedCanAdvance = () => false
  let syncEditorMode = () => {}
  let closeMappingPanel = () => {}
  let referenceLayerStatusMessage = () => ""
  let allLabelObjectTypes = () => []
  let allLabelThingTypes = () => []
  let allLabelIntegrations = () => []
  let allLabelIntegrationObjectTypes = () => []
  let labelVisibleForPlacement = () => true
  let renderLabelsMenu = () => {}
  let renderReferenceLayerMenu = () => {}
  let renderReferenceLayerList = () => {}
  let applyViewerLabelsVisibilityPreference = () => {}
  let setLabelsMenuOpen = () => {}
  let setReferenceLayerMenuOpen = () => {}
  let setExportMenuOpen = () => {}
  let setActiveButton = () => {}
  let prepareForViewChange = () => {}
  let configureOrthographicCamera = () => {}
  let applyDefault3DCamera = () => {}
  let apply3DView = () => {}
  let apply2DView = () => {}
  let applyOrthographic2DView = () => {}
  let applyWalkthroughView = () => {}
  let applyDefaultView = () => {}
  let applyQueuedViewMode = () => {}
  let queueViewMode = () => {}
  let resetView = () => {}
  let fitCurrentView = () => {}
  let toggleAutoRotate = () => {}
  let toggleFullscreen = async () => {}
  let exportCurrentView = () => {}
  let toggleCablingPlanOverlay = async () => {}
  let toggleReferenceLayerById = async () => false
  let applyViewerSettingsToScene = () => {}
  let applyViewerSettingChange = () => {}
  let sync3DViewerSettingsToCurrentView = () => {}
  let sync2DViewerSettingsToCurrentView = () => {}
  let syncWalkthroughSettingsToCurrentView = () => {}
  let loadReferenceLayerDraft = () => {}
  let upsertReferenceLayer = () => {}
  let applyReferenceLayerSelection = async () => {}
  let applyCablingPlanSettingChange = () => {}
  let resetViewerSettings = () => {}
  let saveViewerSettings = async () => {}
  let createReferenceLayer = async () => {}
  let replaceReferenceLayerPdf = async () => {}
  let deleteReferenceLayer = async () => {}
  let toggleViewerSettingsPanel = async () => {}
  let embedBridge = {
    enabled: false,
    notifyReady() {},
    notifyStateChanged() {},
    notifySelectionChanged() {},
    notifyError() {},
    destroy() {}
  }

  const activeReferenceLayer = () => findReferenceLayerById(state.referenceLayers, state.activeReferenceLayerId)
  const activeReferenceLayerVisible = () =>
    Boolean(state.activeReferenceLayerId && state.visibleReferenceLayerIds.has(state.activeReferenceLayerId))
  const referenceLayerOverlayEntry = (referenceLayerId = state.activeReferenceLayerId) =>
    referenceLayerId ? state.referenceLayerOverlaysById.get(referenceLayerId) || null : null
  const syncActiveReferenceLayerVisibilityState = () => {
    state.cablingPlanVisible = activeReferenceLayerVisible()
  }
  const savedReferenceLayerDraft = (referenceLayerId) => state.savedReferenceLayersById.get(referenceLayerId) || null
  const referenceLayerDirtyFor = (referenceLayer) => {
    if (!referenceLayer) return false

    const savedReferenceLayer = savedReferenceLayerDraft(referenceLayer.id)
    if (!savedReferenceLayer) return true

    return referenceLayer.name !== savedReferenceLayer.name ||
      referenceLayer.defaultVisible !== savedReferenceLayer.defaultVisible ||
      !cablingPlanSettingsEqual(referenceLayer.settings || {}, savedReferenceLayer.settings || {})
  }
  const activeReferenceLayerSettingsDirty = () => !cablingPlanSettingsEqual(state.cablingPlanSettings, state.cablingPlanSavedSettings)
  const activeReferenceLayerMetaDirty = () =>
    state.cablingPlanName !== state.cablingPlanSavedName ||
    state.cablingPlanDefaultVisible !== state.cablingPlanSavedDefaultVisible
  const cablingPlanSettingsDirty = () => !cablingPlanSettingsEqual(state.cablingPlanSettings, state.cablingPlanSavedSettings)
  const viewerSettingsDirty = () => !viewerSettingsEqual(state.viewerSettings, state.savedViewerSettings)
  const referenceLayerDirty = () => state.referenceLayers.some((referenceLayer) => referenceLayerDirtyFor(referenceLayer))
  const anyViewerSettingsDirty = () => viewerSettingsDirty() || referenceLayerDirty()

  const setActiveCamera = (nextCamera) => {
    camera = nextCamera
    controls.object = camera
  }

  const applyControlTuning = (mode = state.viewMode) => {
    if (is2DLikeMode(mode)) {
      controls.panSpeed = 2.4
      controls.zoomSpeed = 2.2
      controls.rotateSpeed = 0.7
      controls.mouseButtons = twoDMouseButtons
      controls.touches = twoDTouchBindings
      return
    }

    if (mode === "walkthrough") {
      controls.panSpeed = 1.15
      controls.zoomSpeed = 1.1
      controls.rotateSpeed = 0.9
      controls.mouseButtons = defaultMouseButtons
      controls.touches = defaultTouchBindings
      return
    }

    controls.panSpeed = 1
    controls.zoomSpeed = 1
    controls.rotateSpeed = 1
    controls.mouseButtons = defaultMouseButtons
    controls.touches = defaultTouchBindings
  }

  const syncViewerDataset = (settings = state.viewerSettings) => {
    element.dataset.viewerObjectIconScale = settings.objectIconScale
    element.dataset.viewerDefaultViewMode = settings.defaultViewMode
    element.dataset.viewerDefaultYawDegrees = settings.defaultYawDegrees
    element.dataset.viewerDefaultPitchDegrees = settings.defaultPitchDegrees
    element.dataset.viewerDefaultZoomMultiplier = settings.defaultZoomMultiplier
    element.dataset.viewerDefault2dZoomMultiplier = settings.default2DZoomMultiplier
    element.dataset.viewerDefault2dOffsetX = settings.default2DOffsetX
    element.dataset.viewerDefault2dOffsetZ = settings.default2DOffsetZ
    element.dataset.viewerDefaultVerticalBias = settings.defaultVerticalBias
    element.dataset.viewerWalkthroughOffsetX = settings.walkthroughOffsetX
    element.dataset.viewerWalkthroughOffsetZ = settings.walkthroughOffsetZ
    element.dataset.viewerWalkthroughYawDegrees = settings.walkthroughYawDegrees
    element.dataset.viewerWalkthroughPitchDegrees = settings.walkthroughPitchDegrees
    element.dataset.viewerWalkthroughZoomMultiplier = settings.walkthroughZoomMultiplier
    element.dataset.viewerWalkthroughEyeHeightRatio = settings.walkthroughEyeHeightRatio
    element.dataset.viewerWalkthroughCameraX = settings.walkthroughCameraX ?? ""
    element.dataset.viewerWalkthroughCameraY = settings.walkthroughCameraY ?? ""
    element.dataset.viewerWalkthroughCameraZ = settings.walkthroughCameraZ ?? ""
    element.dataset.viewerWalkthroughTargetX = settings.walkthroughTargetX ?? ""
    element.dataset.viewerWalkthroughTargetY = settings.walkthroughTargetY ?? ""
    element.dataset.viewerWalkthroughTargetZ = settings.walkthroughTargetZ ?? ""
    element.dataset.viewerModelOffsetY = settings.modelOffsetY
    element.dataset.viewerLabelsVisible = settings.labelsVisible ? "true" : "false"
    element.dataset.viewerSnapToGround = settings.snapToGround ? "true" : "false"
  }

  const defaultViewVerticalOffsetDistance = (framing = state.framing) =>
    Math.max(framing?.sphere?.radius || 0, (framing?.maxDimension || 0) * 0.35, 1)

  const defaultViewAnchor = (framing = state.framing) => {
    if (!framing) return null

    const anchor = framing.center.clone()
    // Lowering the target nudges the model higher in frame without changing world placement.
    anchor.y -= defaultViewVerticalOffsetDistance(framing) * state.viewerSettings.defaultVerticalBias
    return anchor
  }

  const walkthroughBaseDepth = (framing = state.framing) => {
    if (!framing) return 1
    return Math.max(Math.max(framing.sphere?.radius || 1, 1) * 2.35, Math.max(framing.distance || 1, 0.1) * 0.95)
  }

  const default2DFocusPoint = (framing = state.framing) => {
    if (!framing) return null
    const floorY = framing.box.min.y - Math.max((framing.sphere?.radius || 1) * 0.02, 0.1)

    return new THREE.Vector3(
      framing.center.x + state.viewerSettings.default2DOffsetX,
      floorY,
      framing.center.z + state.viewerSettings.default2DOffsetZ
    )
  }

  const walkthroughFocusPoint = (framing = state.framing) => {
    if (!framing) return null

    return new THREE.Vector3(
      framing.center.x + state.viewerSettings.walkthroughOffsetX,
      framing.center.y,
      framing.center.z + state.viewerSettings.walkthroughOffsetZ
    )
  }

  const walkthroughPoseFromSettings = () => {
    const {
      walkthroughCameraX,
      walkthroughCameraY,
      walkthroughCameraZ,
      walkthroughTargetX,
      walkthroughTargetY,
      walkthroughTargetZ
    } = state.viewerSettings

    if (
      [walkthroughCameraX, walkthroughCameraY, walkthroughCameraZ, walkthroughTargetX, walkthroughTargetY, walkthroughTargetZ]
        .every((value) => Number.isFinite(value))
    ) {
      return {
        cameraPosition: new THREE.Vector3(walkthroughCameraX, walkthroughCameraY, walkthroughCameraZ),
        targetPosition: new THREE.Vector3(walkthroughTargetX, walkthroughTargetY, walkthroughTargetZ)
      }
    }

    return null
  }

  const deriveWalkthroughHelperSettings = ({
    baseSettings = state.viewerSettings,
    cameraPosition = camera.position,
    targetPosition = controls.target,
    framing = state.framing
  } = {}) => {
    if (!framing) return null

    const horizontalDistance = Math.max(
      Math.hypot(cameraPosition.x - targetPosition.x, cameraPosition.z - targetPosition.z),
      0.0001
    )
    const floorY = framing.box.min.y

    return {
      ...baseSettings,
      walkthroughOffsetX: Number((targetPosition.x - framing.center.x).toFixed(3)),
      walkthroughOffsetZ: Number((targetPosition.z - framing.center.z).toFixed(3)),
      walkthroughYawDegrees: Number(
        THREE.MathUtils.radToDeg(Math.atan2(cameraPosition.z - targetPosition.z, cameraPosition.x - targetPosition.x)).toFixed(3)
      ),
      walkthroughPitchDegrees: Number(
        THREE.MathUtils.radToDeg(Math.atan2(targetPosition.y - cameraPosition.y, horizontalDistance)).toFixed(3)
      ),
      walkthroughZoomMultiplier: Number(horizontalDistance.toFixed(4)),
      walkthroughEyeHeightRatio: Number((cameraPosition.y - floorY).toFixed(4))
    }
  }

  const viewerSettingInputValue = (input) => {
    if (!input) return null
    return input.type === "checkbox" ? input.checked : input.value
  }

  const formattedViewerSettingValue = (key, value) => {
    if (key === "objectIconScale") return parseFloatOr(value, 1).toFixed(2)
    return value
  }

  const syncViewerSettingInput = (key, input, value) => {
    if (!input) return

    if (input.type === "checkbox") {
      input.checked = Boolean(value)
      return
    }

    input.value = formattedViewerSettingValue(key, value)
  }

  const syncReferenceLayersDataset = () => {
    element.dataset.referenceLayers = JSON.stringify(state.referenceLayers)
  }

  const storeReferenceLayerDraft = (referenceLayerId, attributes = {}) => {
    let storedReferenceLayer = null

    state.referenceLayers = state.referenceLayers.map((referenceLayer) => {
      if (referenceLayer.id !== referenceLayerId) return referenceLayer

      storedReferenceLayer = normalizeReferenceLayer({
        ...referenceLayer,
        ...attributes,
        settings: cloneCablingPlanSettings(attributes.settings || referenceLayer.settings || {})
      })
      return storedReferenceLayer
    })

    if (storedReferenceLayer) syncReferenceLayersDataset()
    return storedReferenceLayer
  }

  const syncActiveReferenceLayerDraftToCollection = () => {
    const referenceLayer = activeReferenceLayer()
    if (!referenceLayer) return null

    return storeReferenceLayerDraft(referenceLayer.id, {
      name: state.cablingPlanName,
      defaultVisible: state.cablingPlanDefaultVisible,
      settings: state.cablingPlanSettings
    })
  }

  const markReferenceLayerSaved = (referenceLayer) => {
    if (!referenceLayer) return

    state.savedReferenceLayersById.set(referenceLayer.id, {
      name: referenceLayer.name,
      defaultVisible: referenceLayer.defaultVisible,
      settings: cloneCablingPlanSettings(referenceLayer.settings || {})
    })
  }

  const syncCablingPlanForm = () => {
    Object.entries(viewerSettingInputs).forEach(([key, input]) => {
      if (!input) return
      syncViewerSettingInput(key, input, state.viewerSettings[key])
      input.disabled = state.cablingPlanSaving
    })
    if (referenceLayerCreateSubmitButton) {
      const hasCreateName = Boolean(referenceLayerCreateNameInput?.value?.toString().trim())
      const hasCreateFile = Boolean(referenceLayerCreateFileInput?.files?.length)
      referenceLayerCreateSubmitButton.disabled = state.cablingPlanSaving || !hasCreateName || !hasCreateFile
    }
    if (viewerSet3DCurrentButton) {
      viewerSet3DCurrentButton.disabled = state.cablingPlanSaving || !state.framing || state.viewMode !== "3d"
    }
    if (viewerSet2DCurrentButton) {
      viewerSet2DCurrentButton.disabled = state.cablingPlanSaving || !state.framing || !is2DLikeMode(state.viewMode)
    }
    if (viewerSetWalkthroughCurrentButton) {
      viewerSetWalkthroughCurrentButton.disabled = state.cablingPlanSaving || !state.framing || state.viewMode !== "walkthrough"
    }

    if (viewerSettingsSaveButton) viewerSettingsSaveButton.disabled = state.cablingPlanSaving || !anyViewerSettingsDirty()
    if (viewerSettingsResetButton) viewerSettingsResetButton.disabled = state.cablingPlanSaving || !anyViewerSettingsDirty()
  }

  const setViewerSettingsSubsectionExpanded = (subsection, expanded) => {
    const button = viewerSettingsSubsectionButtons.find((entry) => entry.dataset.viewerSettingsSubsection === subsection)
    const panelElement = viewerSettingsSubsectionPanels.find((entry) => entry.dataset.viewerSettingsSubsectionPanel === subsection)
    if (!button || !panelElement) return

    button.setAttribute("aria-expanded", expanded ? "true" : "false")
    button.classList.toggle("is-expanded", expanded)
    panelElement.hidden = !expanded
  }

  const viewerSettingsSubsectionForCurrentView = () => {
    if (is2DLikeMode(state.viewMode)) return "camera-2d"
    if (state.viewMode === "walkthrough") return "walkthrough"
    return "camera-3d"
  }

  const expandViewerSettingsForCurrentView = () => {
    const activeSubsection = viewerSettingsSubsectionForCurrentView()

    viewerSettingsSubsectionButtons.forEach((button) => {
      const subsection = button.dataset.viewerSettingsSubsection
      const expanded = subsection === "general" || subsection === activeSubsection
      setViewerSettingsSubsectionExpanded(subsection, expanded)
    })
  }

  const hydratePlacementPresetIcons = () => {
    placementPresetButtons.forEach((button) => {
      const icon = button.querySelector("[data-placement-preset-icon]")
      if (!icon) return
      icon.dataset.kind = normalizeThingKind(button.dataset.placementKindPreset || "")
      icon.innerHTML = placementKindIconMarkup(button.dataset.placementKindPreset)
    })
  }

  const selectedNodeKey = () => state.selectedObject?.userData?.dtNodeKey
  const nodePopupEntryForKey = (nodeKey) => {
    if (!nodeKey) return null
    return state.mappingsByNodeKey.get(nodeKey) || state.nodeMappingsByNodeKey.get(nodeKey)?.[0] || null
  }

  const selectedMapping = () => nodePopupEntryForKey(selectedNodeKey())
  const selectedNodeMappings = () => state.nodeMappingsByNodeKey.get(selectedNodeKey()) || []
  const selectedPlacementMappings = () => state.nodeMappingsByPlacementId.get(state.selectedPlacement?.id) || []
  const placementObjectType = (placement) => {
    return normalizeThingKind(placement?.metadata?.object_type || "")
  }
  const placementLabelMappings = (placement) => state.nodeMappingsByPlacementId.get(placement?.id) || []
  const orderedNavigationPoints = () =>
    Array.from(state.navigationPointsById.values()).sort((left, right) =>
      left.sort_order - right.sort_order || left.id.localeCompare(right.id)
    )
  const navigationPointOrdinal = (navigationPoint) =>
    orderedNavigationPoints().findIndex((entry) => entry.id === navigationPoint?.id) + 1
  const navigationPointLabel = (navigationPoint) => {
    const ordinal = navigationPointOrdinal(navigationPoint)
    return ordinal > 0 ? `Navigation Point ${ordinal}` : "Navigation Point"
  }

  const integrationObjectTypeKey = (mapping) => `${mapping.integration_id}:${mapping.object_type}`
  const isPhoto360ObjectType = (objectType) =>
    typeof objectType === "string" && objectType.startsWith("360_photo_session:")
  const photo360MappingFrom = (mappings = []) =>
    Array.from(mappings).find((mapping) =>
      mapping?.integration_key === "this_model_360_photo" &&
        isPhoto360ObjectType(mapping?.object_type)
    ) || null
  const photo360MappingForPlacement = (placement) =>
    photo360MappingFrom(state.nodeMappingsByPlacementId.get(placement?.id) || [])
  const photo360MappingForNodeKey = (nodeKey) =>
    photo360MappingFrom(state.nodeMappingsByNodeKey.get(nodeKey) || [])
  const photo360SessionIdFromMapping = (mapping) => {
    if (!mapping) return null
    if (mapping.metadata?.plan_capture_session_id) return mapping.metadata.plan_capture_session_id.toString()
    if (typeof mapping.object_type === "string" && mapping.object_type.startsWith("360_photo_session:")) {
      return mapping.object_type.split(":")[1] || null
    }
    return null
  }
  const photo360CaptureIdFromMapping = (mapping) =>
    mapping?.external_id?.toString() || mapping?.metadata?.plan_capture_id?.toString() || null
  const captureSessionConfigForId = (sessionId) =>
    Array.from(viewerConfig.assets?.capture_sessions || []).find((entry) => entry.id?.toString() === sessionId?.toString()) || null
  const photo360PlacementEntriesForSession = (sessionId) => {
    const normalizedSessionId = sessionId?.toString()
    if (!normalizedSessionId) return []

    const sessionConfig = captureSessionConfigForId(normalizedSessionId)
    const captureOrder = new Map(
      Array.from(sessionConfig?.captures || []).map((capture, index) => [capture.id?.toString(), index])
    )
    const capturesById = new Map(
      Array.from(sessionConfig?.captures || []).map((capture) => [capture.id?.toString(), capture])
    )

    return Array.from(state.placementsById.values())
      .map((placement) => {
        const mapping = photo360MappingForPlacement(placement)
        if (!mapping) return null
        if (photo360SessionIdFromMapping(mapping) !== normalizedSessionId) return null

        const captureId = photo360CaptureIdFromMapping(mapping)
        if (!captureId) return null

        return {
          placement,
          mapping,
          captureId,
          capture: capturesById.get(captureId) || null,
          sessionId: normalizedSessionId,
          sortOrder: captureOrder.get(captureId) ?? Number.MAX_SAFE_INTEGER
        }
      })
      .filter(Boolean)
      .sort((left, right) =>
        left.sortOrder - right.sortOrder ||
        parseFloatOr(left.placement?.metadata?.plan_capture_id, 0) - parseFloatOr(right.placement?.metadata?.plan_capture_id, 0) ||
        left.placement.id.toString().localeCompare(right.placement.id.toString())
      )
  }
  const photo360EntryForMapping = (mapping, placement = null) => {
    const sessionId = photo360SessionIdFromMapping(mapping)
    const captureId = photo360CaptureIdFromMapping(mapping)
    if (!sessionId || !captureId) return null

    const entries = photo360PlacementEntriesForSession(sessionId)
    const matchedEntry = entries.find((entry) =>
      (placement && entry.placement.id?.toString() === placement.id?.toString()) ||
      entry.captureId === captureId
    ) || null
    if (!matchedEntry) return null

    const currentIndex = entries.findIndex((entry) => entry.captureId === matchedEntry.captureId)
    return {
      sessionId,
      entries,
      currentEntry: matchedEntry,
      currentIndex,
      previousEntry: currentIndex > 0 ? entries[currentIndex - 1] : null,
      nextEntry: currentIndex >= 0 && currentIndex < entries.length - 1 ? entries[currentIndex + 1] : null
    }
  }
  const photo360HotspotDistanceMeters = (leftEntry, rightEntry) => {
    if (!leftEntry?.placement?.position || !rightEntry?.placement?.position) return null
    const dx = parseFloatOr(rightEntry.placement.position.x, 0) - parseFloatOr(leftEntry.placement.position.x, 0)
    const dy = parseFloatOr(rightEntry.placement.position.y, 0) - parseFloatOr(leftEntry.placement.position.y, 0)
    const dz = parseFloatOr(rightEntry.placement.position.z, 0) - parseFloatOr(leftEntry.placement.position.z, 0)
    return Math.hypot(dx, dy, dz)
  }
  const formatPhoto360Distance = (distance) => {
    if (!Number.isFinite(distance)) return ""
    if (distance < 10) return `${distance.toFixed(1)} m`
    return `${Math.round(distance)} m`
  }
  const photo360NearbyEntries = (context, limit = 6) => {
    if (!context?.currentEntry) return []

    const currentCaptureId = context.currentEntry.captureId
    const currentIndex = context.currentIndex
    const byDistance = Array.from(context.entries || [])
      .filter((entry) => entry.captureId !== currentCaptureId)
      .map((entry) => ({
        ...entry,
        distance: photo360HotspotDistanceMeters(context.currentEntry, entry),
        sequenceDelta: Math.abs((context.entries.findIndex((candidate) => candidate.captureId === entry.captureId)) - currentIndex)
      }))
      .sort((left, right) =>
        left.sequenceDelta - right.sequenceDelta ||
        (left.distance ?? Number.MAX_SAFE_INTEGER) - (right.distance ?? Number.MAX_SAFE_INTEGER)
      )

    const selected = new Map()
    ;[context.previousEntry, context.nextEntry].filter(Boolean).forEach((entry) => {
      selected.set(entry.captureId, {
        ...entry,
        distance: photo360HotspotDistanceMeters(context.currentEntry, entry)
      })
    })
    byDistance.forEach((entry) => {
      if (selected.size >= limit) return
      selected.set(entry.captureId, entry)
    })

    return Array.from(selected.values())
      .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
      .slice(0, limit)
  }
  const photo360CaptureForMapping = (mapping) => {
    if (!mapping?.external_id) return null

    return Array.from(viewerConfig.assets?.capture_sessions || []).flatMap((session) =>
      Array.from(session.captures || []).map((capture) => ({ ...capture, session }))
    ).find((entry) => entry.id?.toString() === mapping.external_id.toString()) || null
  }
  const formatPhoto360CapturedAt = (value) => {
    if (!value) return ""

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""

    return new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    }).format(date)
  }
  const formatCaptureSessionCapturedAt = (value) => {
    if (!value) return ""

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""

    return new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    }).format(date)
  }
  const captureAlignmentOptionLabel = (sessionPayload) => {
    const name = sessionPayload?.session?.name || "Capture Session"
    const capturedAtLabel = formatCaptureSessionCapturedAt(sessionPayload?.session?.captured_at)
    return capturedAtLabel ? `${name} · ${capturedAtLabel}` : name
  }
  const captureSessionTimestamp = (sessionPayload) => {
    const value = sessionPayload?.session?.captured_at
    if (!value) return Number.NEGATIVE_INFINITY

    const timestamp = new Date(value).getTime()
    return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp
  }
  const sortedCaptureAlignmentSessions = () =>
    Array.from(state.captureAlignmentSessions).sort((left, right) =>
      captureSessionTimestamp(right) - captureSessionTimestamp(left) ||
        (right?.session?.id || 0) - (left?.session?.id || 0)
    )
  const launchPhoto360Mapping = (mapping, placement = null) => {
    if (!mapping) return false

    const capture = photo360CaptureForMapping(mapping)
    const fileUrl = mapping.metadata?.plan_capture_file_url || capture?.file_url
    if (!fileUrl) {
      setFeedback("This 360 photo does not have an available file URL yet.", "error")
      return true
    }

    const label = mapping.external_label || mapping.metadata?.plan_capture_name || capture?.name || "360 photo"
    const capturedAt = mapping.metadata?.plan_capture_captured_at || capture?.session?.captured_at
    closePopup()
    openPhoto360Viewer({ fileUrl, label, capturedAt, mapping, placement })
    return true
  }

  const normalizeCaptureTransform = (transform = {}) => ({
    scale: Math.max(parseFloatOr(transform.scale, 1), 0.001),
    rotation_degrees: parseFloatOr(transform.rotation_degrees ?? transform.rotationDegrees, 0),
    offset_x: parseFloatOr(transform.offset_x ?? transform.offsetX, 0),
    offset_z: parseFloatOr(transform.offset_z ?? transform.offsetZ, 0),
    elevation_y: parseFloatOr(transform.elevation_y ?? transform.elevationY, state.framing?.box?.min?.y || 0)
  })

  const normalizeCaptureAlignmentSession = (payload = {}) => ({
    ...payload,
    session: payload.session || {},
    alignment: payload.alignment || null,
    coordinate_frame: payload.coordinate_frame || payload.coordinateFrame || {},
    captures: Array.from(payload.captures || []),
    polyline: Array.from(payload.polyline || [])
  })

  const activeCaptureAlignmentSession = () =>
    state.captureAlignmentSessions.find((entry) => entry.session?.id?.toString() === state.activeCaptureSessionId?.toString()) || null

  const captureTransformForSession = (sessionPayload = activeCaptureAlignmentSession()) =>
    normalizeCaptureTransform(state.captureAlignmentDraft || sessionPayload?.alignment?.transform || {})

  const transformedCapturePoint = (source, transform = captureTransformForSession()) => {
    const radians = THREE.MathUtils.degToRad(transform.rotation_degrees)
    const cos = Math.cos(radians)
    const sin = Math.sin(radians)
    const x = parseFloatOr(source?.x, 0) * transform.scale
    const z = parseFloatOr(source?.z, 0) * transform.scale

    return {
      x: (x * cos) - (z * sin) + transform.offset_x,
      y: transform.elevation_y,
      z: (x * sin) + (z * cos) + transform.offset_z
    }
  }

  const captureAlignmentSceneY = (transform = captureTransformForSession()) =>
    transform.elevation_y + state.viewerSettings.modelOffsetY + 0.08

  const captureAlignmentSourceStats = (sessionPayload = activeCaptureAlignmentSession()) => {
    const captures = Array.from(sessionPayload?.captures || [])
    if (!captures.length) return null

    const valuesX = captures.map((capture) => parseFloatOr(capture?.source?.x, 0))
    const valuesZ = captures.map((capture) => parseFloatOr(capture?.source?.z, 0))
    const minX = Math.min(...valuesX)
    const maxX = Math.max(...valuesX)
    const minZ = Math.min(...valuesZ)
    const maxZ = Math.max(...valuesZ)

    return {
      minX,
      maxX,
      minZ,
      maxZ,
      center: {
        x: (minX + maxX) / 2,
        z: (minZ + maxZ) / 2
      }
    }
  }

  const captureAlignmentControlFrame = (sessionPayload = activeCaptureAlignmentSession(), transform = captureTransformForSession(sessionPayload)) => {
    const sourceStats = captureAlignmentSourceStats(sessionPayload)
    if (!sourceStats) return null

    const points = Array.from(sessionPayload?.captures || []).map((capture) => transformedCapturePoint(capture.source, transform))
    if (!points.length) return null

    const minX = Math.min(...points.map((point) => point.x))
    const maxX = Math.max(...points.map((point) => point.x))
    const minZ = Math.min(...points.map((point) => point.z))
    const maxZ = Math.max(...points.map((point) => point.z))
    const width = Math.max(maxX - minX, 0.8)
    const depth = Math.max(maxZ - minZ, 0.8)
    const planCenter = transformedCapturePoint(sourceStats.center, transform)
    const handleRadius = Math.max(Math.min(width, depth) * 0.065, 0.28)
    const rotationArm = Math.max(depth * 0.28, 0.8)

    return {
      sourceCenter: sourceStats.center,
      planCenter,
      minX: planCenter.x - width / 2,
      maxX: planCenter.x + width / 2,
      minZ: planCenter.z - depth / 2,
      maxZ: planCenter.z + depth / 2,
      width,
      depth,
      handleRadius,
      rotationArm,
      y: captureAlignmentSceneY(transform)
    }
  }

  const captureAlignmentOffsetsForCenter = ({ sourceCenter, planCenter, scale, rotation_degrees }) => {
    const radians = THREE.MathUtils.degToRad(rotation_degrees)
    const cos = Math.cos(radians)
    const sin = Math.sin(radians)
    const scaledX = parseFloatOr(sourceCenter?.x, 0) * scale
    const scaledZ = parseFloatOr(sourceCenter?.z, 0) * scale

    return {
      offset_x: planCenter.x - ((scaledX * cos) - (scaledZ * sin)),
      offset_z: planCenter.z - ((scaledX * sin) + (scaledZ * cos))
    }
  }

  const captureAlignmentAngleAtPoint = (point, center) =>
    Math.atan2(point.z - center.z, point.x - center.x)

  const captureAlignmentDeltaAngle = (currentAngle, initialAngle) =>
    Math.atan2(Math.sin(currentAngle - initialAngle), Math.cos(currentAngle - initialAngle))

  const captureAlignmentEndpointFor = (sessionPayload, key) => {
    const sessionId = sessionPayload?.session?.id?.toString()
    const configSession = Array.from(viewerConfig.assets?.capture_sessions || [])
      .find((entry) => entry.id?.toString() === sessionId)
    return configSession?.[key] || ""
  }

  const syncCaptureAlignmentDataset = () => {
    viewerConfig.assets = viewerConfig.assets || {}
    viewerConfig.assets.capture_session_alignments = state.captureAlignmentSessions
  }

  const setActiveCaptureAlignmentSession = (sessionId) => {
    state.activeCaptureSessionId = sessionId?.toString() || null
    const sessionPayload = activeCaptureAlignmentSession()
    state.captureAlignmentDraft = normalizeCaptureTransform(sessionPayload?.alignment?.transform || {})
    renderCaptureAlignmentInspector()
    rebuildCaptureAlignmentObjects()
  }

  const captureAlignmentSummary = (sessionPayload) => {
    const status = sessionPayload?.alignment?.status || "unaligned"
    const count = sessionPayload?.captures?.length || 0
    const frame = sessionPayload?.coordinate_frame?.key
    const pieces = [`${count} point${count === 1 ? "" : "s"}`, status]
    if (frame) pieces.push(`frame ${frame}`)
    if (sessionPayload?.alignment?.visible_in_viewer) pieces.push("visible")
    return pieces.join(" · ")
  }

  const makeCaptureNumberTexture = (label) => {
    const canvas = document.createElement("canvas")
    canvas.width = 96
    canvas.height = 96
    const context = canvas.getContext("2d")
    context.fillStyle = "#f97316"
    context.beginPath()
    context.arc(48, 48, 34, 0, Math.PI * 2)
    context.fill()
    context.lineWidth = 8
    context.strokeStyle = "#ffffff"
    context.stroke()
    context.fillStyle = "#ffffff"
    context.font = "bold 38px system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
    context.textAlign = "center"
    context.textBaseline = "middle"
    context.fillText(label.toString(), 48, 49)
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    return texture
  }

  const clearCaptureAlignmentObjects = () => {
    state.captureAlignmentObjects.forEach((object) => {
      scene.remove(object)
      object.traverse?.((child) => {
        child.geometry?.dispose?.()
        child.material?.map?.dispose?.()
        child.material?.dispose?.()
      })
      object.geometry?.dispose?.()
      object.material?.dispose?.()
    })
    state.captureAlignmentObjects = []
  }

  const rebuildCaptureAlignmentObjects = () => {
    clearCaptureAlignmentObjects()
    const sessionPayload = activeCaptureAlignmentSession()
    if (!sessionPayload || state.panelMode !== "capture" || state.sidePanelMode !== "mapping") return

    const transform = captureTransformForSession(sessionPayload)
    const controlFrame = captureAlignmentControlFrame(sessionPayload, transform)
    const points = sessionPayload.captures.map((capture) => {
      const point = transformedCapturePoint(capture.source, transform)
      return new THREE.Vector3(point.x, captureAlignmentSceneY(transform), point.z)
    })

    if (points.length >= 2) {
      const line = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(points),
        new THREE.LineBasicMaterial({
          color: "#f97316",
          linewidth: 2,
          depthTest: false
        })
      )
      line.renderOrder = 6
      scene.add(line)
      state.captureAlignmentObjects.push(line)
    }

    if (controlFrame) {
      const corners = [
        new THREE.Vector3(controlFrame.minX, controlFrame.y, controlFrame.minZ),
        new THREE.Vector3(controlFrame.maxX, controlFrame.y, controlFrame.minZ),
        new THREE.Vector3(controlFrame.maxX, controlFrame.y, controlFrame.maxZ),
        new THREE.Vector3(controlFrame.minX, controlFrame.y, controlFrame.maxZ),
        new THREE.Vector3(controlFrame.minX, controlFrame.y, controlFrame.minZ)
      ]
      const boxLine = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(corners),
        new THREE.LineDashedMaterial({
          color: "#fb923c",
          dashSize: 0.24,
          gapSize: 0.16,
          depthTest: false,
          transparent: true,
          opacity: 0.95
        })
      )
      boxLine.computeLineDistances()
      boxLine.renderOrder = 6
      scene.add(boxLine)
      state.captureAlignmentObjects.push(boxLine)

      const translatePlane = new THREE.Mesh(
        new THREE.PlaneGeometry(controlFrame.width, controlFrame.depth),
        new THREE.MeshBasicMaterial({
          color: "#fb923c",
          transparent: true,
          opacity: 0.001,
          side: THREE.DoubleSide,
          depthTest: false,
          depthWrite: false
        })
      )
      translatePlane.rotation.x = -Math.PI / 2
      translatePlane.position.set(controlFrame.planCenter.x, controlFrame.y, controlFrame.planCenter.z)
      translatePlane.userData.dtCaptureAlignmentControl = "translate"
      translatePlane.renderOrder = 5
      scene.add(translatePlane)
      state.captureAlignmentObjects.push(translatePlane)

      const handleGeometry = new THREE.CircleGeometry(controlFrame.handleRadius, 24)
      const scaleHandleMaterial = new THREE.MeshBasicMaterial({
        color: "#ffffff",
        depthTest: false,
        depthWrite: false
      })
      ;[
        [controlFrame.minX, controlFrame.minZ],
        [controlFrame.maxX, controlFrame.minZ],
        [controlFrame.maxX, controlFrame.maxZ],
        [controlFrame.minX, controlFrame.maxZ]
      ].forEach(([x, z]) => {
        const handle = new THREE.Mesh(handleGeometry, scaleHandleMaterial.clone())
        handle.rotation.x = -Math.PI / 2
        handle.position.set(x, controlFrame.y + 0.002, z)
        handle.userData.dtCaptureAlignmentControl = "scale"
        handle.renderOrder = 7
        scene.add(handle)
        state.captureAlignmentObjects.push(handle)
      })

      const topMidpoint = new THREE.Vector3(controlFrame.planCenter.x, controlFrame.y, controlFrame.minZ)
      const rotateHandlePosition = new THREE.Vector3(
        controlFrame.planCenter.x,
        controlFrame.y,
        controlFrame.minZ - controlFrame.rotationArm
      )
      const rotateArm = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([topMidpoint, rotateHandlePosition]),
        new THREE.LineBasicMaterial({
          color: "#fb923c",
          depthTest: false
        })
      )
      rotateArm.renderOrder = 6
      scene.add(rotateArm)
      state.captureAlignmentObjects.push(rotateArm)

      const rotateHandle = new THREE.Mesh(
        new THREE.CircleGeometry(controlFrame.handleRadius * 1.05, 24),
        new THREE.MeshBasicMaterial({
          color: "#f97316",
          depthTest: false,
          depthWrite: false
        })
      )
      rotateHandle.rotation.x = -Math.PI / 2
      rotateHandle.position.copy(rotateHandlePosition)
      rotateHandle.position.y += 0.002
      rotateHandle.userData.dtCaptureAlignmentControl = "rotate"
      rotateHandle.renderOrder = 7
      scene.add(rotateHandle)
      state.captureAlignmentObjects.push(rotateHandle)
    }

    const unitSize = Math.max((state.framing?.sphere?.radius || 1) * 0.035, 0.7) * Math.max(state.viewerSettings.objectIconScale || 1, 0.5)
    points.forEach((point, index) => {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: makeCaptureNumberTexture(index + 1),
          transparent: true,
          depthTest: false,
          depthWrite: false
        })
      )
      sprite.position.copy(point)
      sprite.position.y += unitSize * 0.18
      sprite.scale.set(unitSize * 1.15, unitSize * 1.15, 1)
      sprite.renderOrder = 7
      scene.add(sprite)
      state.captureAlignmentObjects.push(sprite)
    })
  }

  const placementInteractionReady = () => Boolean(selectedPlacementSourcePosition())
  const currentPlacementObjectType = () => {
    if (state.selectedPlacement?.metadata?.object_type) {
      return placementObjectType(state.selectedPlacement)
    }

    const activeButton = placementPresetButtons.find((button) => button.classList.contains("is-active"))
    return normalizeThingKind(activeButton?.dataset.placementKindPreset || "sensor")
  }

  const setPlacementObjectType = (value) => {
    const candidate = normalizeThingKind(value || "sensor") || "sensor"
    const normalized = BUILT_IN_PLACEMENT_OBJECT_TYPES.includes(candidate) ? candidate : "sensor"

    placementPresetButtons.forEach((button) => {
      const active = button.dataset.placementKindPreset === normalized
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  const currentPlacementVerticalMode = () => {
    if (state.selectedPlacement?.metadata?.vertical_mode) {
      return normalizePlacementVerticalMode(state.selectedPlacement.metadata.vertical_mode)
    }

    const activeButton = placementVerticalModeButtons.find((button) => button.classList.contains("is-active"))
    return normalizePlacementVerticalMode(activeButton?.dataset.placementVerticalMode)
  }

  const setPlacementVerticalMode = (mode) => {
    const normalizedMode = normalizePlacementVerticalMode(mode)

    placementVerticalModeButtons.forEach((button) => {
      const active = button.dataset.placementVerticalMode === normalizedMode
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  const syncPlacementVerticalModeAvailability = () => {
    const ready = placementInteractionReady()

    placementVerticalModeButtons.forEach((button) => {
      button.disabled = !ready
      button.classList.toggle("is-disabled", !ready)
      button.setAttribute("aria-disabled", ready ? "false" : "true")
    })
  }

  const selectedPlacementSourcePosition = () => {
    if (state.selectedPlacementPoint) {
      return {
        x: state.selectedPlacementPoint.x,
        y: state.selectedPlacementPoint.y,
        z: state.selectedPlacementPoint.z
      }
    }

    return state.selectedPlacement?.position || null
  }

  const placementCeilingY = () => {
    const box = state.framing?.box
    if (!box) return null

    const inset = Math.max((state.framing?.sphere?.radius || 1) * 0.02, 0.2)
    return box.max.y - inset
  }

  const resolvedPlacementPosition = (position, metadata = {}) => {
    if (!position) return null

    if (normalizePlacementVerticalMode(metadata.vertical_mode) !== "ceiling") return { ...position }

    const ceilingY = placementCeilingY()
    if (ceilingY === null) return { ...position }

    return {
      x: position.x,
      y: ceilingY,
      z: position.z
    }
  }

  const withResolvedPlacementPosition = (placement) => ({
    ...placement,
    metadata: placement.metadata || {},
    render_position: resolvedPlacementPosition(placement.position, placement.metadata || {})
  })

  const selectedPlacementMetadata = () => {
    if (state.selectedPlacement) {
      return {
        ...(state.selectedPlacement.metadata || {}),
        object_type: normalizeThingKind(placementThingKindInput?.value || "") || currentPlacementObjectType(),
        vertical_mode: currentPlacementVerticalMode()
      }
    }

    return {
      object_type: currentPlacementObjectType(),
      vertical_mode: currentPlacementVerticalMode()
    }
  }

  const selectedPlacementPosition = () => resolvedPlacementPosition(selectedPlacementSourcePosition(), selectedPlacementMetadata())

  const formatDetailValue = (value) => {
    if (value === null || value === undefined || value === "") return "-"
    if (typeof value === "object") return JSON.stringify(value)
    return String(value)
  }

  const refreshSelectWidget = (selectElement) => {
    if (!selectElement) return

    const currentValue = selectElement.value

    if (window.jQuery && window.jQuery.fn && window.jQuery.fn.select2) {
      const $select = window.jQuery(selectElement)

      if ($select.hasClass("select2-hidden-accessible")) {
        $select.select2("destroy")
      }
    }

    if (typeof window.initSelect2 === "function") {
      window.initSelect2(selectElement.parentElement || selectElement)
    }

    if (window.jQuery && window.jQuery.fn && window.jQuery.fn.select2) {
      const $select = window.jQuery(selectElement)
      if (Array.from(selectElement.options).some((option) => option.value === currentValue)) {
        $select.val(currentValue)
      }
      $select.trigger("change.select2")
      return
    }

    if (Array.from(selectElement.options).some((option) => option.value === currentValue)) {
      selectElement.value = currentValue
    }
  }

  const bindEnhancedSelectChange = (selectElement, handler) => {
    if (!selectElement) return

    let scheduled = false
    const scheduleHandler = () => {
      if (scheduled) return
      scheduled = true

      queueMicrotask(() => {
        scheduled = false
        handler()
      })
    }

    selectElement.addEventListener("change", scheduleHandler)

    if (window.jQuery) {
      window.jQuery(selectElement).on("select2:select select2:clear", scheduleHandler)
    }
  }

  const vectorFromPosition = (position) =>
    new THREE.Vector3(position?.x || 0, position?.y || 0, position?.z || 0)

  const navigationPointPoseFromHit = (hit) => {
    const point = hit?.point?.clone()
    if (!point) return null

    const worldNormal = hit.face?.normal?.clone()
    if (worldNormal) {
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld)
      worldNormal.applyMatrix3(normalMatrix).normalize()
    }

    const surfaceNormal =
      worldNormal && worldNormal.lengthSq() > 0.0001
        ? worldNormal
        : new THREE.Vector3(0, 1, 0)

    const rotation = new THREE.Euler().setFromQuaternion(
      new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        surfaceNormal
      ),
      "XYZ"
    )

    return {
      position: {
        x: Number(point.x.toFixed(4)),
        y: Number(point.y.toFixed(4)),
        z: Number(point.z.toFixed(4))
      },
      rotation: {
        x: Number(rotation.x.toFixed(4)),
        y: Number(rotation.y.toFixed(4)),
        z: Number(rotation.z.toFixed(4))
      }
    }
  }

  const searchEntryId = (entry) => `${entry.kind}:${entry.key}`

  let cancelCameraAnimation = () => {}
  let focusCameraOnPoint = () => {}
  let animateWalkthroughToNavigationPoint = () => {}
  let focusOnNodeObject = () => {}
  let focusOnPlacement = () => {}
  let focusOnNavigationPoint = () => {}

  ;({
    cancelCameraAnimation,
    focusCameraOnPoint,
    animateWalkthroughToNavigationPoint,
    focusOnNodeObject,
    focusOnPlacement,
    focusOnNavigationPoint
  } = createViewerCameraController({
    state,
    camera,
    perspectiveCamera,
    controls,
    setActiveCamera,
    applyOrbitBounds: (...args) => applyOrbitBounds(...args),
    setActiveButton: (...args) => setActiveButton(...args),
    notifyEmbedStateChanged: () => notifyEmbedStateChanged(),
    vectorFromPosition,
    clampNumber
  }))

  ;({
    closePopup,
    loadPopupLiveDetails,
    refreshPopupLiveDetails,
    executePopupAction,
    renderPopup
  } = createPopupController({
    state,
    popup,
    popupKicker,
    popupTitle,
    popupMeta,
    popupAttributes,
    selectedPlacementMappings,
    selectedNodeMappings,
    apiRequest: (...args) => apiRequest(...args),
    escapeHtml,
    formatDetailValue,
    formatPosition,
    normalizePlacementVerticalMode,
    renderLiveDetailBlocks
  }))

  ;({
    hideSearchResults,
    filteredSearchEntries,
    renderSearchResults,
    rebuildSearchEntries,
    focusSearchEntry
  } = createSearchController({
    state,
    searchInput,
    searchResults,
    searchEntryId,
    escapeHtml,
    formatPosition,
    resolveMappingObject: (key) => state.nodesByKey.get(key),
    resolvePlacement: (id) => state.placementsById.get(id),
    handlePlacementFocus: (placement) => {
      if (state.mappingMode) setPanelMode("place")
      selectPlacement(placement)
      focusOnPlacement(placement)

      if (state.mappingMode) {
        closePopup()
        setFeedback(`Focused ${placement.thing.name}.`, "success")
      } else {
        state.popupEntry = { kind: "placement", ...placement }
        renderPopup()
      }
    },
    handleMappingFocus: (object, mapping) => {
      if (state.mappingMode) setPanelMode("tag")
      selectNodeObject(object)
      focusOnNodeObject(object)

      if (state.mappingMode) {
        closePopup()
        setFeedback(`Focused ${mapping.thing.name}.`, "success")
      } else {
        state.popupEntry = { kind: "mapping", ...mapping }
        renderPopup()
      }
    }
  }))

  const clearHotspots = () => {
    state.hotspotElementsById.forEach((button) => button.remove())
    state.hotspotElementsById.clear()
  }

  const ensureHotspot = (placement) => {
    if (!labelsLayer) return null
    if (state.hotspotElementsById.has(placement.id)) return state.hotspotElementsById.get(placement.id)

    const button = document.createElement("button")
    button.type = "button"
    button.className = "plan-viewer-hotspot"
    button.dataset.hotspotPlacementId = placement.id
    button.innerHTML = '<span class="plan-viewer-hotspot-dot" aria-hidden="true"></span><span class="plan-viewer-hotspot-label"></span>'
    button.querySelector(".plan-viewer-hotspot-label").textContent = placement.thing.name
    labelsLayer.appendChild(button)
    state.hotspotElementsById.set(placement.id, button)
    return button
  }

  const placementHotspotWorldPosition = (object, { inlineRight = false } = {}) => {
    if (!object) return null

    const sprite = object.children.find((child) => child?.isSprite)
    if (!sprite) return object.getWorldPosition(new THREE.Vector3())

    const centerY = typeof sprite.center?.y === "number" ? sprite.center.y : 0.5
    const scaleY = typeof sprite.scale?.y === "number" ? sprite.scale.y : 0
    const opticalBiasY = inlineRight ? scaleY * 0.22 : 0
    const visualCenterOffsetY = sprite.position.y + (0.5 - centerY) * scaleY + opticalBiasY

    return object.localToWorld(new THREE.Vector3(0, visualCenterOffsetY, 0))
  }

  const updateHotspots = () => {
    if (!labelsLayer) return

    if (state.photo360 || state.photo360Loading) {
      labelsLayer.hidden = true
      labelsLayer.setAttribute("aria-hidden", "true")
      return
    }

    const shouldShow = state.labelsVisible && state.placementsById.size > 0
    labelsLayer.hidden = !shouldShow
    labelsLayer.setAttribute("aria-hidden", shouldShow ? "false" : "true")

    if (!shouldShow) return

    const visiblePlacementIds = new Set()
    const width = element.clientWidth || 1
    const height = element.clientHeight || 1
    const hotspotLift = Math.max((state.framing?.sphere?.radius || 1) * 0.03, 0.6)

    state.placementsById.forEach((placement) => {
      if (!labelVisibleForPlacement(placement)) return

      const object = state.placementObjectsById.get(placement.id)
      if (!object) return

      const button = ensureHotspot(placement)
      if (!button) return

      visiblePlacementIds.add(placement.id)
      button.querySelector(".plan-viewer-hotspot-label").textContent = placement.thing.name

      const inlineRight = is2DLikeMode(state.viewMode)
      const worldPosition =
        placementHotspotWorldPosition(object, { inlineRight }) || object.getWorldPosition(new THREE.Vector3())
      if (!inlineRight) worldPosition.y += hotspotLift
      const projected = worldPosition.project(camera)
      const onscreen =
        projected.z > -1 &&
        projected.z < 1 &&
        projected.x > -1.08 &&
        projected.x < 1.08 &&
        projected.y > -1.08 &&
        projected.y < 1.08

      if (!onscreen) {
        button.hidden = true
        return
      }

      button.hidden = false
      button.classList.toggle("is-inline-right", is2DLikeMode(state.viewMode))
      button.style.left = `${(projected.x * 0.5 + 0.5) * width}px`
      button.style.top = `${(-projected.y * 0.5 + 0.5) * height}px`
      button.classList.toggle("is-active", state.selectedPlacement?.id === placement.id && !state.mappingMode)
    })

    state.hotspotElementsById.forEach((button, placementId) => {
      if (!visiblePlacementIds.has(placementId)) {
        button.remove()
        state.hotspotElementsById.delete(placementId)
      }
    })
  }

  const clearFeedback = () => {
    if (state.feedbackTimer) {
      window.clearTimeout(state.feedbackTimer)
      state.feedbackTimer = null
    }

    ;[feedbackEl, viewerSettingsFeedbackEl].forEach((elementNode) => {
      if (!elementNode) return
      elementNode.textContent = ""
      elementNode.dataset.tone = "neutral"
      elementNode.hidden = true
    })
  }

  const setFeedback = (message, tone = "neutral", options = {}) => {
    const targetFeedbackEl =
      state.sidePanelMode === "viewer-settings" && viewerSettingsFeedbackEl
        ? viewerSettingsFeedbackEl
        : feedbackEl
    if (!targetFeedbackEl) return

    const suppressInViewerSettingsPanel =
      state.sidePanelMode === "viewer-settings" &&
      !options.force &&
      (tone === "neutral" || tone === "success")

    if (suppressInViewerSettingsPanel) {
      clearFeedback()
      return
    }

    if (!message) {
      clearFeedback()
      return
    }

    if (state.feedbackTimer) {
      window.clearTimeout(state.feedbackTimer)
      state.feedbackTimer = null
    }

    ;[feedbackEl, viewerSettingsFeedbackEl].forEach((elementNode) => {
      if (!elementNode || elementNode === targetFeedbackEl) return
      elementNode.textContent = ""
      elementNode.dataset.tone = "neutral"
      elementNode.hidden = true
    })

    if (options.html) {
      targetFeedbackEl.innerHTML = message
    } else {
      targetFeedbackEl.textContent = message
    }
    targetFeedbackEl.dataset.tone = tone
    targetFeedbackEl.hidden = false

    if ((tone === "neutral" || tone === "success") && !options.persist) {
      state.feedbackTimer = window.setTimeout(() => {
        clearFeedback()
      }, 3200)
    }
  }

  const removePhoto360Overlay = () => {
    state.photo360HotspotElementsById.forEach((button) => button.remove())
    state.photo360HotspotElementsById.clear()
    state.photo360MinimapRegions = []
    state.photo360Overlay?.remove()
    state.photo360Overlay = null
  }

  const removePhoto360LoadingOverlay = () => {
    state.photo360LoadingOverlay?.remove()
    state.photo360LoadingOverlay = null
    state.photo360LoadingMessage = null
  }

  const setPhoto360LoadingMessage = (message) => {
    if (!state.photo360LoadingMessage) return
    state.photo360LoadingMessage.textContent = message
  }

  const renderPhoto360LoadingOverlay = (label, message = "Preparing 360 view...") => {
    removePhoto360LoadingOverlay()

    const overlay = document.createElement("div")
    overlay.className = "plan-viewer-photo360-loading-status"
    overlay.innerHTML = `
      <div class="plan-viewer-photo360-loading-card">
        <span class="plan-viewer-photo360-loading-kicker">360 Photo</span>
        <strong>${escapeHtml(label || "Loading capture")}</strong>
        <p>${escapeHtml(message)}</p>
      </div>
    `
    element.appendChild(overlay)
    state.photo360LoadingOverlay = overlay
    state.photo360LoadingMessage = overlay.querySelector("p")
  }

  const setPhoto360Loading = (loading) => {
    state.photo360Loading = Boolean(loading)
    element.classList.toggle("is-photo360-loading", state.photo360Loading)

    if (!state.photo360Loading) removePhoto360LoadingOverlay()

    if (labelsLayer && state.photo360Loading) {
      labelsLayer.hidden = true
      labelsLayer.setAttribute("aria-hidden", "true")
    }

    syncPlacementVisibility()
    syncNavigationPointVisibility()
    updateHotspots()
  }

  const ensurePhoto360HotspotElement = (entry) => {
    if (!state.photo360Overlay) return null
    if (state.photo360HotspotElementsById.has(entry.captureId)) return state.photo360HotspotElementsById.get(entry.captureId)

    const container = state.photo360Overlay.querySelector("[data-photo360-hotspots]")
    if (!container) return null

    const button = document.createElement("button")
    button.type = "button"
    button.className = "plan-viewer-photo360-hotspot"
    button.dataset.photo360TargetCaptureId = entry.captureId
    button.innerHTML = `
      <span class="plan-viewer-photo360-hotspot-index"></span>
      <span class="plan-viewer-photo360-hotspot-copy">
        <strong></strong>
        <span></span>
      </span>
    `
    button.addEventListener("click", () => {
      openPhoto360Entry(entry)
    })
    container.appendChild(button)
    state.photo360HotspotElementsById.set(entry.captureId, button)
    return button
  }

  const photo360LocalDirectionVector = (fromEntry, toEntry) => {
    if (!fromEntry?.placement?.position || !toEntry?.placement?.position) return null

    const delta = new THREE.Vector3(
      parseFloatOr(toEntry.placement.position.x, 0) - parseFloatOr(fromEntry.placement.position.x, 0),
      parseFloatOr(toEntry.placement.position.y, 0) - parseFloatOr(fromEntry.placement.position.y, 0),
      parseFloatOr(toEntry.placement.position.z, 0) - parseFloatOr(fromEntry.placement.position.z, 0)
    )
    if (delta.lengthSq() < 0.0001) return null

    const currentYaw = parseFloatOr(fromEntry.placement.rotation?.y, 0)
    delta.applyAxisAngle(new THREE.Vector3(0, 1, 0), -currentYaw)

    return new THREE.Vector3(delta.x, delta.y * 0.45, -delta.z).normalize()
  }

  const photo360HotspotProjection = (fromEntry, toEntry) => {
    const localDirection = photo360LocalDirectionVector(fromEntry, toEntry)
    if (!localDirection) return null

    const point = localDirection.clone().multiplyScalar(12)
    const projected = point.project(perspectiveCamera)
    const visible =
      projected.z > -1 &&
      projected.z < 1 &&
      projected.x > -1.18 &&
      projected.x < 1.18 &&
      projected.y > -1.18 &&
      projected.y < 1.18

    return {
      projected,
      visible
    }
  }

  const normalizeDegrees = (value) => {
    const normalized = value % 360
    return normalized < 0 ? normalized + 360 : normalized
  }

  const radiansToDegrees = (value) => normalizeDegrees(THREE.MathUtils.radToDeg(value || 0))

  const photo360WorldBearingDegrees = (fromEntry, toEntry) => {
    if (!fromEntry?.placement?.position || !toEntry?.placement?.position) return null
    const dx = parseFloatOr(toEntry.placement.position.x, 0) - parseFloatOr(fromEntry.placement.position.x, 0)
    const dz = parseFloatOr(toEntry.placement.position.z, 0) - parseFloatOr(fromEntry.placement.position.z, 0)
    if (Math.hypot(dx, dz) < 0.0001) return null
    return normalizeDegrees(THREE.MathUtils.radToDeg(Math.atan2(dz, dx)))
  }

  const photo360LocalBearingDegrees = (fromEntry, toEntry) => {
    const localDirection = photo360LocalDirectionVector(fromEntry, toEntry)
    if (!localDirection) return null
    return normalizeDegrees(THREE.MathUtils.radToDeg(Math.atan2(localDirection.x, -localDirection.z)))
  }

  const photo360CameraBearingDegrees = () => {
    const direction = perspectiveCamera.getWorldDirection(new THREE.Vector3())
    direction.y = 0
    if (direction.lengthSq() < 0.0001) return null
    direction.normalize()
    return normalizeDegrees(THREE.MathUtils.radToDeg(Math.atan2(direction.x, -direction.z)))
  }

  const formatDebugAngle = (value) => Number.isFinite(value) ? `${value.toFixed(1)}deg` : "-"

  const photo360DirectionalEntries = (context = state.photo360?.context) => {
    if (!context?.currentEntry) return { forwardEntry: null, backEntry: null }

    const cameraDirection = perspectiveCamera.getWorldDirection(new THREE.Vector3())
    cameraDirection.y = 0
    if (cameraDirection.lengthSq() < 0.0001) {
      cameraDirection.set(0, 0, -1)
    } else {
      cameraDirection.normalize()
    }

    const candidates = Array.from(context.entries || [])
      .filter((entry) => entry.captureId !== context.currentEntry.captureId)
      .map((entry) => {
        const localDirection = photo360LocalDirectionVector(context.currentEntry, entry)
        if (!localDirection) return null

        localDirection.y = 0
        if (localDirection.lengthSq() < 0.0001) return null
        localDirection.normalize()

        return {
          entry,
          alignment: cameraDirection.dot(localDirection),
          distance: photo360HotspotDistanceMeters(context.currentEntry, entry) ?? Number.MAX_SAFE_INTEGER
        }
      })
      .filter(Boolean)

    const forwardEntry = candidates
      .filter((candidate) => candidate.alignment > 0.15)
      .sort((left, right) => right.alignment - left.alignment || left.distance - right.distance)[0]?.entry || null

    const backEntry = candidates
      .filter((candidate) => candidate.alignment < -0.15)
      .sort((left, right) => left.alignment - right.alignment || left.distance - right.distance)[0]?.entry || null

    return { forwardEntry, backEntry }
  }

  const renderPhoto360Minimap = () => {
    if (!state.photo360?.context || !state.photo360Overlay) return

    const canvas = state.photo360Overlay.querySelector("[data-photo360-minimap-canvas]")
    if (!canvas) return

    const context2d = canvas.getContext("2d")
    if (!context2d) return

    const bounds = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    const width = Math.max(Math.round(bounds.width * dpr), 1)
    const height = Math.max(Math.round(bounds.height * dpr), 1)
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
    }
    context2d.setTransform(dpr, 0, 0, dpr, 0, 0)
    context2d.clearRect(0, 0, bounds.width, bounds.height)

    const entries = Array.from(state.photo360.context.entries || [])
    if (!entries.length) return

    const padding = 16
    const minX = Math.min(...entries.map((entry) => parseFloatOr(entry.placement.position.x, 0)))
    const maxX = Math.max(...entries.map((entry) => parseFloatOr(entry.placement.position.x, 0)))
    const minZ = Math.min(...entries.map((entry) => parseFloatOr(entry.placement.position.z, 0)))
    const maxZ = Math.max(...entries.map((entry) => parseFloatOr(entry.placement.position.z, 0)))
    const spanX = Math.max(maxX - minX, 1)
    const spanZ = Math.max(maxZ - minZ, 1)
    const drawableWidth = Math.max(bounds.width - padding * 2, 1)
    const drawableHeight = Math.max(bounds.height - padding * 2, 1)
    const scale = Math.min(drawableWidth / spanX, drawableHeight / spanZ)
    const offsetX = (bounds.width - spanX * scale) / 2
    const offsetY = (bounds.height - spanZ * scale) / 2
    const pointFor = (entry) => ({
      x: offsetX + (parseFloatOr(entry.placement.position.x, 0) - minX) * scale,
      y: offsetY + (parseFloatOr(entry.placement.position.z, 0) - minZ) * scale
    })

    context2d.lineWidth = 2
    context2d.strokeStyle = "rgba(251, 146, 60, 0.75)"
    context2d.beginPath()
    entries.forEach((entry, index) => {
      const point = pointFor(entry)
      if (index === 0) context2d.moveTo(point.x, point.y)
      else context2d.lineTo(point.x, point.y)
    })
    context2d.stroke()

    state.photo360MinimapRegions = []
    const nearbyCaptureIds = new Set(photo360NearbyEntries(state.photo360.context).map((entry) => entry.captureId))
    entries.forEach((entry, index) => {
      const point = pointFor(entry)
      const isCurrent = entry.captureId === state.photo360.context.currentEntry?.captureId
      const isNearby = nearbyCaptureIds.has(entry.captureId)
      const radius = isCurrent ? 6 : isNearby ? 4.5 : 3.5

      context2d.fillStyle = isCurrent ? "#f97316" : isNearby ? "#fb923c" : "rgba(248, 250, 252, 0.88)"
      context2d.strokeStyle = isCurrent ? "#ffffff" : "rgba(255,255,255,0.92)"
      context2d.lineWidth = isCurrent ? 2.5 : 1.5
      context2d.beginPath()
      context2d.arc(point.x, point.y, radius, 0, Math.PI * 2)
      context2d.fill()
      context2d.stroke()

      if (isCurrent || isNearby) {
        context2d.fillStyle = isCurrent ? "#ffffff" : "#1e293b"
        context2d.font = "600 10px system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
        context2d.textAlign = "center"
        context2d.textBaseline = "bottom"
        context2d.fillText(String(index + 1), point.x, point.y - radius - 4)
      }

      state.photo360MinimapRegions.push({
        captureId: entry.captureId,
        x: point.x,
        y: point.y,
        radius: Math.max(radius + 6, 10),
        entry
      })
    })
  }

  const updatePhoto360Overlay = () => {
    if (!state.photo360 || !state.photo360Overlay) return

    const previousButton = state.photo360Overlay.querySelector("[data-photo360-previous]")
    const nextButton = state.photo360Overlay.querySelector("[data-photo360-next]")
    const forwardButton = state.photo360Overlay.querySelector("[data-photo360-forward]")
    const backButton = state.photo360Overlay.querySelector("[data-photo360-back]")
    if (previousButton) previousButton.disabled = !state.photo360.context?.previousEntry
    if (nextButton) nextButton.disabled = !state.photo360.context?.nextEntry
    const { forwardEntry, backEntry } = photo360DirectionalEntries(state.photo360.context)
    if (forwardButton) forwardButton.disabled = !forwardEntry
    if (backButton) backButton.disabled = !backEntry

    const debugPanel = state.photo360Overlay.querySelector("[data-photo360-debug]")
    if (debugPanel && state.photo360.context?.currentEntry) {
      const currentEntry = state.photo360.context.currentEntry
      const nearbyEntries = photo360NearbyEntries(state.photo360.context)
      const currentIndex = state.photo360.context.entries.findIndex((entry) => entry.captureId === currentEntry.captureId) + 1
      const currentYaw = radiansToDegrees(parseFloatOr(currentEntry.placement.rotation?.y, 0))
      const cameraBearing = photo360CameraBearingDegrees()

      debugPanel.innerHTML = `
        <div class="plan-viewer-photo360-debug-block">
          <strong>Debug</strong>
          <div>Current: #${currentIndex || "-"} | yaw ${formatDebugAngle(currentYaw)} | camera ${formatDebugAngle(cameraBearing)}</div>
          <div>Forward: ${forwardEntry ? `#${state.photo360.context.entries.findIndex((entry) => entry.captureId === forwardEntry.captureId) + 1}` : "-"}</div>
          <div>Back: ${backEntry ? `#${state.photo360.context.entries.findIndex((entry) => entry.captureId === backEntry.captureId) + 1}` : "-"}</div>
        </div>
        ${nearbyEntries.map((entry) => {
    const index = state.photo360.context.entries.findIndex((candidate) => candidate.captureId === entry.captureId) + 1
    const targetYaw = radiansToDegrees(parseFloatOr(entry.placement.rotation?.y, 0))
    const worldBearing = photo360WorldBearingDegrees(currentEntry, entry)
    const localBearing = photo360LocalBearingDegrees(currentEntry, entry)
    const distance = formatPhoto360Distance(photo360HotspotDistanceMeters(currentEntry, entry))
    return `
            <div class="plan-viewer-photo360-debug-row">
              <strong>#${index}</strong>
              <span>target ${formatDebugAngle(targetYaw)}</span>
              <span>world ${formatDebugAngle(worldBearing)}</span>
              <span>local ${formatDebugAngle(localBearing)}</span>
              <span>${distance || "-"}</span>
            </div>
          `
  }).join("")}
      `
    }

    const hotspotLayer = state.photo360Overlay.querySelector("[data-photo360-hotspots]")
    if (!hotspotLayer) return

    const nearbyEntries = photo360NearbyEntries(state.photo360.context)
    const width = element.clientWidth || 1
    const height = element.clientHeight || 1
    const visibleCaptureIds = new Set()

    nearbyEntries.forEach((entry) => {
      const button = ensurePhoto360HotspotElement(entry)
      if (!button) return

      visibleCaptureIds.add(entry.captureId)
      const projection = photo360HotspotProjection(state.photo360.context.currentEntry, entry)
      if (!projection?.visible) {
        button.hidden = true
        return
      }

      const title = entry.mapping?.external_label || entry.capture?.name || entry.placement?.thing?.name || "360 photo"
      const distance = formatPhoto360Distance(photo360HotspotDistanceMeters(state.photo360.context.currentEntry, entry))
      const index = state.photo360.context.entries.findIndex((candidate) => candidate.captureId === entry.captureId) + 1
      button.querySelector(".plan-viewer-photo360-hotspot-index").textContent = String(index)
      button.querySelector(".plan-viewer-photo360-hotspot-copy strong").textContent = title
      button.querySelector(".plan-viewer-photo360-hotspot-copy span").textContent = distance || "Open"
      button.style.left = `${(projection.projected.x * 0.5 + 0.5) * width}px`
      button.style.top = `${(-projection.projected.y * 0.5 + 0.5) * height}px`
      button.hidden = false
    })

    state.photo360HotspotElementsById.forEach((button, captureId) => {
      if (!visibleCaptureIds.has(captureId)) {
        button.hidden = true
      }
    })
  }

  const renderPhoto360Overlay = (label, capturedAt, context) => {
    removePhoto360Overlay()
    const capturedAtLabel = formatPhoto360CapturedAt(capturedAt)
    const currentIndex = context?.currentIndex >= 0 ? context.currentIndex + 1 : null
    const countLabel = context?.entries?.length ? `${currentIndex}/${context.entries.length}` : null

    const overlay = document.createElement("div")
    overlay.className = "plan-viewer-photo360-overlay"
    overlay.innerHTML = `
      <div class="plan-viewer-photo360-hotspots-layer" data-photo360-hotspots></div>
      <div class="plan-viewer-photo360-timeline" aria-label="360 photo timeline navigation preview">
            <button type="button" class="plan-viewer-photo360-timeline-button" data-photo360-previous ${context?.previousEntry ? "" : "disabled"} aria-label="Earlier capture" title="Earlier capture">
              <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
            </button>
            <div class="plan-viewer-photo360-captured-badge">${[
    countLabel ? `Photo ${escapeHtml(countLabel)}` : null,
    capturedAtLabel ? `Captured ${escapeHtml(capturedAtLabel)}` : null
  ].filter(Boolean).join(" · ")}</div>
            <button type="button" class="plan-viewer-photo360-timeline-button" data-photo360-next ${context?.nextEntry ? "" : "disabled"} aria-label="Later capture" title="Later capture">
              <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </button>
      </div>
      <div class="plan-viewer-photo360-minimap">
        <div class="plan-viewer-photo360-minimap-wrap">
          <div class="plan-viewer-photo360-minimap-nav">
            <button type="button" class="plan-viewer-photo360-direction-button" data-photo360-forward aria-label="Move forward" title="Move forward">
              <i class="fa-solid fa-arrow-up" aria-hidden="true"></i>
            </button>
            <button type="button" class="plan-viewer-photo360-direction-button" data-photo360-back aria-label="Move back" title="Move back">
              <i class="fa-solid fa-arrow-down" aria-hidden="true"></i>
            </button>
          </div>
          <div class="plan-viewer-photo360-minimap-card">
            <span class="plan-viewer-photo360-minimap-kicker">Session Map</span>
            <canvas class="plan-viewer-photo360-minimap-canvas" data-photo360-minimap-canvas></canvas>
            <div class="plan-viewer-photo360-debug" data-photo360-debug></div>
          </div>
        </div>
      </div>
      <div class="plan-viewer-photo360-bar">
        <div class="plan-viewer-photo360-copy">
          <span>360 Photo</span>
          <strong>${escapeHtml(label || "360 photo")}</strong>
        </div>
        <button type="button" class="plan-viewer-photo360-close">Back to model</button>
      </div>
    `
    overlay.querySelector("[data-photo360-previous]")?.addEventListener("click", () => {
      if (state.photo360?.context?.previousEntry) openPhoto360Entry(state.photo360.context.previousEntry)
    })
    overlay.querySelector("[data-photo360-next]")?.addEventListener("click", () => {
      if (state.photo360?.context?.nextEntry) openPhoto360Entry(state.photo360.context.nextEntry)
    })
    overlay.querySelector("[data-photo360-forward]")?.addEventListener("click", () => {
      const { forwardEntry } = photo360DirectionalEntries(state.photo360?.context)
      if (forwardEntry) openPhoto360Entry(forwardEntry)
    })
    overlay.querySelector("[data-photo360-back]")?.addEventListener("click", () => {
      const { backEntry } = photo360DirectionalEntries(state.photo360?.context)
      if (backEntry) openPhoto360Entry(backEntry)
    })
    overlay.querySelector("[data-photo360-minimap-canvas]")?.addEventListener("click", (event) => {
      const canvas = event.currentTarget
      const rect = canvas.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      const target = state.photo360MinimapRegions.find((region) =>
        Math.hypot(region.x - x, region.y - y) <= region.radius
      )
      if (target?.entry) openPhoto360Entry(target.entry)
    })
    overlay.querySelector(".plan-viewer-photo360-close")?.addEventListener("click", () => {
      closePhoto360Viewer()
    })
    element.appendChild(overlay)
    state.photo360Overlay = overlay
    updatePhoto360Overlay()
    renderPhoto360Minimap()
  }

  const openPhoto360Entry = (entry) => {
    if (!entry) return

    const capture = entry.capture || photo360CaptureForMapping(entry.mapping)
    const fileUrl = entry.mapping?.metadata?.plan_capture_file_url || capture?.file_url
    if (!fileUrl) {
      setFeedback("This 360 photo does not have an available file URL yet.", "error")
      return
    }

    openPhoto360Viewer({
      fileUrl,
      label: entry.mapping?.external_label || entry.capture?.name || entry.placement?.thing?.name || "360 photo",
      capturedAt: entry.mapping?.metadata?.plan_capture_captured_at || capture?.session?.captured_at,
      mapping: entry.mapping,
      placement: entry.placement
    })
  }

  const disposePhoto360Mesh = () => {
    if (!state.photo360) return

    scene.remove(state.photo360.mesh)
    state.photo360.mesh.geometry?.dispose()
    state.photo360.mesh.material?.dispose()
    state.photo360.texture?.dispose()
    state.photo360 = null
  }

  const syncPhoto360ContentVisibility = () => {
    const contentVisible = !state.photo360
    if (state.model) state.model.visible = contentVisible
    grid.visible = contentVisible
    state.placementObjectsById.forEach((object) => { object.visible = contentVisible && object.visible })
    state.navigationPointObjectsById.forEach((object) => { object.visible = contentVisible && object.visible })
    state.referenceLayerOverlaysById.forEach((entry) => {
      entry.overlay.visible = contentVisible && entry.overlay.visible
    })
    if (state.selectionHelper) state.selectionHelper.visible = contentVisible
    if (state.selectionMarker) state.selectionMarker.visible = contentVisible
    if (state.hoverHelper) state.hoverHelper.visible = contentVisible
    if (state.hoverMarker) state.hoverMarker.visible = contentVisible
    if (labelsLayer && state.photo360) {
      labelsLayer.hidden = true
      labelsLayer.setAttribute("aria-hidden", "true")
    }
  }

  const applyPhoto360Controls = () => {
    setActiveCamera(perspectiveCamera)
    perspectiveCamera.fov = state.photo360?.fov || photo360DefaultFov
    perspectiveCamera.updateProjectionMatrix()
    camera.position.set(0, 0, 0.01)
    controls.target.set(0, 0, 0)
    controls.enablePan = false
    controls.enableZoom = false
    controls.enableRotate = true
    controls.autoRotate = false
    controls.minDistance = 0.01
    controls.maxDistance = 0.01
    controls.minPolarAngle = 0.05
    controls.maxPolarAngle = Math.PI - 0.05
    controls.mouseButtons = defaultMouseButtons
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.ROTATE }
    controls.update()
  }

  const setPhoto360Fov = (fov) => {
    if (!state.photo360) return

    perspectiveCamera.fov = clampNumber(fov, photo360MinFov, photo360MaxFov)
    perspectiveCamera.updateProjectionMatrix()
    state.photo360.fov = perspectiveCamera.fov
  }

  const handlePhoto360Wheel = (event) => {
    if (!state.photo360) return

    event.preventDefault()
    event.stopImmediatePropagation()
    const fovDelta = clampNumber(event.deltaY * 0.035, -8, 8)
    setPhoto360Fov(perspectiveCamera.fov + fovDelta)
  }

  const openPhoto360Viewer = ({ fileUrl, label, capturedAt, mapping = null, placement = null }) => {
    if (!fileUrl) return

    const loadToken = Symbol("photo360-load")
    state.photo360LoadToken = loadToken
    const photoContext = mapping ? photo360EntryForMapping(mapping, placement) : null
    const requestedFov = state.photo360?.fov || photo360DefaultFov
    const previous = state.photo360?.previous || {
      camera,
      cameraPosition: camera.position.clone(),
      fov: camera.isPerspectiveCamera ? camera.fov : perspectiveCamera.fov,
      target: controls.target.clone(),
      viewMode: state.viewMode,
      enablePan: controls.enablePan,
      enableZoom: controls.enableZoom,
      enableRotate: controls.enableRotate,
      autoRotate: controls.autoRotate,
      minDistance: controls.minDistance,
      maxDistance: controls.maxDistance,
      minPolarAngle: controls.minPolarAngle,
      maxPolarAngle: controls.maxPolarAngle,
      mouseButtons: { ...controls.mouseButtons },
      touches: { ...controls.touches }
    }

    disposePhoto360Mesh()
    removePhoto360Overlay()
    setPhoto360Loading(true)
    renderPhoto360LoadingOverlay(label, "Preparing 360 view...")
    const loadingStartedAt = performance.now()
    setFeedback(`Loading ${label}.`, "working", { persist: true })

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (state.photo360LoadToken !== loadToken) return

        setPhoto360LoadingMessage("Fetching photo from secure storage...")
        photo360TextureLoader.load(
          fileUrl,
          (texture) => {
            setPhoto360LoadingMessage("Building panoramic scene...")
            const showPanorama = () => {
              if (state.photo360LoadToken !== loadToken) {
                texture.dispose()
                return
              }

              texture.colorSpace = THREE.SRGBColorSpace
              texture.wrapS = THREE.RepeatWrapping
              texture.repeat.x = -1
              texture.offset.x = 1

              const mesh = new THREE.Mesh(
                new THREE.SphereGeometry(500, 96, 48),
                new THREE.MeshBasicMaterial({
                  map: texture,
                  side: THREE.BackSide
                })
              )
              mesh.name = "photo360-panorama"
              scene.add(mesh)
              state.photo360 = {
                mesh,
                texture,
                label,
                capturedAt,
                fileUrl,
                mapping,
                placement,
                context: photoContext,
                previous,
                fov: requestedFov
              }
              state.photo360LoadToken = null
              renderPhoto360Overlay(label, capturedAt, photoContext)
              setPhoto360LoadingMessage("Placing you inside capture...")
              setPhoto360Loading(false)
              clearSelectedObject()
              closePopup()
              applyPhoto360Controls()
              syncPhoto360ContentVisibility()
              updatePhoto360Overlay()
              renderPhoto360Minimap()
              setFeedback(`Viewing ${label}.`, "success")
            }

            const remainingLoadingMs = Math.max(0, photo360MinimumLoadingMs - (performance.now() - loadingStartedAt))
            if (remainingLoadingMs > 0) {
              window.setTimeout(showPanorama, remainingLoadingMs)
            } else {
              showPanorama()
            }
          },
          undefined,
          () => {
            if (state.photo360LoadToken !== loadToken) return
            state.photo360LoadToken = null
            setPhoto360Loading(false)
            removePhoto360Overlay()
            setFeedback("Could not load this 360 photo.", "error")
          }
        )
      })
    })
  }

  const closePhoto360Viewer = ({ showFeedback = true } = {}) => {
    const previous = state.photo360?.previous
    state.photo360LoadToken = null
    setPhoto360Loading(false)
    disposePhoto360Mesh()
    removePhoto360Overlay()

    if (previous) {
      setActiveCamera(previous.camera || perspectiveCamera)
      if (camera.isPerspectiveCamera && Number.isFinite(previous.fov)) {
        camera.fov = previous.fov
        camera.updateProjectionMatrix()
      }
      camera.position.copy(previous.cameraPosition)
      controls.target.copy(previous.target)
      controls.enablePan = previous.enablePan
      controls.enableZoom = previous.enableZoom
      controls.enableRotate = previous.enableRotate
      controls.autoRotate = previous.autoRotate
      controls.minDistance = previous.minDistance
      controls.maxDistance = previous.maxDistance
      controls.minPolarAngle = previous.minPolarAngle
      controls.maxPolarAngle = previous.maxPolarAngle
      controls.mouseButtons = previous.mouseButtons
      controls.touches = previous.touches
      state.viewMode = previous.viewMode || state.viewMode
      controls.update()
    }

    syncPhoto360ContentVisibility()
    syncPlacementVisibility()
    syncNavigationPointVisibility()
    syncCablingPlanOverlayPresentation()
    updateHotspots()
    setActiveButton()
    state.photo360MinimapRegions = []
    state.photo360HotspotElementsById.clear()

    if (showFeedback) setFeedback("Returned to model view.", "neutral")
  }

  const disposeHelperObject = (helper) => {
    if (!helper) return

    scene.remove(helper)
    helper.traverse((child) => {
      if (child.geometry) child.geometry.dispose()
      if (child.material) {
        if (Array.isArray(child.material)) child.material.forEach((material) => material.dispose())
        else child.material.dispose()
      }
    })
  }

  const clearSelectionHelper = () => {
    if (!state.selectionHelper) return

    disposeHelperObject(state.selectionHelper)
    state.selectionHelper = null
  }

  const clearSelectionMarker = () => {
    if (!state.selectionMarker) return

    disposeHelperObject(state.selectionMarker)
    state.selectionMarker = null
  }

  const clearHoverHelper = () => {
    if (!state.hoverHelper) return

    disposeHelperObject(state.hoverHelper)
    state.hoverHelper = null
  }

  const setNavigationPointHoverState = (object, hovered) => {
    if (!object?.userData?.dtNavigationPointId) return

    object.traverse((child) => {
      if (!child.isMesh || !child.material) return

      const baseColor = child.userData.dtNavigationBaseColor
      const baseEmissive = child.userData.dtNavigationBaseEmissive
      if (baseColor === null || baseEmissive === null) return

      child.material.color.setHex(hovered ? 0x16a34a : baseColor)
      child.material.emissive.setHex(hovered ? 0x86efac : baseEmissive)
    })
  }

  const clearNavigationPointHoverState = () => {
    if (!state.hoveredNavigationPointObject) return

    setNavigationPointHoverState(state.hoveredNavigationPointObject, false)
    state.hoveredNavigationPointObject = null
  }

  const clearHoverMarker = () => {
    if (!state.hoverMarker) return

    disposeHelperObject(state.hoverMarker)
    state.hoverMarker = null
  }

  const anchorForObject = (object, point) => {
    if (point) return point.clone()

    const box = new THREE.Box3().setFromObject(object)
    return box.getCenter(new THREE.Vector3())
  }

  const buildOutlineHelper = (object, color) => {
    const outlineGroup = new THREE.Group()
    const lineMaterial = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: 0.95,
      depthTest: false
    })

    object.traverse((child) => {
      if (!child.isMesh || !child.geometry) return

      const edges = new THREE.EdgesGeometry(child.geometry, 32)
      const lines = new THREE.LineSegments(edges, lineMaterial.clone())
      lines.matrix.copy(child.matrixWorld)
      lines.matrixAutoUpdate = false
      outlineGroup.add(lines)
    })

    return outlineGroup
  }

const buildPointMarker = (point, color, emissive) => {
  const markerRadius = Math.max((state.framing?.sphere?.radius || 1) * 0.015, 0.08)
  const marker = new THREE.Mesh(
      new THREE.SphereGeometry(markerRadius, 24, 24),
      new THREE.MeshStandardMaterial({
        color,
        emissive,
        emissiveIntensity: 0.85,
        metalness: 0.1,
        roughness: 0.3,
        depthTest: false
      })
    )

    marker.renderOrder = 1000
  marker.position.copy(point)
  return marker
}

  const currentPlacementMarkerKind = () => {
    if (state.selectedPlacement?.thing?.kind) return state.selectedPlacement.thing.kind
    if (placementThingKindInput?.value) return placementThingKindInput.value
    return "sensor"
  }

  const hoveredPlacementMarkerKind = () => {
    const placementId = state.hoveredObject?.userData?.dtPlacementId
    if (placementId) {
      const placement = state.placementsById.get(placementId)
      if (placement?.thing?.kind) return placement.thing.kind
    }

    if (placementThingKindInput?.value) return placementThingKindInput.value
    return "sensor"
  }

  const buildPlacementPointMarker = (point, kind, tone = "selected") => {
    const definition = placementKindIconDefinition(kind)
    const markerScale = placementVisualUnitSize(state.framing) * definition.sceneScale * state.viewerSettings.objectIconScale
    const shadowOpacity = tone === "selected" ? 0.2 : 0.12
    const spriteOpacity = tone === "selected" ? 1 : 0.84
    const group = new THREE.Group()
    const shadow = new THREE.Mesh(
      new THREE.CircleGeometry(markerScale * 0.17, 24),
      new THREE.MeshBasicMaterial({
        color: definition.accent,
        transparent: true,
        opacity: shadowOpacity,
        depthWrite: false,
        depthTest: false
      })
    )

    shadow.rotation.x = -Math.PI / 2
    shadow.position.y = 0.004
    shadow.renderOrder = 1000

    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: placementKindIconTexture(kind),
        transparent: true,
        opacity: spriteOpacity,
        alphaTest: 0.05,
        depthWrite: false,
        depthTest: false
      })
    )

    sprite.center.set(0.5, 0.08)
    sprite.scale.set(markerScale, markerScale, 1)
    sprite.position.y = 0.08
    sprite.renderOrder = 1001

    group.add(shadow, sprite)
    group.position.copy(point)
    return group
  }

  const currentSelectedVisualObject = () =>
    state.selectedNavigationPointObject || state.selectedPlacementObject || state.selectedObject

  const currentSelectedVisualPoint = () => {
    if (state.selectedNavigationPoint) return vectorFromPosition(state.selectedNavigationPoint.position)
    if (state.selectedPlacementPoint) return vectorFromPosition(selectedPlacementPosition())
    if (state.selectedPoint) return state.selectedPoint
    return null
  }

  const refreshSelectionHelper = () => {
    clearSelectionHelper()
    clearSelectionMarker()
    const selectedVisualObject = currentSelectedVisualObject()
    const selectedVisualPoint = currentSelectedVisualPoint()
    if (!selectedVisualObject && !selectedVisualPoint) return

    if (selectedVisualObject) {
      state.selectionHelper = buildOutlineHelper(selectedVisualObject, 0x21529a)
      scene.add(state.selectionHelper)
    }

    const selectionPoint = selectedVisualObject ? anchorForObject(selectedVisualObject, selectedVisualPoint) : selectedVisualPoint
    const placementPointSelected = Boolean(state.selectedPlacementPoint)

    state.selectionMarker = placementPointSelected
      ? buildPlacementPointMarker(selectionPoint, currentPlacementMarkerKind(), "selected")
      : buildPointMarker(selectionPoint, 0x0d6f63, 0x34d3a2)
    scene.add(state.selectionMarker)
  }

  const refreshHoverHelper = () => {
    clearNavigationPointHoverState()
    clearHoverHelper()
    clearHoverMarker()
    if (!state.hoveredObject && !state.hoveredPoint) return

    if (state.hoveredObject?.userData?.dtNavigationPointId) {
      setNavigationPointHoverState(state.hoveredObject, true)
      state.hoveredNavigationPointObject = state.hoveredObject
      return
    }

    if (state.hoveredObject && state.hoveredObject === currentSelectedVisualObject()) return

    if (state.hoveredObject) {
      state.hoverHelper = buildOutlineHelper(state.hoveredObject, 0x21529a)
      scene.add(state.hoverHelper)
    }

    const hoverPoint = state.hoveredObject
      ? anchorForObject(state.hoveredObject, state.hoveredPoint)
      : state.panelMode === "place"
        ? vectorFromPosition(resolvedPlacementPosition(
            state.hoveredPoint,
            { vertical_mode: currentPlacementVerticalMode() }
          ))
        : state.hoveredPoint
    const placementVisualHovered = state.panelMode === "place"

    state.hoverMarker = placementVisualHovered
      ? buildPlacementPointMarker(hoverPoint, hoveredPlacementMarkerKind(), "hover")
      : buildPointMarker(hoverPoint, 0x21529a, 0x9ec5ff)
    scene.add(state.hoverMarker)
  }

  const populateThingSelect = () => {
    if (!existingThingSelect) return

    const currentValue = existingThingSelect.value
    existingThingSelect.innerHTML = '<option value="">Select a thing</option>'

    state.things.forEach((thing) => {
      const option = document.createElement("option")
      option.value = thing.id
      option.textContent = thing.kind ? `${thing.name} (${thing.kind})` : thing.name
      existingThingSelect.appendChild(option)
    })

    if (state.things.some((thing) => thing.id === currentValue)) {
      existingThingSelect.value = currentValue
    }

    refreshSelectWidget(existingThingSelect)
  }

  const populatePlacementThingSelect = () => {
    if (!placementExistingThingSelect) return

    const currentValue = placementExistingThingSelect.value
    placementExistingThingSelect.innerHTML = '<option value="">Select a thing</option>'

    state.things.forEach((thing) => {
      const option = document.createElement("option")
      option.value = thing.id
      option.textContent = thing.kind ? `${thing.name} (${thing.kind})` : thing.name
      placementExistingThingSelect.appendChild(option)
    })

    if (state.things.some((thing) => thing.id === currentValue)) {
      placementExistingThingSelect.value = currentValue
    }

    refreshSelectWidget(placementExistingThingSelect)
  }

  const selectedIntegrationDefinition = (selectElement = nodeMappingIntegrationSelect) => {
    if (!selectElement?.value) return null
    return state.integrations.find((integration) => integration.id === selectElement.value) || null
  }

  const selectedObjectTypeDefinition = (integrationSelect = nodeMappingIntegrationSelect, objectTypeSelect = nodeMappingObjectTypeSelect) => {
    const integration = selectedIntegrationDefinition(integrationSelect)
    if (!integration || !objectTypeSelect?.value) return null

    return integration.object_types.find((objectType) => objectType.key === objectTypeSelect.value) || null
  }

  const populateIntegrationSelect = (selectElement = nodeMappingIntegrationSelect) => {
    if (!selectElement) return

    const currentValue = selectElement.value
    selectElement.innerHTML = '<option value="">Select an integration</option>'

    state.integrations.forEach((integration) => {
      const option = document.createElement("option")
      option.value = integration.id
      option.textContent = integration.name
      selectElement.appendChild(option)
    })

    if (state.integrations.some((integration) => integration.id === currentValue)) {
      selectElement.value = currentValue
    }

    refreshSelectWidget(selectElement)
  }

  const populateObjectTypeSelect = (integrationSelect = nodeMappingIntegrationSelect, objectTypeSelect = nodeMappingObjectTypeSelect) => {
    if (!objectTypeSelect) return

    const integration = selectedIntegrationDefinition(integrationSelect)
    const objectTypes = integration?.object_types || []
    const currentValue = objectTypeSelect.value
    objectTypeSelect.innerHTML = '<option value="">Select an object type</option>'

    objectTypes.forEach((objectType) => {
      const option = document.createElement("option")
      option.value = objectType.key
      option.textContent = objectType.label
      objectTypeSelect.appendChild(option)
    })

    if (objectTypes.some((objectType) => objectType.key === currentValue)) {
      objectTypeSelect.value = currentValue
    } else if (objectTypes.length) {
      objectTypeSelect.value = objectTypes[0].key
    }

    refreshSelectWidget(objectTypeSelect)
  }

  const populateExternalObjectSelect = (
    options = [],
    selectElement = nodeMappingExternalObjectSelect,
    { placeholder = "Select an object", disabled = options.length === 0 } = {}
  ) => {
    if (!selectElement) return

    const currentValue = selectElement.value
    selectElement.dataset.select2Placeholder = placeholder
    selectElement.innerHTML = ""

    const placeholderOption = document.createElement("option")
    placeholderOption.value = ""
    placeholderOption.textContent = placeholder
    selectElement.appendChild(placeholderOption)

    options.forEach((optionData) => {
      const option = document.createElement("option")
      option.value = optionData.id
      option.textContent = optionData.name
      option.dataset.externalMetadata = JSON.stringify(optionData.metadata || {})
      selectElement.appendChild(option)
    })

    if (options.some((optionData) => optionData.id === currentValue)) {
      selectElement.value = currentValue
    }

    selectElement.disabled = disabled
    refreshSelectWidget(selectElement)
  }

  const nodeMappingCollectionKey = (integrationId, objectType) => `${integrationId}:${objectType}`

  const fetchNodeMappingCollection = async ({
    integrationSelect = nodeMappingIntegrationSelect,
    objectTypeSelect = nodeMappingObjectTypeSelect,
    externalObjectSelect = nodeMappingExternalObjectSelect
  } = {}) => {
    const integration = selectedIntegrationDefinition(integrationSelect)
    const objectType = selectedObjectTypeDefinition(integrationSelect, objectTypeSelect)

    if (!integration || !objectType) {
      populateExternalObjectSelect([], externalObjectSelect, {
        placeholder: "Select an object type first",
        disabled: true
      })
      return
    }

    const cacheKey = nodeMappingCollectionKey(integration.id, objectType.key)
    if (state.integrationCollections.has(cacheKey)) {
      const cachedCollection = state.integrationCollections.get(cacheKey)
      populateExternalObjectSelect(cachedCollection, externalObjectSelect, {
        placeholder: cachedCollection.length ? "Select an object" : "No objects available",
        disabled: cachedCollection.length === 0
      })
      return
    }

    populateExternalObjectSelect([], externalObjectSelect, {
      placeholder: "Loading objects...",
      disabled: true
    })
    setFeedback("Loading integration objects...", "working")

    try {
      const collection = await apiRequest(objectType.collection_url)
      state.integrationCollections.set(cacheKey, collection)
      populateExternalObjectSelect(collection, externalObjectSelect, {
        placeholder: collection.length ? "Select an object" : "No objects available",
        disabled: collection.length === 0
      })
      setFeedback(
        collection.length ? "Integration objects loaded." : "This integration returned no objects for the selected type.",
        collection.length ? "success" : "neutral"
      )
    } catch (error) {
      populateExternalObjectSelect([], externalObjectSelect, {
        placeholder: "Could not load objects",
        disabled: true
      })
      setFeedback(`Could not load integration objects: ${error.message}`, "error")
    }
  }

  const selectedObjectLabel = () => {
    if (!state.selectedObject) return "Nothing selected"

    if (hasUsefulNodeName(state.selectedObject)) return state.selectedObject.name

    return state.selectedObject.userData.dtNodeKey || state.selectedObject.type || "Unnamed node"
  }

  const selectedOptionMetadata = (selectedOption) => {
    if (!selectedOption?.dataset?.externalMetadata) return {}

    try {
      const metadata = JSON.parse(selectedOption.dataset.externalMetadata)
      return metadata && typeof metadata === "object" && !Array.isArray(metadata) ? metadata : {}
    } catch (_error) {
      return {}
    }
  }

  const fillThingForm = (mapping) => {
    if (!thingNameInput || !thingKindInput || !thingDescriptionInput || !thingAttributesInput) return

    if (mapping) {
      thingNameInput.value = mapping.thing.name || ""
      thingKindInput.value = mapping.thing.kind || ""
      thingDescriptionInput.value = mapping.thing.description || ""
      thingAttributesInput.value = JSON.stringify(mapping.thing.attributes || {}, null, 2)
      populateKindSelect(thingKindInput, tagKindOptions(), thingKindInput.value)
      if (thingSubmitButton) thingSubmitButton.textContent = "Save Tag"
      return
    }

    thingNameInput.value = ""
    thingKindInput.value = ""
    thingDescriptionInput.value = ""
    thingAttributesInput.value = ""
    populateKindSelect(thingKindInput, tagKindOptions(), thingKindInput.value)
    if (thingSubmitButton) thingSubmitButton.textContent = "Save Tag"
  }

  const fillPlacementThingForm = (placement) => {
    if (!placementThingNameInput || !placementThingKindInput || !placementThingDescriptionInput || !placementThingAttributesInput) return
    const builtInPlacementKinds = ["sensor", "wireless_access_point", "label", "360_photo"]

    if (placement) {
      placementThingNameInput.value = placement.thing.name || ""
      placementThingKindInput.value = placement.thing.kind || ""
      placementThingDescriptionInput.value = placement.thing.description || ""
      placementThingAttributesInput.value = JSON.stringify(placement.thing.attributes || {}, null, 2)
      populateKindSelect(placementThingKindInput, [...placementKindOptions(), ...builtInPlacementKinds], placementThingKindInput.value)
      setPlacementObjectType(placement.metadata?.object_type || placement.thing.kind)
      setPlacementVerticalMode(placement.metadata?.vertical_mode)
      syncPlacementVerticalModeAvailability()
      if (placementThingSubmitButton) placementThingSubmitButton.textContent = "Save Object"
      return
    }

    if (state.selectedPlacementPoint) {
      populateKindSelect(placementThingKindInput, [...placementKindOptions(), ...builtInPlacementKinds], placementThingKindInput.value)
      if (!placementPresetButtons.some((button) => button.classList.contains("is-active"))) {
        setPlacementObjectType("sensor")
      }
      if (!placementVerticalModeButtons.some((button) => button.classList.contains("is-active"))) {
        setPlacementVerticalMode("surface")
      }
      syncPlacementVerticalModeAvailability()
      if (placementThingSubmitButton) placementThingSubmitButton.textContent = "Save Object"
      return
    }

    placementThingNameInput.value = ""
    placementThingKindInput.value = ""
    placementThingDescriptionInput.value = ""
    placementThingAttributesInput.value = ""
    populateKindSelect(placementThingKindInput, [...placementKindOptions(), ...builtInPlacementKinds], placementThingKindInput.value)
    setPlacementObjectType("sensor")
    setPlacementVerticalMode("surface")
    syncPlacementVerticalModeAvailability()
    if (placementThingSubmitButton) placementThingSubmitButton.textContent = "Save Object"
  }

  const syncPlacementPresetButtons = () => {
    const selectedKind = currentPlacementObjectType()

    placementPresetButtons.forEach((button) => {
      const active = button.dataset.placementKindPreset === selectedKind
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  const uniqueSortedKinds = (values) =>
    Array.from(
      values.reduce((result, value) => {
        const rawValue = value?.toString().trim()
        if (!rawValue) return result

        const normalizedValue = normalizeThingKind(rawValue)
        if (!normalizedValue) return result
        if (!result.has(normalizedValue)) result.set(normalizedValue, rawValue)
        return result
      }, new Map()).values()
    ).sort((left, right) => left.localeCompare(right))

  const tagKindOptions = () =>
    uniqueSortedKinds(
      Array.from(state.mappingsByNodeKey.values()).map((mapping) => mapping.thing?.kind)
    )

  const placementKindOptions = () =>
    uniqueSortedKinds(
      Array.from(state.placementsById.values()).map((placement) => placement.thing?.kind)
    )

  ;({
    referenceLayerStatusMessage,
    allLabelObjectTypes,
    allLabelThingTypes,
    allLabelIntegrations,
    allLabelIntegrationObjectTypes,
    labelVisibleForPlacement,
    renderLabelsMenu,
    renderReferenceLayerMenu,
    renderReferenceLayerList,
    applyViewerLabelsVisibilityPreference
  } = createViewerMenusController({
    state,
    labelsPanel,
    labelsMarkersList,
    labelsObjectTypesList,
    labelsThingTypesList,
    labelsIntegrationsList,
    labelsIntegrationObjectTypesList,
    referenceLayerMenuList,
    referenceLayerList,
    activeReferenceLayer,
    cloneCablingPlanSettings,
    escapeHtml,
    placementObjectTypeLabel,
    builtInPlacementObjectTypes: BUILT_IN_PLACEMENT_OBJECT_TYPES,
    uniqueSortedKinds,
    placementObjectType,
    placementLabelMappings
  }))

  const populateKindSelect = (selectElement, values, currentValue = "") => {
    if (!selectElement) return

    const selectedValue = currentValue?.toString().trim() || ""
    selectElement.innerHTML = '<option value=""></option>'

    uniqueSortedKinds([...(values || []), selectedValue]).forEach((value) => {
      const option = document.createElement("option")
      option.value = value
      option.textContent = value
      selectElement.appendChild(option)
    })

    refreshSelectWidget(selectElement)

    if (window.jQuery && window.jQuery.fn && window.jQuery.fn.select2) {
      window.jQuery(selectElement).val(selectedValue).trigger("change.select2")
    } else {
      selectElement.value = selectedValue
    }
  }

  const setTaggableSelectValue = (selectElement, value) => {
    if (!selectElement) return

    const normalizedValue = value?.toString().trim() || ""
    if (
      normalizedValue &&
      !Array.from(selectElement.options).some((option) => option.value === normalizedValue)
    ) {
      selectElement.appendChild(new Option(normalizedValue, normalizedValue, true, true))
    }

    if (window.jQuery && window.jQuery.fn && window.jQuery.fn.select2) {
      window.jQuery(selectElement).val(normalizedValue).trigger("change")
    } else {
      selectElement.value = normalizedValue
      selectElement.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  const renderMappingList = () => {
    if (!mappingList) return

    const mappings = Array.from(state.mappingsByNodeKey.values())
    if (mappings.length === 0) {
      mappingList.innerHTML = '<p class="plan-mapping-empty">No tagged items yet.</p>'
      return
    }

    mappingList.innerHTML = mappings
      .sort((left, right) => left.thing.name.localeCompare(right.thing.name))
      .map((mapping) => {
        const label = mapping.model_node_name || mapping.model_node_key
        const kind = mapping.thing.kind ? `<span class="plan-mapping-chip">${mapping.thing.kind}</span>` : ""

        return `
          <button type="button" class="plan-mapping-list-item" data-node-key="${mapping.model_node_key}">
            <span class="plan-mapping-list-title">${mapping.thing.name}</span>
            ${kind}
            <span class="plan-mapping-list-subtitle">${label}</span>
          </button>
        `
      })
      .join("")
  }

  const renderNodeMappingsList = () => {
    if (!nodeMappingsList) return

    const mappings = selectedNodeMappings()
    if (mappings.length === 0) {
      nodeMappingsList.innerHTML = '<p class="plan-mapping-empty">No external mappings for this node yet.</p>'
      return
    }

    nodeMappingsList.innerHTML = mappings
      .sort((left, right) => left.integration_name.localeCompare(right.integration_name) || left.object_label.localeCompare(right.object_label))
      .map((mapping) => `
        <div class="plan-mapping-list-item">
          <span class="plan-mapping-list-title">${escapeHtml(mapping.external_label || mapping.external_id)}</span>
          <span class="plan-mapping-chip">${escapeHtml(mapping.integration_name)}</span>
          <span class="plan-mapping-list-subtitle">${escapeHtml(mapping.object_label)}</span>
          <div class="plan-mapping-actions">
            <button type="button" class="btn btn-ghost" data-node-mapping-remove="${mapping.id}">Remove</button>
          </div>
        </div>
      `)
      .join("")
  }

  const renderPlacementNodeMappingsList = () => {
    if (!placementNodeMappingsList) return

    const mappings = selectedPlacementMappings()
    if (mappings.length === 0) {
      placementNodeMappingsList.innerHTML = '<p class="plan-mapping-empty">No external mappings for this placed object yet.</p>'
      return
    }

    placementNodeMappingsList.innerHTML = mappings
      .sort((left, right) => left.integration_name.localeCompare(right.integration_name) || left.object_label.localeCompare(right.object_label))
      .map((mapping) => `
        <div class="plan-mapping-list-item">
          <span class="plan-mapping-list-title">${escapeHtml(mapping.external_label || mapping.external_id)}</span>
          <span class="plan-mapping-chip">${escapeHtml(mapping.integration_name)}</span>
          <span class="plan-mapping-list-subtitle">${escapeHtml(mapping.object_label)}</span>
          <div class="plan-mapping-actions">
            <button type="button" class="btn btn-ghost" data-placement-node-mapping-remove="${mapping.id}">Remove</button>
          </div>
        </div>
      `)
      .join("")
  }

  const renderPlacementList = () => {
    if (!placementList) return

    const placements = Array.from(state.placementsById.values())
    if (placements.length === 0) {
      placementList.innerHTML = '<p class="plan-mapping-empty">No placed objects yet.</p>'
      return
    }

    placementList.innerHTML = placements
      .sort((left, right) => left.thing.name.localeCompare(right.thing.name))
      .map((placement) => {
        const kind = placement.thing.kind ? `<span class="plan-mapping-chip">${placement.thing.kind}</span>` : ""
        const activeClass = state.selectedPlacement?.id === placement.id ? " is-active" : ""

        return `
          <div class="plan-mapping-list-item plan-mapping-list-item-with-actions${activeClass}">
            <button type="button" class="plan-mapping-list-card" data-placement-id="${placement.id}">
              <span class="plan-mapping-list-title">${placement.thing.name}</span>
              ${kind}
              <span class="plan-mapping-list-subtitle">${formatPosition(placement.render_position || placement.position)}</span>
            </button>
            <button
              type="button"
              class="plan-mapping-list-delete"
              data-delete-placement-id="${placement.id}"
              aria-label="Delete ${escapeHtml(placement.thing.name)}"
              title="Delete placed object">
              <i class="fa-solid fa-trash" aria-hidden="true"></i>
            </button>
          </div>
        `
      })
      .join("")
  }

  const renderTagInspector = () => {
    const mapping = selectedMapping()

    if (selectedNameEl) selectedNameEl.textContent = selectedObjectLabel()
    if (selectedKeyEl) selectedKeyEl.textContent = selectedNodeKey() || "-"
    if (selectedTagEl) selectedTagEl.textContent = mapping ? mapping.thing.name : "Unmapped"
    if (removeMappingButton) removeMappingButton.disabled = !mapping

    populateThingSelect()
    fillThingForm(mapping)
    renderMappingList()
    setMappingTab(mapping ? "create-edit" : state.mappingTab)
  }

  const renderPlacementInspector = () => {
    const selectedPlacement = state.selectedPlacement
    const position = selectedPlacementPosition()

    if (placementSelectionNameEl) {
      placementSelectionNameEl.textContent = selectedPlacement ? "Existing placement" : position ? "New placement point" : "No location selected"
    }
    if (placementSelectionPositionEl) placementSelectionPositionEl.textContent = formatPosition(position)
    if (placementSelectionThingEl) placementSelectionThingEl.textContent = selectedPlacement ? selectedPlacement.thing.name : "New placement"
    if (removePlacementButton) removePlacementButton.disabled = !selectedPlacement

    populatePlacementThingSelect()
    fillPlacementThingForm(selectedPlacement)
    syncPlacementPresetButtons()
    renderPlacementList()
    setPlacementTab(selectedPlacement ? "create-edit" : state.placementTab)
  }

  const renderNavigationPointList = () => {
    if (!navigationPointList) return

    const navigationPoints = orderedNavigationPoints()
    if (navigationPoints.length === 0) {
      navigationPointList.innerHTML = '<p class="plan-mapping-empty">No navigation points yet.</p>'
      return
    }

    navigationPointList.innerHTML = navigationPoints
      .map((navigationPoint) => {
        const activeClass = state.selectedNavigationPoint?.id === navigationPoint.id ? " is-active" : ""

        return `
          <div class="plan-mapping-list-item plan-mapping-list-item-with-actions${activeClass}">
            <button type="button" class="plan-mapping-list-card" data-navigation-point-id="${navigationPoint.id}">
              <span class="plan-mapping-list-title">${escapeHtml(navigationPointLabel(navigationPoint))}</span>
              <span class="plan-mapping-list-subtitle">${escapeHtml(formatPosition(navigationPoint.position))}</span>
            </button>
            <button
              type="button"
              class="plan-mapping-list-delete"
              data-delete-navigation-point-id="${navigationPoint.id}"
              aria-label="Delete ${escapeHtml(navigationPointLabel(navigationPoint))}"
              title="Delete navigation point">
              <i class="fa-solid fa-trash" aria-hidden="true"></i>
            </button>
          </div>
        `
      })
      .join("")
  }

  const renderNavigationPointInspector = () => {
    if (navigationPointSelectionNameEl) {
      navigationPointSelectionNameEl.textContent = state.selectedNavigationPoint ? navigationPointLabel(state.selectedNavigationPoint) : "No navigation point selected"
    }
    if (navigationPointSelectionPositionEl) {
      navigationPointSelectionPositionEl.textContent = formatPosition(state.selectedNavigationPoint?.position)
    }
    if (navigationPointCountEl) {
      navigationPointCountEl.textContent = String(state.navigationPointsById.size)
    }
    if (removeNavigationPointButton) {
      removeNavigationPointButton.disabled = !state.selectedNavigationPoint
    }

    renderNavigationPointList()
  }

  const renderCaptureAlignmentInspector = () => {
    if (!captureAlignmentSessionSelect) return

    const currentValue = state.activeCaptureSessionId
    const orderedSessions = sortedCaptureAlignmentSessions()
    captureAlignmentSessionSelect.dataset.select2Placeholder = "Select a capture session"
    captureAlignmentSessionSelect.innerHTML = '<option value="">Select a capture session</option>'
    orderedSessions.forEach((sessionPayload) => {
      const option = document.createElement("option")
      option.value = sessionPayload.session.id
      option.textContent = captureAlignmentOptionLabel(sessionPayload)
      captureAlignmentSessionSelect.appendChild(option)
    })
    if (currentValue && Array.from(captureAlignmentSessionSelect.options).some((option) => option.value === currentValue)) {
      captureAlignmentSessionSelect.value = currentValue
    }
    captureAlignmentSessionSelect.disabled = state.captureAlignmentSessions.length === 0
    refreshSelectWidget(captureAlignmentSessionSelect)

    const sessionPayload = activeCaptureAlignmentSession()
    const transform = captureTransformForSession(sessionPayload)
    Object.entries(captureAlignmentTransformInputs).forEach(([key, input]) => {
      if (!input) return
      input.value = transform[key].toFixed(2)
      input.disabled = !sessionPayload
    })

    if (captureAlignmentStatusEl) {
      captureAlignmentStatusEl.textContent = sessionPayload
        ? captureAlignmentSummary(sessionPayload)
        : state.captureAlignmentSessions.length > 0
          ? "Select a capture session to preview its route."
          : "No capture sessions with pose data are available."
    }
    if (captureAlignmentPublishButton) {
      captureAlignmentPublishButton.disabled = !sessionPayload || sessionPayload.captures.length === 0
    }
    if (captureAlignmentDeleteButton) {
      captureAlignmentDeleteButton.disabled = !sessionPayload || !sessionPayload.alignment?.published_at
    }

    if (captureAlignmentList) {
      const published = orderedSessions.filter((entry) => entry.alignment?.published_at)
      captureAlignmentList.innerHTML = published.length
        ? published.map((entry) => `
            <div class="plan-mapping-list-item plan-mapping-list-item-with-actions${entry.alignment?.visible_in_viewer ? " is-active" : ""}">
              <button type="button" class="plan-mapping-list-card" data-capture-session-select="${entry.session.id}">
                <span class="plan-mapping-list-title">${escapeHtml(entry.session.name || "Capture Session")}</span>
                <span class="plan-mapping-list-subtitle">${escapeHtml(captureAlignmentSummary(entry))}</span>
              </button>
              <button type="button" class="btn btn-ghost" data-capture-session-visible="${entry.session.id}" ${entry.alignment?.visible_in_viewer ? "disabled" : ""}>Show</button>
              <button type="button" class="plan-mapping-list-delete" data-capture-session-delete="${entry.session.id}" aria-label="Remove ${escapeHtml(entry.session.name || "Capture Session")} from plan" title="Remove from plan">
                <i class="fa-solid fa-trash" aria-hidden="true"></i>
              </button>
            </div>
          `).join("")
        : '<p class="plan-mapping-empty">No published capture sessions yet.</p>'
    }
  }

  const syncNodeMappingFormDefaults = async () => {
    const nodeKey = selectedNodeKey()
    if (state.nodeMappingFormNodeKey === nodeKey) return

    state.nodeMappingFormNodeKey = nodeKey
    const mappings = selectedNodeMappings()
    const firstMapping = mappings[0]

    if (!firstMapping) {
      if (nodeMappingIntegrationSelect) nodeMappingIntegrationSelect.value = ""
      populateObjectTypeSelect()
      populateExternalObjectSelect([])
      return
    }

    if (nodeMappingIntegrationSelect && !nodeMappingIntegrationSelect.value) {
      nodeMappingIntegrationSelect.value = firstMapping.integration_id
      populateObjectTypeSelect()
    }
    if (nodeMappingObjectTypeSelect && !nodeMappingObjectTypeSelect.value) {
      nodeMappingObjectTypeSelect.value = firstMapping.object_type
      await fetchNodeMappingCollection()
    }
    if (nodeMappingExternalObjectSelect && !nodeMappingExternalObjectSelect.value) {
      nodeMappingExternalObjectSelect.value = firstMapping.external_id
    }
  }

  const syncPlacementNodeMappingFormDefaults = async () => {
    const placementId = state.selectedPlacement?.id
    if (state.placementNodeMappingFormPlacementId === placementId) return

    state.placementNodeMappingFormPlacementId = placementId
    const mappings = selectedPlacementMappings()
    const firstMapping = mappings[0]

    if (!firstMapping) {
      if (placementNodeMappingIntegrationSelect) placementNodeMappingIntegrationSelect.value = ""
      populateObjectTypeSelect(placementNodeMappingIntegrationSelect, placementNodeMappingObjectTypeSelect)
      populateExternalObjectSelect([], placementNodeMappingExternalObjectSelect)
      return
    }

    if (placementNodeMappingIntegrationSelect) {
      placementNodeMappingIntegrationSelect.value = firstMapping.integration_id
      populateObjectTypeSelect(placementNodeMappingIntegrationSelect, placementNodeMappingObjectTypeSelect)
    }
    if (placementNodeMappingObjectTypeSelect) {
      placementNodeMappingObjectTypeSelect.value = firstMapping.object_type
      await fetchNodeMappingCollection({
        integrationSelect: placementNodeMappingIntegrationSelect,
        objectTypeSelect: placementNodeMappingObjectTypeSelect,
        externalObjectSelect: placementNodeMappingExternalObjectSelect
      })
    }
    if (placementNodeMappingExternalObjectSelect) {
      placementNodeMappingExternalObjectSelect.value = firstMapping.external_id
    }
  }

  const renderIntegrationMappingInspector = () => {
    populateIntegrationSelect(nodeMappingIntegrationSelect)
    populateObjectTypeSelect(nodeMappingIntegrationSelect, nodeMappingObjectTypeSelect)
    renderNodeMappingsList()
    populateIntegrationSelect(placementNodeMappingIntegrationSelect)
    populateObjectTypeSelect(placementNodeMappingIntegrationSelect, placementNodeMappingObjectTypeSelect)
    renderPlacementNodeMappingsList()
  }

  const renderInspector = () => {
    const panelOpen = Boolean(state.sidePanelMode)

    shell?.classList.toggle("is-mapping-open", panelOpen)
    shell?.classList.toggle("is-panel-open", panelOpen)
    shell?.classList.toggle("is-guided-editor", state.sidePanelMode === "mapping" && state.editorMode === "guided")
    shell?.classList.toggle("is-advanced-editor", state.sidePanelMode === "mapping" && state.editorMode === "advanced")
    panel?.setAttribute("aria-hidden", panelOpen ? "false" : "true")
    setActiveButton()
    renderCablingPlanEditor()
    setPanelMode(state.panelMode)
    syncEditorMode()
    if (saveModelButton) saveModelButton.disabled = state.savingModel || !state.modelDirty

    renderTagInspector()
    renderIntegrationMappingInspector()
    renderPlacementInspector()
    renderNavigationPointInspector()
    renderCaptureAlignmentInspector()
    rebuildCaptureAlignmentObjects()
    renderPopup()
    renderSearchResults()
    updateHotspots()
  }

  const applyMappingMetadataToNode = (object, mapping) => {
    if (!object) return

    object.userData.dtNodeKey = mapping ? mapping.model_node_key : object.userData.dtNodeKey

    if (mapping) {
      object.userData.dtThingId = mapping.thing.id
      object.userData.dtThingName = mapping.thing.name
      object.userData.dtThingKind = mapping.thing.kind || null
    } else {
      delete object.userData.dtThingId
      delete object.userData.dtThingName
      delete object.userData.dtThingKind
    }
  }

  const syncMappings = (mappings) => {
    state.mappingsByNodeKey = new Map()

    state.nodesByKey.forEach((object) => applyMappingMetadataToNode(object, null))
    mappings.forEach((mapping) => {
      state.mappingsByNodeKey.set(mapping.model_node_key, mapping)
      applyMappingMetadataToNode(state.nodesByKey.get(mapping.model_node_key), mapping)
    })

    rebuildSearchEntries()
    renderInspector()
  }

  const mergeMapping = (mapping) => {
    state.mappingsByNodeKey.set(mapping.model_node_key, mapping)
    applyMappingMetadataToNode(state.nodesByKey.get(mapping.model_node_key), mapping)
    rebuildSearchEntries()
    renderInspector()
  }

  const syncNodeMappings = (nodeMappings, integrations = state.integrations) => {
    state.integrations = integrations || []
    state.nodeMappingsById = new Map()
    state.nodeMappingsByNodeKey = new Map()
    state.nodeMappingsByPlacementId = new Map()

    nodeMappings.forEach((mapping) => {
      state.nodeMappingsById.set(mapping.id, mapping)
      if (mapping.thing_placement_id) {
        const mappings = state.nodeMappingsByPlacementId.get(mapping.thing_placement_id) || []
        mappings.push(mapping)
        state.nodeMappingsByPlacementId.set(mapping.thing_placement_id, mappings)
      } else {
        const mappings = state.nodeMappingsByNodeKey.get(mapping.model_node_key) || []
        mappings.push(mapping)
        state.nodeMappingsByNodeKey.set(mapping.model_node_key, mappings)
      }
    })

    renderInspector()
  }

  const mergeNodeMapping = (mapping, integrations = state.integrations) => {
    state.integrations = integrations || state.integrations
    state.nodeMappingsById.set(mapping.id, mapping)
    if (mapping.thing_placement_id) {
      const mappings = Array.from(state.nodeMappingsByPlacementId.get(mapping.thing_placement_id) || []).filter((entry) => entry.id !== mapping.id)
      mappings.push(mapping)
      state.nodeMappingsByPlacementId.set(mapping.thing_placement_id, mappings)
    } else {
      const mappings = Array.from(state.nodeMappingsByNodeKey.get(mapping.model_node_key) || []).filter((entry) => entry.id !== mapping.id)
      mappings.push(mapping)
      state.nodeMappingsByNodeKey.set(mapping.model_node_key, mappings)
    }
    renderInspector()
  }

  const clearPlacementObjects = () => {
    state.placementObjectsById.forEach((object) => disposeHelperObject(object))
    state.placementObjectsById.clear()
  }

  const clearNavigationPointObjects = () => {
    state.hoveredNavigationPointObject = null
    state.navigationPointObjectsById.forEach((object) => disposeHelperObject(object))
    state.navigationPointObjectsById.clear()
  }

  const syncPlacementVisibility = () => {
    state.placementObjectsById.forEach((object, placementId) => {
      if (state.photo360 || state.photo360Loading) {
        object.visible = false
        return
      }

      const placement = state.placementsById.get(placementId)
      const selected = state.selectedPlacement?.id === placementId
      const generatedCapturePlacement = placement?.metadata?.generated_by === "capture_session_alignment"
      object.visible = Boolean(placement && (selected || generatedCapturePlacement || labelVisibleForPlacement(placement)))
    })
  }

  const rebuildPlacementObjects = () => {
    clearPlacementObjects()
    const placementUnitSize = placementVisualUnitSize(state.framing)

    state.placementsById.forEach((placement) => {
      const object = buildPlacementVisual(
        placement,
        placementUnitSize,
        state.viewerSettings.objectIconScale,
        state.viewerSettings.modelOffsetY
      )
      state.placementObjectsById.set(placement.id, object)
      scene.add(object)
    })

    syncPlacementVisibility()
  }

  const syncNavigationPointVisibility = () => {
    const visible = !state.photo360 && !state.photo360Loading && state.markersVisible && ["3d", "2d", "walkthrough"].includes(state.viewMode)
    state.navigationPointObjectsById.forEach((object) => {
      object.visible = visible
    })
  }

  const rebuildNavigationPointObjects = () => {
    clearNavigationPointObjects()
    const unitSize = Math.max((state.framing?.sphere?.radius || 1) * 0.028, 0.48)

    state.navigationPointsById.forEach((navigationPoint) => {
      const object = buildNavigationPointVisual(
        navigationPoint,
        unitSize,
        state.viewerSettings.modelOffsetY
      )
      state.navigationPointObjectsById.set(navigationPoint.id, object)
      scene.add(object)
    })

    syncNavigationPointVisibility()
  }

  const syncPlacements = (placements) => {
    state.placementsById = new Map(placements.map((placement) => {
      const normalizedPlacement = withResolvedPlacementPosition(placement)
      return [normalizedPlacement.id, normalizedPlacement]
    }))
    rebuildPlacementObjects()

    if (state.selectedPlacement && state.placementsById.has(state.selectedPlacement.id)) {
      state.selectedPlacement = state.placementsById.get(state.selectedPlacement.id)
      state.selectedPlacementObject = state.placementObjectsById.get(state.selectedPlacement.id) || null
    } else {
      state.selectedPlacement = null
      state.selectedPlacementObject = null
    }

    rebuildSearchEntries()
    renderLabelsMenu()
    renderInspector()
    refreshSelectionHelper()
  }

  const mergePlacement = (placement) => {
    const normalizedPlacement = withResolvedPlacementPosition(placement)
    state.placementsById.set(placement.id, normalizedPlacement)
    rebuildPlacementObjects()
    state.selectedPlacement = normalizedPlacement
    state.selectedPlacementObject = state.placementObjectsById.get(placement.id) || null
    state.selectedPlacementPoint = null
    rebuildSearchEntries()
    renderLabelsMenu()
    renderInspector()
    refreshSelectionHelper()
  }

  const syncNavigationPoints = (navigationPoints) => {
    state.navigationPointsById = new Map(
      navigationPoints.map((navigationPoint) => {
        const normalizedNavigationPoint = normalizeNavigationPoint(navigationPoint)
        return [normalizedNavigationPoint.id, normalizedNavigationPoint]
      })
    )
    rebuildNavigationPointObjects()

    if (state.selectedNavigationPoint && state.navigationPointsById.has(state.selectedNavigationPoint.id)) {
      state.selectedNavigationPoint = state.navigationPointsById.get(state.selectedNavigationPoint.id)
      state.selectedNavigationPointObject = state.navigationPointObjectsById.get(state.selectedNavigationPoint.id) || null
    } else {
      state.selectedNavigationPoint = null
      state.selectedNavigationPointObject = null
    }

    renderInspector()
    refreshSelectionHelper()
  }

  const markModelDirty = () => {
    state.modelDirty = true
    renderInspector()
  }

  const applyOrbitBounds = ({ mode = state.viewMode, update = true } = {}) => {
    const previousDamping = controls.enableDamping
    controls.enableDamping = false
    applyControlTuning(mode)

    if (is2DLikeMode(mode)) {
      controls.enableRotate = false
      controls.minPolarAngle = 0
      controls.maxPolarAngle = 0
      if (update) controls.update()
      controls.enableDamping = previousDamping
      return
    }

    controls.enableRotate = true

    if (mode === "walkthrough") {
      controls.minPolarAngle = state.orbitLock ? Math.PI * 0.3 : 0.12
      controls.maxPolarAngle = state.orbitLock ? Math.PI * 0.58 : Math.PI - 0.12
      if (update) controls.update()
      controls.enableDamping = previousDamping
      return
    }

    controls.minPolarAngle = 0
    controls.maxPolarAngle = state.orbitLock ? Math.PI / 2 : Math.PI - 0.02
    if (update) controls.update()
    controls.enableDamping = previousDamping
  }

  const refreshModelFraming = () => {
    state.framing = state.model ? frameObject(perspectiveCamera, state.model) : defaultSceneFraming(perspectiveCamera)
    return state.framing
  }

  const syncCablingPlanOverlayPresentation = () => {
    state.referenceLayerOverlaysById.forEach((entry, referenceLayerId) => {
      const referenceLayer = findReferenceLayerById(state.referenceLayers, referenceLayerId)
      const renderOrder = referenceLayer?.position ?? 0
      entry.overlay.visible = !state.photo360 && state.visibleReferenceLayerIds.has(referenceLayerId)
      entry.overlay.renderOrder = renderOrder
      if (entry.mesh) entry.mesh.renderOrder = renderOrder
    })
  }

  const applyReferenceLayerSettingsToOverlay = (referenceLayer, overlayEntry, settings) => {
    if (!referenceLayer || !overlayEntry || !state.framing) return

    const { box, center } = state.framing
    const layerStackOffset = (referenceLayer.position || 0) * 0.002
    overlayEntry.overlay.position.set(
      center.x + settings.offsetX,
      box.min.y + settings.elevation + layerStackOffset,
      center.z + settings.offsetZ
    )
    overlayEntry.overlay.rotation.y = THREE.MathUtils.degToRad(settings.rotationDegrees)
    overlayEntry.overlay.scale.setScalar(settings.scale)

    if (overlayEntry.mesh.material) {
      overlayEntry.mesh.material.opacity = settings.opacity
      overlayEntry.mesh.material.needsUpdate = true
    }
  }

  const applyCablingPlanSettingsToOverlay = () => {
    const referenceLayer = activeReferenceLayer()
    const overlayEntry = referenceLayerOverlayEntry()
    if (!referenceLayer || !overlayEntry) return

    applyReferenceLayerSettingsToOverlay(referenceLayer, overlayEntry, state.cablingPlanSettings)
  }

  const replaceCablingPlanTexture = (overlayEntry, texture) => {
    if (!overlayEntry?.mesh?.material) return

    const previousTexture = overlayEntry.texture
    overlayEntry.mesh.material.map = texture
    overlayEntry.mesh.material.needsUpdate = true
    overlayEntry.texture = texture
    previousTexture?.dispose()
  }

  const refreshCablingPlanTexture = async (referenceLayerId = state.activeReferenceLayerId) => {
    const referenceLayer = findReferenceLayerById(state.referenceLayers, referenceLayerId)
    const overlayEntry = referenceLayerOverlayEntry(referenceLayerId)
    if (!referenceLayer || !overlayEntry?.sourceImage) return

    overlayEntry.refreshVersion = (overlayEntry.refreshVersion || 0) + 1
    const refreshVersion = overlayEntry.refreshVersion
    const settings = referenceLayerId === state.activeReferenceLayerId
      ? state.cablingPlanSettings
      : cloneCablingPlanSettings(referenceLayer.settings || {})
    const texture = buildTransparentCablingPlanTextureFromImage(overlayEntry.sourceImage, settings)

    if (refreshVersion !== overlayEntry.refreshVersion) {
      texture.dispose()
      return
    }

    replaceCablingPlanTexture(overlayEntry, texture)
  }

  const scheduleCablingPlanTextureRefresh = (referenceLayerId = state.activeReferenceLayerId) => {
    const overlayEntry = referenceLayerOverlayEntry(referenceLayerId)
    if (!overlayEntry) return

    if (overlayEntry.refreshTimer) {
      window.clearTimeout(overlayEntry.refreshTimer)
    }

    overlayEntry.refreshTimer = window.setTimeout(() => {
      overlayEntry.refreshTimer = null
      void refreshCablingPlanTexture(referenceLayerId).catch(() => {
        setFeedback("Could not update the reference layer texture preview.", "error")
      })
    }, 120)
  }

  const disposeCablingPlanOverlay = (referenceLayerId = state.activeReferenceLayerId) => {
    const overlayEntry = referenceLayerOverlayEntry(referenceLayerId)
    if (!overlayEntry) return

    scene.remove(overlayEntry.overlay)
    overlayEntry.mesh?.geometry?.dispose()
    overlayEntry.mesh?.material?.dispose()
    overlayEntry.texture?.dispose()
    if (overlayEntry.refreshTimer) {
      window.clearTimeout(overlayEntry.refreshTimer)
      overlayEntry.refreshTimer = null
    }
    state.referenceLayerOverlaysById.delete(referenceLayerId)
  }

  const createCablingPlanOverlay = async (referenceLayerId = state.activeReferenceLayerId) => {
    if (referenceLayerOverlayEntry(referenceLayerId) || !state.framing) return

    const referenceLayer = findReferenceLayerById(state.referenceLayers, referenceLayerId)
    const imageUrl = referenceLayer?.imageUrl
    if (!imageUrl || !referenceLayer?.processed) return

    const settings = referenceLayerId === state.activeReferenceLayerId
      ? state.cablingPlanSettings
      : cloneCablingPlanSettings(referenceLayer.settings || {})
    const { texture, image } = await buildTransparentCablingPlanTexture(imageUrl, settings)

    const { box, size, center, sphere } = state.framing
    const fittedSize = fitRectWithinRect(
      texture.image?.width || texture.source.data.width,
      texture.image?.height || texture.source.data.height,
      Math.max(size.x, sphere.radius * 0.5, 1),
      Math.max(size.z, sphere.radius * 0.5, 1)
    )

    const overlayMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(fittedSize.width, fittedSize.height),
      new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: settings.opacity,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1
      })
    )

    overlayMesh.rotation.x = -Math.PI / 2

    const overlay = new THREE.Group()
    overlay.name = `reference-layer-overlay-${referenceLayer.id}`
    overlay.userData.dtOverlay = "reference-layer"
    overlay.userData.dtReferenceLayerId = referenceLayer.id
    overlay.add(overlayMesh)

    scene.add(overlay)
    state.referenceLayerOverlaysById.set(referenceLayer.id, {
      overlay,
      mesh: overlayMesh,
      texture,
      sourceImage: image,
      refreshTimer: null,
      refreshVersion: 0
    })
    applyReferenceLayerSettingsToOverlay(referenceLayer, referenceLayerOverlayEntry(referenceLayer.id), settings)
    syncCablingPlanOverlayPresentation()
  }

  const ensureCablingPlanOverlay = async (referenceLayerId = state.activeReferenceLayerId) => {
    if (!referenceLayerId || !state.visibleReferenceLayerIds.has(referenceLayerId) || referenceLayerOverlayEntry(referenceLayerId)) return

    try {
      await createCablingPlanOverlay(referenceLayerId)
    } catch (_error) {
      state.visibleReferenceLayerIds.delete(referenceLayerId)
      syncActiveReferenceLayerVisibilityState()
      setActiveButton()
      setFeedback("Could not load the selected reference layer image.", "error")
    }
  }

  const ensureVisibleReferenceLayerOverlays = async () => {
    for (const referenceLayerId of Array.from(state.visibleReferenceLayerIds)) {
      await ensureCablingPlanOverlay(referenceLayerId)
    }
  }

  const applyModelOffsetToScene = () => {
    if (!state.model) return

    if (state.viewerSettings.snapToGround) {
      snapModelToGround(state.model, {
        baseY: state.modelBasePositionY + state.viewerSettings.modelOffsetY,
        groundOffsetY: state.modelGroundSnapOffsetY
      })
    } else {
      state.model.position.y = state.modelBasePositionY + state.viewerSettings.modelOffsetY
      state.model.updateMatrixWorld(true)
    }
  }

  const apiRequest = async (url, options = {}) => {
    const headers = new Headers(options.headers || {})
    headers.set("Accept", "application/json")

    if (!options.body || !(options.body instanceof FormData)) {
      headers.set("Content-Type", "application/json")
    }

    const token = csrfToken()
    if (token) headers.set("X-CSRF-Token", token)

    const response = await fetch(url, { credentials: "same-origin", ...options, headers })
    if (response.ok) return response.status === 204 ? {} : response.json()

    const errorPayload = await response.json().catch(() => ({}))
    const message = errorPayload.error || errorPayload.message || "Request failed"
    throw new Error(message)
  }

  const captureAlignmentRequestBody = () => ({
    capture_session_alignment: {
      transform: captureTransformForSession(),
      status: "aligned"
    }
  })

  saveCaptureAlignment = async () => {
    const sessionPayload = activeCaptureAlignmentSession()
    const url = captureAlignmentEndpointFor(sessionPayload, "alignment_publish_url")
    if (!sessionPayload || !url) {
      setFeedback("Choose a capture session to align.", "error")
      return
    }

    try {
      const payload = await apiRequest(url, {
        method: "POST",
        body: JSON.stringify(captureAlignmentRequestBody())
      })
      syncCaptureAlignmentPayload(payload)
      syncPlacements(payload.placements || [])
      syncNodeMappings(payload.node_mappings || [], payload.integrations || state.integrations)
      setFeedback("Capture session alignment saved.", "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  publishCaptureAlignment = async () => {
    const sessionPayload = activeCaptureAlignmentSession()
    const url = captureAlignmentEndpointFor(sessionPayload, "alignment_publish_url")
    if (!sessionPayload || !url) {
      setFeedback("Choose a capture session to publish.", "error")
      return
    }

    try {
      const payload = await apiRequest(url, {
        method: "POST",
        body: JSON.stringify(captureAlignmentRequestBody())
      })
      syncCaptureAlignmentPayload(payload)
      syncPlacements(payload.placements || [])
      syncNodeMappings(payload.node_mappings || [], payload.integrations || state.integrations)
      setFeedback("Published linked 360 photo objects.", "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  removeCaptureAlignmentPublishedObjects = async (sessionId = state.activeCaptureSessionId) => {
    const sessionPayload = state.captureAlignmentSessions.find((entry) => entry.session.id?.toString() === sessionId?.toString())
    const url = captureAlignmentEndpointFor(sessionPayload, "alignment_published_objects_url")
    if (!sessionPayload || !url || !sessionPayload.alignment?.published_at) {
      setFeedback("Choose a published capture session to remove from the plan.", "error")
      return
    }

    const sessionName = sessionPayload.session?.name || "this capture session"
    const confirmed = window.confirm(`Remove published 360 photo objects for ${sessionName} from this plan? The uploaded capture session will be kept.`)
    if (!confirmed) return

    try {
      const payload = await apiRequest(url, {
        method: "DELETE",
        body: JSON.stringify({})
      })
      syncCaptureAlignmentPayload(payload)
      syncPlacements(payload.placements || [])
      syncNodeMappings(payload.node_mappings || [], payload.integrations || state.integrations)
      setActiveCaptureAlignmentSession(sessionPayload.session.id)
      setFeedback("Removed published 360 photo objects from the plan.", "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  switchVisibleCaptureSession = async (sessionId) => {
    const sessionPayload = state.captureAlignmentSessions.find((entry) => entry.session.id?.toString() === sessionId?.toString())
    const url = captureAlignmentEndpointFor(sessionPayload, "alignment_visible_url")
    if (!sessionPayload || !url) return

    try {
      const payload = await apiRequest(url, {
        method: "POST",
        body: JSON.stringify({})
      })
      syncCaptureAlignmentPayload(payload)
      syncPlacements(payload.placements || [])
      syncNodeMappings(payload.node_mappings || [], payload.integrations || state.integrations)
      setActiveCaptureAlignmentSession(sessionId)
      setFeedback("Visible capture session updated.", "success")
    } catch (error) {
      setFeedback(error.message, "error")
    }
  }

  ;({
    setLabelsMenuOpen,
    setReferenceLayerMenuOpen,
    setExportMenuOpen,
    setActiveButton,
    prepareForViewChange,
    configureOrthographicCamera,
    applyDefault3DCamera,
    apply3DView,
    apply2DView,
    applyOrthographic2DView,
    applyWalkthroughView,
    applyDefaultView,
    applyQueuedViewMode,
    queueViewMode,
    resetView,
    fitCurrentView,
    toggleAutoRotate,
    toggleFullscreen,
    exportCurrentView
  } = createViewerModesController({
    state,
    element,
    stage,
    toolbar,
    exportPanel,
    referenceLayerPanel,
    labelsPanel,
    renderer,
    scene,
    camera,
    perspectiveCamera,
    orthographicCamera,
    controls,
    setActiveCamera,
    applyOrbitBounds,
    refreshModelFraming,
    defaultViewAnchor,
    default2DFocusPoint,
    walkthroughFocusPoint,
    walkthroughPoseFromSettings,
    deriveWalkthroughHelperSettings,
    cloneViewerSettings,
    syncCablingPlanOverlayPresentation,
    syncNavigationPointVisibility,
    syncCablingPlanForm,
    notifyEmbedStateChanged: () => notifyEmbedStateChanged(),
    setFeedback,
    slugify,
    parseFloatOr,
    clampNumber,
    is2DLikeMode
  }))

  ;({
    toggleCablingPlanOverlay,
    toggleReferenceLayerById,
    applyViewerSettingsToScene,
    applyViewerSettingChange,
    sync3DViewerSettingsToCurrentView,
    sync2DViewerSettingsToCurrentView,
    syncWalkthroughSettingsToCurrentView,
    loadReferenceLayerDraft,
    upsertReferenceLayer,
    applyReferenceLayerSelection,
    applyCablingPlanSettingChange,
    resetViewerSettings,
    saveViewerSettings,
    createReferenceLayer,
    replaceReferenceLayerPdf,
    deleteReferenceLayer,
    toggleViewerSettingsPanel
  } = createViewerSettingsController({
    state,
    element,
    renderer,
    camera,
    controls,
    grid,
    referenceLayerCreateForm,
    referenceLayerCreateNameInput,
    referenceLayerCreateFileInput,
    findReferenceLayerById,
    initialReferenceLayerId,
    cloneCablingPlanSettings,
    cloneViewerSettings,
    viewerSettingsFromPayload,
    activeReferenceLayer,
    anyViewerSettingsDirty,
    syncActiveReferenceLayerVisibilityState,
    renderReferenceLayerMenu,
    renderReferenceLayerList,
    setActiveButton,
    renderInspector,
    syncReferenceLayersDataset,
    syncViewerDataset,
    applyViewerLabelsVisibilityPreference,
    applyModelOffsetToScene,
    refreshModelFraming,
    applyReferenceLayerSettingsToOverlay,
    rebuildPlacementObjects,
    rebuildNavigationPointObjects,
    rebuildCaptureAlignmentObjects,
    refreshSelectionHelper,
    refreshHoverHelper,
    apply2DView,
    applyOrthographic2DView,
    applyWalkthroughView,
    apply3DView,
    defaultViewVerticalOffsetDistance,
    deriveWalkthroughHelperSettings,
    walkthroughFocusPoint,
    syncActiveReferenceLayerDraftToCollection,
    markReferenceLayerSaved,
    savedReferenceLayerDraft,
    referenceLayerDirtyFor,
    ensureCablingPlanOverlay,
    ensureVisibleReferenceLayerOverlays,
    syncCablingPlanOverlayPresentation,
    disposeCablingPlanOverlay,
    scheduleCablingPlanTextureRefresh,
    applyCablingPlanSettingsToOverlay,
    closePopup: (...args) => closePopup(...args),
    clearHoveredObject: (...args) => clearHoveredObject(...args),
    clearSelectedObject: (...args) => clearSelectedObject(...args),
    expandViewerSettingsForCurrentView,
    setFeedback,
    apiRequest,
    syncCablingPlanForm,
    notifyEmbedStateChanged,
    syncNavigationPointVisibility,
    parseBooleanOr,
    is2DLikeMode,
    isOrthographic2DMode,
    isPerspective2DMode,
    clampNumber,
    parseFloatOr
  }))

  const persistModelFile = async () => {
    if (!state.model || state.savingModel) return

    state.savingModel = true
    renderInspector()
    setFeedback("Saving updated GLB...", "working")

    try {
      const arrayBuffer = await exportGlb(state.model)
      const file = new File([arrayBuffer], `${slugify(element.dataset.planName)}.glb`, {
        type: "model/gltf-binary"
      })
      const formData = new FormData()
      formData.append("model_file", file)

      await apiRequest(element.dataset.modelFileUrl, {
        method: "PATCH",
        body: formData
      })

      state.modelDirty = false
      setFeedback("GLB saved.", "success")
    } catch (error) {
      setFeedback(`Could not save GLB: ${error.message}`, "error")
    } finally {
      state.savingModel = false
      renderInspector()
    }
  }

  const refreshMappingsFromServer = async () => {
    const payload = await apiRequest(element.dataset.mappingsUrl)
    state.things = payload.things || []
    syncMappings(payload.mappings || [])
  }

  const refreshNodeMappingsFromServer = async () => {
    const payload = await apiRequest(element.dataset.nodeMappingsUrl)
    syncNodeMappings(payload.node_mappings || [], payload.integrations || [])
  }

  const refreshPlacementsFromServer = async () => {
    const payload = await apiRequest(element.dataset.placementsUrl)
    syncPlacements(payload.placements || [])
  }

  const refreshNavigationPointsFromServer = async () => {
    const payload = await apiRequest(element.dataset.navigationPointsUrl)
    syncNavigationPoints(payload.navigation_points || [])
  }

  const syncCaptureAlignmentPayload = (payload = {}) => {
    state.captureAlignmentSessions = Array.from(payload.capture_session_alignments || [])
      .map((entry) => normalizeCaptureAlignmentSession(entry))
      .filter((entry) => entry.captures.length > 0)
    syncCaptureAlignmentDataset()

    const visibleSession = state.captureAlignmentSessions.find((entry) => entry.alignment?.visible_in_viewer)
    const firstDraft = state.captureAlignmentSessions.find((entry) => entry.alignment)
    const firstSession = visibleSession || firstDraft || state.captureAlignmentSessions[0] || null
    if (!state.activeCaptureSessionId && firstSession) {
      state.activeCaptureSessionId = firstSession.session.id?.toString()
      state.captureAlignmentDraft = normalizeCaptureTransform(firstSession.alignment?.transform || {})
    }
    if (state.activeCaptureSessionId && !activeCaptureAlignmentSession()) {
      state.activeCaptureSessionId = firstSession?.session.id?.toString() || null
      state.captureAlignmentDraft = firstSession ? normalizeCaptureTransform(firstSession.alignment?.transform || {}) : null
    }
    renderCaptureAlignmentInspector()
    rebuildCaptureAlignmentObjects()
  }

  const refreshCaptureSessionAlignmentsFromServer = async () => {
    if (!element.dataset.captureSessionAlignmentsUrl) return

    const payload = await apiRequest(element.dataset.captureSessionAlignmentsUrl)
    syncCaptureAlignmentPayload(payload)
  }

  let clearNodeSelectionState = () => {}
  let clearPlacementSelectionState = () => {}
  let clearNavigationPointSelectionState = () => {}
  let selectNodeObject = () => {}
  let selectPlacement = () => {}
  let selectPlacementPoint = () => {}
  let selectNavigationPoint = () => {}
  let setHoveredObject = () => {}
  let clearHoveredObject = () => {}
  let clearSelectedObject = () => {}

  ;({
    clearNodeSelectionState,
    clearPlacementSelectionState,
    clearNavigationPointSelectionState,
    selectNodeObject,
    selectPlacement,
    selectPlacementPoint,
    selectNavigationPoint,
    setHoveredObject,
    clearHoveredObject,
    clearSelectedObject
  } = createViewerSelectionController({
    state,
    refreshSelectionHelper,
    refreshHoverHelper,
    renderInspector,
    syncNodeMappingFormDefaults,
    syncPlacementNodeMappingFormDefaults,
    notifyEmbedSelectionChanged: () => notifyEmbedSelectionChanged(),
    clearNavigationPointHoverState,
    clearHoverHelper,
    clearHoverMarker,
    clearSelectionHelper,
    clearSelectionMarker
  }))

  ;({
    renderCablingPlanEditor,
    setMappingTab,
    setPlacementTab,
    setPanelMode,
    guidedMaxStep,
    guidedCanAdvance,
    syncEditorMode,
    closeMappingPanel
  } = createViewerMappingPanelController({
    state,
    mappingPanel,
    cablingPlanPanel,
    viewerSettingsAccordionButtons,
    viewerSettingsAccordionPanels,
    tabButtons,
    tabPanels,
    placementTabButtons,
    placementTabPanels,
    panelModeButtons,
    advancedPanelModeButtons,
    panelModePanels,
    guidedStepLabels,
    guidedEntry,
    guidedNav,
    guidedSharedStepLabel,
    guidedHeaderEl,
    advancedPanelModeSwitcher,
    editorModeLink,
    editorModeHintEl,
    guidedTitleEl,
    tagSelectionSection,
    tagThingSection,
    tagIntegrationSection,
    tagListSection,
    placeSelectionSection,
    placeThingSection,
    placeIntegrationSection,
    placeListSection,
    navigateSelectionSection,
    navigateListSection,
    advancedTagAccordionButtons,
    advancedTagAccordionPanels,
    advancedPlaceAccordionButtons,
    advancedPlaceAccordionPanels,
    advancedNavigateAccordionButtons,
    advancedNavigateAccordionPanels,
    advancedCaptureAccordionButtons,
    advancedCaptureAccordionPanels,
    guidedPrevButton,
    guidedStepNextButton,
    guidedFinishButton,
    renderReferenceLayerMenu,
    renderReferenceLayerList,
    syncCablingPlanForm,
    selectedPlacementPosition,
    selectedNodeKey,
    selectedMapping,
    clearHoveredObject,
    clearSelectedObject,
    renderInspector,
    setFeedback,
    renderer
  }))

  ;({
    embedBridge,
    embedSnapshot,
    notifyEmbedStateChanged,
    notifyEmbedSelectionChanged
  } = createViewerEmbedController({
    state,
    viewerConfig,
    element,
    selectedObjectLabel,
    selectedNodeKey,
    navigationPointLabel,
    focusSearchEntry,
    setPanelMode,
    selectPlacement,
    focusOnPlacement,
    selectNodeObject,
    focusOnNodeObject,
    selectNavigationPoint,
    focusOnNavigationPoint,
    closePopup,
    renderPopup,
    queueViewMode,
    fitCurrentView,
    resetView
  }))

  const updatePointerRay = (event) => {
    const rect = renderer.domElement.getBoundingClientRect()
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
    raycaster.setFromCamera(pointer, camera)
  }

  const pickIntersection = (event, { includePlacements = true, includeNavigationPoints = false, includeModel = true, allowSprites = false } = {}) => {
    updatePointerRay(event)

    const targets = [
      ...(includeNavigationPoints ? Array.from(state.navigationPointObjectsById.values()) : []),
      ...(includePlacements ? Array.from(state.placementObjectsById.values()) : []),
      ...(includeModel && state.model ? state.model.children : [])
    ]

    return targets.length
      ? raycaster.intersectObjects(targets, true).find((intersection) =>
          intersection.object.isMesh || (allowSprites && intersection.object.isSprite)
        ) || null
      : null
  }

  const pickFallbackSurfaceIntersection = (event) => {
    if (!state.framing) return null

    updatePointerRay(event)

    const point = new THREE.Vector3()
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -state.framing.box.min.y)
    const hitPoint = raycaster.ray.intersectPlane(plane, point)
    if (!hitPoint) return null

    return {
      point: hitPoint.clone(),
      object: scene,
      face: {
        normal: new THREE.Vector3(0, 1, 0)
      }
    }
  }

  const resolveCaptureAlignmentControl = (object) => {
    let current = object
    while (current) {
      if (current.userData?.dtCaptureAlignmentControl) return current
      current = current.parent
    }
    return null
  }

  const pickCaptureAlignmentControl = (event) => {
    updatePointerRay(event)
    const targets = state.captureAlignmentObjects.filter((object) =>
      object.userData?.dtCaptureAlignmentControl
    )
    if (!targets.length) return null

    return raycaster.intersectObjects(targets, true)
      .map((intersection) => ({
        ...intersection,
        control: resolveCaptureAlignmentControl(intersection.object)
      }))
      .find((intersection) => intersection.control) || null
  }

  const pickPlacementSurfaceIntersection = (event) => {
    const hit = pickIntersection(event, {
      includePlacements: true,
      includeNavigationPoints: false,
      includeModel: true
    })

    if (hit) return hit
    if (!state.model) return pickFallbackSurfaceIntersection(event)
    return null
  }

  const pickNavigationSurfaceIntersection = (event) => {
    const hit = pickIntersection(event, {
      includePlacements: false,
      includeNavigationPoints: false,
      includeModel: true
    })

    if (hit) return hit
    if (!state.model) return pickFallbackSurfaceIntersection(event)
    return null
  }

  const handlePointerDown = (event) => {
    cancelCameraAnimation()
    state.pointerDown = { x: event.clientX, y: event.clientY }
    state.pointerDragged = false
    if (state.mappingMode && state.panelMode === "capture" && activeCaptureAlignmentSession()) {
      const transform = captureTransformForSession()
      const controlHit = pickCaptureAlignmentControl(event)
      const surfaceHit = pickFallbackSurfaceIntersection(event)
      const frame = captureAlignmentControlFrame(activeCaptureAlignmentSession(), transform)

      if (controlHit?.control && surfaceHit && frame) {
        const mode = controlHit.control.userData.dtCaptureAlignmentControl
        state.captureAlignmentDragging = {
          mode,
          sourceCenter: frame.sourceCenter,
          planCenter: frame.planCenter,
          offsetX: transform.offset_x,
          offsetZ: transform.offset_z,
          initialScale: transform.scale,
          initialRotation: transform.rotation_degrees,
          initialPointerAngle: captureAlignmentAngleAtPoint(surfaceHit.point, frame.planCenter),
          initialDistance: Math.max(
            Math.hypot(surfaceHit.point.x - frame.planCenter.x, surfaceHit.point.z - frame.planCenter.z),
            0.001
          ),
          start: surfaceHit.point.clone()
        }
        controls.enabled = false
      } else if (surfaceHit) {
        state.captureAlignmentDragging = {
          mode: "translate",
          start: surfaceHit.point.clone(),
          offsetX: transform.offset_x,
          offsetZ: transform.offset_z
        }
        controls.enabled = false
      }
    }
  }

  const handlePointerMove = (event) => {
    if (state.photo360) {
      renderer.domElement.style.cursor = "grab"
      return
    }

    if (state.pointerDown) {
      const deltaX = event.clientX - state.pointerDown.x
      const deltaY = event.clientY - state.pointerDown.y
      if (Math.hypot(deltaX, deltaY) > 6) state.pointerDragged = true
    }

    if (state.captureAlignmentDragging) {
      const hit = pickFallbackSurfaceIntersection(event)
      if (hit) {
        const transform = captureTransformForSession()
        if (state.captureAlignmentDragging.mode === "rotate") {
          const rotationDegrees = state.captureAlignmentDragging.initialRotation + THREE.MathUtils.radToDeg(
            captureAlignmentDeltaAngle(
              captureAlignmentAngleAtPoint(hit.point, state.captureAlignmentDragging.planCenter),
              state.captureAlignmentDragging.initialPointerAngle
            )
          )
          const offsets = captureAlignmentOffsetsForCenter({
            sourceCenter: state.captureAlignmentDragging.sourceCenter,
            planCenter: state.captureAlignmentDragging.planCenter,
            scale: transform.scale,
            rotation_degrees: rotationDegrees
          })
          state.captureAlignmentDraft = {
            ...transform,
            rotation_degrees: rotationDegrees,
            ...offsets
          }
          renderer.domElement.style.cursor = "crosshair"
        } else if (state.captureAlignmentDragging.mode === "scale") {
          const distance = Math.max(
            Math.hypot(hit.point.x - state.captureAlignmentDragging.planCenter.x, hit.point.z - state.captureAlignmentDragging.planCenter.z),
            0.001
          )
          const scale = Math.max(
            state.captureAlignmentDragging.initialScale * (distance / state.captureAlignmentDragging.initialDistance),
            0.01
          )
          const offsets = captureAlignmentOffsetsForCenter({
            sourceCenter: state.captureAlignmentDragging.sourceCenter,
            planCenter: state.captureAlignmentDragging.planCenter,
            scale,
            rotation_degrees: transform.rotation_degrees
          })
          state.captureAlignmentDraft = {
            ...transform,
            scale,
            ...offsets
          }
          renderer.domElement.style.cursor = "nwse-resize"
        } else {
          state.captureAlignmentDraft = {
            ...transform,
            offset_x: state.captureAlignmentDragging.offsetX + (hit.point.x - state.captureAlignmentDragging.start.x),
            offset_z: state.captureAlignmentDragging.offsetZ + (hit.point.z - state.captureAlignmentDragging.start.z)
          }
          renderer.domElement.style.cursor = "grabbing"
        }
        renderCaptureAlignmentInspector()
        rebuildCaptureAlignmentObjects()
      }
      return
    }

    if (state.mappingMode && state.panelMode === "capture" && activeCaptureAlignmentSession()) {
      const controlHit = pickCaptureAlignmentControl(event)
      if (controlHit?.control) {
        const mode = controlHit.control.userData.dtCaptureAlignmentControl
        renderer.domElement.style.cursor = mode === "scale" ? "nwse-resize" : mode === "rotate" ? "crosshair" : "grab"
      } else {
        renderer.domElement.style.cursor = state.mappingMode ? "crosshair" : "default"
      }
    }

    const navigationHit = pickIntersection(event, {
      includePlacements: false,
      includeNavigationPoints: true,
      includeModel: false
    })
    const navigationRoot = navigationHit ? resolveNavigationPointRoot(navigationHit.object) : null

    if (!state.mappingMode) {
      if (navigationRoot) {
        setHoveredObject(navigationRoot, navigationHit.point)
        renderer.domElement.style.cursor = "pointer"
        return
      }

      clearHoveredObject()
      renderer.domElement.style.cursor = "default"
      return
    }

    if (state.panelMode === "capture") {
      clearHoveredObject()
      renderer.domElement.style.cursor = activeCaptureAlignmentSession() ? "grab" : "default"
      return
    }

    if (state.panelMode === "navigate") {
      if (navigationRoot) {
        setHoveredObject(navigationRoot, navigationHit.point)
        renderer.domElement.style.cursor = "pointer"
        return
      }

      const surfaceHit = pickNavigationSurfaceIntersection(event)

      if (!surfaceHit) {
        clearHoveredObject()
        renderer.domElement.style.cursor = "default"
        return
      }

      setHoveredObject(null, surfaceHit.point)
      renderer.domElement.style.cursor = "crosshair"
      return
    }

    const hit = pickPlacementSurfaceIntersection(event)
    if (!hit) {
      clearHoveredObject()
      renderer.domElement.style.cursor = "crosshair"
      return
    }

    const placementRoot = resolvePlacementRoot(hit.object)
    if (placementRoot) {
      setHoveredObject(placementRoot, hit.point)
      renderer.domElement.style.cursor = "pointer"
      return
    }

    if (state.panelMode === "place") {
      setHoveredObject(null, hit.point)
      renderer.domElement.style.cursor = "crosshair"
      return
    }

    if (!state.model) {
      clearHoveredObject()
      renderer.domElement.style.cursor = "default"
      return
    }

    const hoveredObject = resolveSelectableObject(hit.object, state.model)
    setHoveredObject(hoveredObject, hit.point)
    renderer.domElement.style.cursor = "pointer"
  }

  const trySelectFromPointer = (event) => {
    if (state.photo360) return
    if (state.pointerDragged) return
    if (state.mappingMode && state.panelMode === "capture") return

    const navigationHit = pickIntersection(event, {
      includePlacements: false,
      includeNavigationPoints: true,
      includeModel: false
    })
    const navigationRoot = navigationHit ? resolveNavigationPointRoot(navigationHit.object) : null
    if (navigationRoot) {
      const navigationPoint = state.navigationPointsById.get(navigationRoot.userData.dtNavigationPointId)
      if (!navigationPoint) return

      selectNavigationPoint(navigationPoint)
      closePopup()

      if (!state.mappingMode) {
        if (is2DLikeMode(state.viewMode) && event.detail < 2) {
          setFeedback(`Double-click ${navigationPointLabel(navigationPoint)} to enter walkthrough.`, "neutral")
          return
        }

        focusOnNavigationPoint(navigationPoint)
        setFeedback(`Moving to ${navigationPointLabel(navigationPoint)}.`, "success")
        return
      }

      if (state.panelMode !== "navigate") return

      return
    }

    const hit = pickPlacementSurfaceIntersection(event)
    if (!hit) {
      if (!state.mappingMode) {
        clearSelectedObject()
        closePopup()
      }
      return
    }

    if (state.mappingMode && state.panelMode === "navigate") {
      const pose = navigationPointPoseFromHit(hit)
      if (!pose) return

      void createNavigationPoint(pose)
      closePopup()
      return
    }

    const placementRoot = resolvePlacementRoot(hit.object)
    if (placementRoot) {
      const placement = state.placementsById.get(placementRoot.userData.dtPlacementId)
      if (!placement) return

      if (state.mappingMode) {
        if (state.panelMode === "place") {
          selectPlacement(placement)
          setFeedback(`Selected ${placement.thing.name}.`, "success")
        }
        closePopup()
        return
      }

      selectPlacement(placement)
      state.popupEntry = { kind: "placement", ...placement }
      renderPopup()
      return
    }

    if (state.mappingMode && state.panelMode === "place") {
      selectPlacementPoint(hit.point)
      setFeedback("Selected placement point.", "success")
      closePopup()
      return
    }

    if (!state.model) return

    const object = resolveSelectableObject(hit.object, state.model)
    const nodeKey = object.userData?.dtNodeKey
    const mapping = nodePopupEntryForKey(nodeKey)

    if (state.mappingMode) {
      selectNodeObject(object, hit.point)
      if (state.panelMode === "tag" && mapping) {
        setFeedback(
          `This node is already tagged as <strong>${escapeHtml(mapping.thing.name)}</strong>. You can update or replace the tag in the next step.`,
          "success",
          { html: true }
        )
      } else {
        setFeedback(`Selected ${selectedObjectLabel()}.`, "success")
      }
      closePopup()
      return
    }

    if (!mapping) {
      clearSelectedObject()
      closePopup()
      return
    }

    selectNodeObject(object, hit.point)
    state.popupEntry = { kind: "mapping", ...mapping }
    renderPopup()
  }

  const tryLaunchPhoto360FromPointer = (event) => {
    if (state.photo360) return
    if (state.pointerDragged) return

    const hit = pickIntersection(event, {
      includePlacements: true,
      includeNavigationPoints: false,
      includeModel: true,
      allowSprites: true
    })
    if (!hit) return

    const placementRoot = resolvePlacementRoot(hit.object)
    if (placementRoot) {
      const placement = state.placementsById.get(placementRoot.userData.dtPlacementId)
      const launched = launchPhoto360Mapping(photo360MappingForPlacement(placement), placement)
      if (launched) {
        event.preventDefault()
        event.stopPropagation()
      }
      return
    }

    if (!state.model) return

    const object = resolveSelectableObject(hit.object, state.model)
    const nodeKey = object?.userData?.dtNodeKey
    const launched = launchPhoto360Mapping(photo360MappingForNodeKey(nodeKey))
    if (launched) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  const mappingPayloadForSelection = () => ({
    model_node_key: selectedNodeKey(),
    model_node_name: selectedObjectLabel(),
    metadata: {}
  })

  const placementPayloadForSelection = () => ({
    position: selectedPlacementSourcePosition(),
    rotation: { x: 0, y: 0, z: 0 },
    scale: 1,
    metadata: selectedPlacementMetadata()
  })

  ;({
    saveSelectedThing,
    savePlacedThing,
    attachExistingThing,
    saveSelectedNodeMapping,
    saveSelectedPlacementNodeMapping,
    removeNodeMapping,
    attachExistingPlacedThing,
    createNavigationPoint,
    removeNavigationPoint,
    removeSelectedMapping,
    removePlacement,
    removeSelectedPlacement,
    confirmAndRemovePlacement
  } = createViewerMutationsController({
    state,
    element,
    apiRequest,
    thingNameInput,
    thingKindInput,
    thingDescriptionInput,
    thingAttributesInput,
    placementThingNameInput,
    placementThingKindInput,
    placementThingDescriptionInput,
    placementThingAttributesInput,
    existingThingSelect,
    placementExistingThingSelect,
    nodeMappingIntegrationSelect,
    nodeMappingObjectTypeSelect,
    nodeMappingExternalObjectSelect,
    placementNodeMappingIntegrationSelect,
    placementNodeMappingObjectTypeSelect,
    placementNodeMappingExternalObjectSelect,
    mappingPayloadForSelection,
    placementPayloadForSelection,
    selectedMapping,
    selectedNodeKey,
    selectedObjectLabel,
    selectedPlacementPosition,
    selectedOptionMetadata,
    mergeMapping,
    mergePlacement,
    mergeNodeMapping,
    syncMappings,
    syncNodeMappings,
    syncPlacements,
    syncNavigationPoints,
    selectNavigationPoint,
    focusOnNavigationPoint,
    navigationPointLabel,
    setFeedback,
    is2DLikeMode
  }))

  toolbar?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-viewer-action]")
    if (!button) return

    if (state.photo360 && button.dataset.viewerAction !== "fullscreen") {
      closePhoto360Viewer({ showFeedback: false })
    }

    if (button.dataset.viewerAction !== "labels" && state.labelsMenuOpen) {
      setLabelsMenuOpen(false)
    }
    if (button.dataset.viewerAction !== "reference-layers" && state.referenceLayerMenuOpen) {
      setReferenceLayerMenuOpen(false)
    }
    if (button.dataset.viewerAction !== "export" && state.exportMenuOpen) {
      setExportMenuOpen(false)
    }

    switch (button.dataset.viewerAction) {
      case "fit":
        closePopup()
        fitCurrentView()
        break
      case "reset":
        closePopup()
        resetView()
        break
      case "view-3d":
        queueViewMode("3d")
        break
      case "view-2d":
        queueViewMode("2d")
        break
      case "reference-layers":
        renderReferenceLayerMenu()

        if (state.referenceLayers.length === 0) {
          setReferenceLayerMenuOpen(false)
          setActiveButton()
          setFeedback("No reference layers are available for this plan yet.", "neutral")
          break
        }

        if (state.referenceLayers.length === 1) {
          const [referenceLayer] = state.referenceLayers
          void (async () => {
            await toggleReferenceLayerById(referenceLayer.id, { showFeedback: false })
            setReferenceLayerMenuOpen(false)
            renderReferenceLayerMenu()
            setActiveButton()
          })()
          break
        }

        setReferenceLayerMenuOpen(!state.referenceLayerMenuOpen)
        setActiveButton()
        break
      case "export":
        if (!state.capabilities.exportImage) break
        setExportMenuOpen(!state.exportMenuOpen)
        setActiveButton()
        break
      case "viewer-settings":
        if (!state.capabilities.viewerSettings) break
        void toggleViewerSettingsPanel()
        break
      case "walkthrough":
        if (!state.model) {
          setFeedback("Upload a GLB to use walkthrough mode.", "neutral")
          break
        }
        queueViewMode("walkthrough")
        break
      case "auto-rotate":
        toggleAutoRotate()
        break
      case "orbit-lock":
        state.orbitLock = !state.orbitLock
        applyOrbitBounds()
        setActiveButton()
        setFeedback(
          state.orbitLock ? "Underside view lock is on." : "Underside view lock is off.",
          "neutral"
        )
        break
      case "labels":
        setLabelsMenuOpen(!state.labelsMenuOpen)
        renderLabelsMenu()
        setActiveButton()
        break
      case "mapping-mode":
        if (!state.capabilities.mapping) {
          setFeedback(`Mapping mode is unavailable in ${state.mode} viewer mode.`, "neutral")
          break
        }
        if (!state.framing) {
          setFeedback("Load the viewer before opening mapping mode.", "neutral")
          break
        }
        if (state.sidePanelMode === "mapping") {
          closeMappingPanel()
        } else {
          if (!state.model && state.panelMode === "tag") {
            setPanelMode("place")
          }
          state.sidePanelMode = "mapping"
          state.mappingMode = true
          state.guidedStep = 1
          state.preferAdvancedEditor = false
          state.advancedNavigateSection = null
          state.advancedCaptureSection = "sessions"
          closePopup()
          if (state.panelMode === "capture") queueViewMode("2d")
          renderer.domElement.style.cursor = "crosshair"
          renderInspector()
          setFeedback(
            state.panelMode === "tag"
              ? "Mapping mode is on. Guided flow is ready for a new tag."
              : state.panelMode === "place"
                ? state.model
                  ? "Mapping mode is on. Guided flow is ready for a new placement."
                  : "Mapping mode is on. Click the plan surface to add a placement."
                : state.panelMode === "capture"
                  ? "Mapping mode is on. Adjust the capture route overlay in 2D."
                  : state.model
                    ? "Mapping mode is on. Click the model to add navigation points."
                    : "Mapping mode is on. Click the plan surface to add navigation points.",
            "neutral"
          )
        }
        break
      case "fullscreen":
        toggleFullscreen()
        break
      default:
        break
    }
  })

  mappingPanelClose?.addEventListener("click", () => {
    if (state.sidePanelMode !== "mapping") return
    closeMappingPanel()
  })

  viewerSettingsClose?.addEventListener("click", () => {
    if (state.sidePanelMode !== "viewer-settings") return
    void toggleViewerSettingsPanel()
  })

  saveModelButton?.addEventListener("click", () => {
    persistModelFile()
  })

  existingThingForm?.addEventListener("submit", (event) => {
    event.preventDefault()
    attachExistingThing()
  })

  placementExistingThingForm?.addEventListener("submit", (event) => {
    event.preventDefault()
    attachExistingPlacedThing()
  })

  captureAlignmentForm?.addEventListener("submit", (event) => {
    event.preventDefault()
    saveCaptureAlignment()
  })

  bindEnhancedSelectChange(captureAlignmentSessionSelect, () => {
    setActiveCaptureAlignmentSession(captureAlignmentSessionSelect.value)
  })

  Object.entries(captureAlignmentTransformInputs).forEach(([key, input]) => {
    input?.addEventListener("input", () => {
      const current = captureTransformForSession()
      state.captureAlignmentDraft = {
        ...current,
        [key]: key === "scale"
          ? Math.max(parseFloatOr(input.value, current[key]), 0.001)
          : parseFloatOr(input.value, current[key])
      }
      rebuildCaptureAlignmentObjects()
    })
  })

  captureAlignmentPublishButton?.addEventListener("click", () => {
    publishCaptureAlignment()
  })
  captureAlignmentDeleteButton?.addEventListener("click", () => {
    removeCaptureAlignmentPublishedObjects()
  })

  captureAlignmentList?.addEventListener("click", (event) => {
    const visibleButton = event.target.closest("[data-capture-session-visible]")
    if (visibleButton) {
      event.preventDefault()
      event.stopPropagation()
      switchVisibleCaptureSession(visibleButton.dataset.captureSessionVisible)
      return
    }

    const deleteButton = event.target.closest("[data-capture-session-delete]")
    if (deleteButton) {
      event.preventDefault()
      event.stopPropagation()
      removeCaptureAlignmentPublishedObjects(deleteButton.dataset.captureSessionDelete)
      return
    }

    const selectButton = event.target.closest("[data-capture-session-select]")
    if (!selectButton) return
    setActiveCaptureAlignmentSession(selectButton.dataset.captureSessionSelect)
  })

  panelModeButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (button.dataset.panelMode === "tag" && !state.model) {
        setFeedback("Upload a GLB to tag existing model geometry.", "neutral")
        return
      }
      state.guidedStep = 1
      state.preferAdvancedEditor = false
      state.advancedTagSection = null
      state.advancedPlaceSection = null
      state.advancedNavigateSection = null
      state.advancedCaptureSection = "sessions"
      clearHoveredObject()
      clearSelectedObject()
      closePopup()
      setPanelMode(button.dataset.panelMode)
      if (button.dataset.panelMode === "capture") queueViewMode("2d")
      renderer.domElement.style.cursor = state.mappingMode ? "crosshair" : "default"
      renderInspector()
      setFeedback(
        button.dataset.panelMode === "tag"
          ? "Tag mode is active. Guided flow is ready for a new tag."
          : button.dataset.panelMode === "place"
            ? state.model
              ? "Place mode is active. Guided flow is ready for a new placement."
              : "Place mode is active. Click the plan surface to choose a placement point."
            : button.dataset.panelMode === "capture"
              ? "Capture session mapping is active. Adjust the 2D route overlay."
              : state.model
                ? "Navigation Point mode is active. Click the model to drop points."
                : "Navigation Point mode is active. Click the plan surface to drop points.",
        "neutral"
      )
    })
  })

  advancedPanelModeButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (button.dataset.advancedPanelMode === "tag" && !state.model) {
        setFeedback("Upload a GLB to tag existing model geometry.", "neutral")
        return
      }
      state.preferAdvancedEditor = true
      state.advancedTagSection = null
      state.advancedPlaceSection = null
      state.advancedNavigateSection = null
      state.advancedCaptureSection = "sessions"
      clearHoveredObject()
      clearSelectedObject()
      closePopup()
      setPanelMode(button.dataset.advancedPanelMode)
      if (button.dataset.advancedPanelMode === "capture") queueViewMode("2d")
      renderer.domElement.style.cursor = state.mappingMode ? "crosshair" : "default"
      renderInspector()
      setFeedback(
        button.dataset.advancedPanelMode === "tag"
          ? "Advanced Mode is active for tag editing."
          : button.dataset.advancedPanelMode === "place"
            ? "Advanced Mode is active for placed object editing."
            : button.dataset.advancedPanelMode === "capture"
              ? "Advanced Mode is active for capture session alignment."
              : "Advanced Mode is active for navigation point editing.",
        "neutral"
      )
    })
  })

  editorModeLink?.addEventListener("click", (event) => {
    event.preventDefault()
    if (state.editorMode === "guided") {
      state.preferAdvancedEditor = true
    } else {
      state.guidedStep = 1
      state.preferAdvancedEditor = false
    }

    renderInspector()
    setFeedback(
      state.preferAdvancedEditor
        ? "Advanced Mode is active."
        : "Guided flow is active.",
      "neutral"
    )
  })

  guidedPrevButton?.addEventListener("click", () => {
    if (state.guidedStep <= 1) return

    state.guidedStep = Math.max(1, state.guidedStep - 1)
    renderInspector()
    setFeedback(`Step ${state.guidedStep} is active.`, "neutral")
  })

  guidedStepNextButton?.addEventListener("click", () => {
    if (!guidedCanAdvance() || state.guidedStep >= guidedMaxStep()) return

    state.guidedStep += 1
    renderInspector()
    setFeedback(
      state.guidedStep === 2
        ? state.panelMode === "tag"
          ? "Step 2 is active. Click model geometry to continue tagging."
          : state.panelMode === "place"
            ? "Step 2 is active. Click the model to choose a placement point."
            : "Step 2 is active. Click the model to drop navigation points."
        : `Step ${state.guidedStep} is active.`,
      "neutral"
    )
  })

  guidedFinishButton?.addEventListener("click", () => {
    state.preferAdvancedEditor = true
    state.advancedTagSection = null
    state.advancedPlaceSection = null
    renderInspector()
    setFeedback("Advanced Mode is active.", "neutral")
  })

  advancedTagAccordionButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (state.editorMode !== "advanced" || state.panelMode !== "tag") return

      state.advancedTagSection =
        state.advancedTagSection === button.dataset.advancedTagAccordion
          ? null
          : button.dataset.advancedTagAccordion
      renderInspector()
    })
  })

  advancedPlaceAccordionButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (state.editorMode !== "advanced" || state.panelMode !== "place") return

      state.advancedPlaceSection =
        state.advancedPlaceSection === button.dataset.advancedPlaceAccordion
          ? null
          : button.dataset.advancedPlaceAccordion
      renderInspector()
    })
  })

  advancedNavigateAccordionButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (state.editorMode !== "advanced" || state.panelMode !== "navigate") return

      state.advancedNavigateSection =
        state.advancedNavigateSection === button.dataset.advancedNavigateAccordion
          ? null
          : button.dataset.advancedNavigateAccordion
      renderInspector()
    })
  })

  advancedCaptureAccordionButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (state.editorMode !== "advanced" || state.panelMode !== "capture") return

      state.advancedCaptureSection =
        state.advancedCaptureSection === button.dataset.advancedCaptureAccordion
          ? null
          : button.dataset.advancedCaptureAccordion
      renderInspector()
    })
  })

  viewerSettingsAccordionButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (state.sidePanelMode !== "viewer-settings") return

      state.viewerSettingsSection =
        state.viewerSettingsSection === button.dataset.viewerSettingsAccordion
          ? null
          : button.dataset.viewerSettingsAccordion
      renderInspector()
    })
  })

  tabButtons.forEach((button) => {
    button.addEventListener("click", () => {
      setMappingTab(button.dataset.mappingTab)
    })
  })

  placementTabButtons.forEach((button) => {
    button.addEventListener("click", () => {
      setPlacementTab(button.dataset.placementTab)
    })
  })

  thingForm?.addEventListener("submit", (event) => {
    event.preventDefault()
    saveSelectedThing()
  })

  placementThingForm?.addEventListener("submit", (event) => {
    event.preventDefault()
    savePlacedThing()
  })

  removeMappingButton?.addEventListener("click", () => {
    removeSelectedMapping()
  })

  removePlacementButton?.addEventListener("click", () => {
    removeSelectedPlacement()
  })

  removeNavigationPointButton?.addEventListener("click", () => {
    removeNavigationPoint()
  })

  placementPresetButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (placementThingKindInput) {
        setPlacementObjectType(button.dataset.placementKindPreset)
        setTaggableSelectValue(placementThingKindInput, button.dataset.placementKindPreset)
        if (button.dataset.placementKindPreset === "360_photo" && placementThingNameInput && !placementThingNameInput.value.trim()) {
          placementThingNameInput.value = "360 Photo"
        }
        syncPlacementPresetButtons()
        refreshSelectionHelper()
        refreshHoverHelper()
      }
    })
  })

  placementVerticalModeButtons.forEach((button) => {
    button.addEventListener("click", () => {
      if (button.disabled) return
      setPlacementVerticalMode(button.dataset.placementVerticalMode)
      renderPlacementInspector()
      refreshSelectionHelper()
      refreshHoverHelper()
    })
  })

  placementThingKindInput?.addEventListener("change", () => {
    syncPlacementPresetButtons()
  })

  viewerSettingsForm?.addEventListener("submit", (event) => {
    event.preventDefault()
    void saveViewerSettings()
  })

  Object.entries(viewerSettingInputs).forEach(([key, input]) => {
    input?.addEventListener("input", () => {
      if (input.type !== "checkbox" && input.value === "") return
      applyViewerSettingChange(key, viewerSettingInputValue(input))
    })

    input?.addEventListener("change", () => {
      if (input.type !== "checkbox" && input.value === "") {
        syncCablingPlanForm()
        return
      }

      applyViewerSettingChange(key, viewerSettingInputValue(input))
    })
  })

  referenceLayerCreateForm?.addEventListener("submit", (event) => {
    event.preventDefault()
    void createReferenceLayer()
  })

  referenceLayerCreateNameInput?.addEventListener("input", () => {
    syncCablingPlanForm()
  })

  referenceLayerCreateFileInput?.addEventListener("change", () => {
    syncCablingPlanForm()
  })

  viewerSettingsSaveButton?.addEventListener("click", () => {
    void saveViewerSettings()
  })

  viewerSettingsResetButton?.addEventListener("click", () => {
    resetViewerSettings()
  })

  viewerSet3DCurrentButton?.addEventListener("click", () => {
    sync3DViewerSettingsToCurrentView()
  })

  viewerSet2DCurrentButton?.addEventListener("click", () => {
    sync2DViewerSettingsToCurrentView()
  })

  viewerSetWalkthroughCurrentButton?.addEventListener("click", () => {
    syncWalkthroughSettingsToCurrentView()
  })

  viewerSettingsSubsectionButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const expanded = button.getAttribute("aria-expanded") === "true"
      button.setAttribute("aria-expanded", expanded ? "false" : "true")
      button.classList.toggle("is-expanded", !expanded)

      const panelElement = viewerSettingsSubsectionPanels.find((entry) => entry.dataset.viewerSettingsSubsectionPanel === button.dataset.viewerSettingsSubsection)
      if (panelElement) panelElement.hidden = expanded
    })
  })

  referenceLayerMenuList?.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null
    const button = target?.closest("[data-reference-layer-menu-id]")
    if (!button) return
    event.preventDefault()
    event.stopPropagation()
    if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation()

    const referenceLayerId = button.dataset.referenceLayerMenuId

    void (async () => {
      try {
        await toggleReferenceLayerById(referenceLayerId, {
          showFeedback: true
        })
        renderReferenceLayerMenu()
        setActiveButton()
      } catch (error) {
        setFeedback(`Could not toggle the reference layer: ${error.message}`, "error")
      }
    })()
  })

  referenceLayerMenuList?.addEventListener("pointerdown", (event) => {
    event.stopPropagation()
  })

  referenceLayerList?.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null
    const toggleButton = target?.closest("[data-reference-layer-editor-toggle-id]")
    if (toggleButton) {
      void applyReferenceLayerSelection(toggleButton.dataset.referenceLayerEditorToggleId, {
        preserveVisibility: true,
        showFeedback: false,
        toggleAccordion: true
      })
      return
    }

    const deleteButton = target?.closest("[data-reference-layer-delete-id]")
    if (deleteButton) {
      event.preventDefault()
      event.stopPropagation()
      void deleteReferenceLayer(deleteButton.dataset.referenceLayerDeleteId)
      return
    }

    const replaceButton = target?.closest("[data-reference-layer-replace-submit-id]")
    if (replaceButton) {
      const panelElement = replaceButton.closest("[data-reference-layer-editor-panel-id]")
      const fileInput = panelElement?.querySelector("[data-reference-layer-replace-file-id]")
      const file = fileInput?.files?.[0]
      void replaceReferenceLayerPdf(replaceButton.dataset.referenceLayerReplaceSubmitId, file)
    }
  })

  referenceLayerList?.addEventListener("input", (event) => {
    const input = event.target.closest("[data-reference-layer-setting]")
    if (!input) return
    if (input.type !== "checkbox" && input.value === "") return

    const referenceLayerId = input.dataset.referenceLayerId
    if (!referenceLayerId) return

    if (referenceLayerId !== state.activeReferenceLayerId) {
      syncActiveReferenceLayerDraftToCollection()
      loadReferenceLayerDraft(findReferenceLayerById(state.referenceLayers, referenceLayerId), { preserveVisibility: true })
    }

    applyCablingPlanSettingChange(input.dataset.referenceLayerSetting, viewerSettingInputValue(input))
  })

  referenceLayerList?.addEventListener("change", (event) => {
    const input = event.target.closest("[data-reference-layer-setting]")
    if (!input) return

    const referenceLayerId = input.dataset.referenceLayerId
    if (!referenceLayerId) return

    if (referenceLayerId !== state.activeReferenceLayerId) {
      syncActiveReferenceLayerDraftToCollection()
      loadReferenceLayerDraft(findReferenceLayerById(state.referenceLayers, referenceLayerId), { preserveVisibility: true })
    }

    if (input.type !== "checkbox" && input.value === "") {
      syncCablingPlanForm()
      return
    }

    applyCablingPlanSettingChange(input.dataset.referenceLayerSetting, viewerSettingInputValue(input))
  })

  exportButtons.forEach((button) => {
    button.addEventListener("click", () => {
      exportCurrentView(button.dataset.viewerExportFormat)
      setExportMenuOpen(false)
      setActiveButton()
    })
  })

  if (window.jQuery && placementThingKindInput) {
    window.jQuery(placementThingKindInput).on("select2:select select2:clear", () => {
      syncPlacementPresetButtons()
    })
  }

  labelsShowAllButton?.addEventListener("click", () => {
    state.labelsVisible = true
    state.markersVisible = true
    state.hiddenLabelObjectTypes.clear()
    state.hiddenLabelThingTypes.clear()
    state.hiddenLabelIntegrationIds.clear()
    state.hiddenLabelIntegrationObjectTypes.clear()
    renderLabelsMenu()
    syncPlacementVisibility()
    updateHotspots()
    syncNavigationPointVisibility()
    setActiveButton()
    setFeedback("All labels and markers are visible.", "neutral")
  })

  labelsHideAllButton?.addEventListener("click", () => {
    state.labelsVisible = false
    state.markersVisible = false
    state.hiddenLabelObjectTypes = new Set(allLabelObjectTypes())
    state.hiddenLabelThingTypes = new Set(allLabelThingTypes())
    state.hiddenLabelIntegrationIds = new Set(allLabelIntegrations().map((option) => option.value))
    state.hiddenLabelIntegrationObjectTypes = new Set(allLabelIntegrationObjectTypes().map((option) => option.value))
    renderLabelsMenu()
    syncPlacementVisibility()
    updateHotspots()
    syncNavigationPointVisibility()
    setActiveButton()
    setFeedback("All labels and markers are hidden.", "neutral")
  })

  labelsPanel?.addEventListener("change", (event) => {
    const markersCheckbox = event.target.closest("[data-viewer-label-markers]")
    const objectTypeCheckbox = event.target.closest("[data-viewer-label-object-type]")
    const thingTypeCheckbox = event.target.closest("[data-viewer-label-thing-type]")
    const integrationCheckbox = event.target.closest("[data-viewer-label-integration-id]")
    const integrationObjectTypeCheckbox = event.target.closest("[data-viewer-label-integration-object-type]")
    if (!markersCheckbox && !objectTypeCheckbox && !thingTypeCheckbox && !integrationCheckbox && !integrationObjectTypeCheckbox) return

    if (markersCheckbox) {
      state.markersVisible = markersCheckbox.checked
      syncNavigationPointVisibility()
      setActiveButton()
      return
    }

    state.labelsVisible = true

    if (objectTypeCheckbox) {
      const value = objectTypeCheckbox.dataset.viewerLabelObjectType
      if (objectTypeCheckbox.checked) state.hiddenLabelObjectTypes.delete(value)
      else state.hiddenLabelObjectTypes.add(value)
    }

    if (thingTypeCheckbox) {
      const value = thingTypeCheckbox.dataset.viewerLabelThingType
      if (thingTypeCheckbox.checked) state.hiddenLabelThingTypes.delete(value)
      else state.hiddenLabelThingTypes.add(value)
    }

    if (integrationCheckbox) {
      const value = integrationCheckbox.dataset.viewerLabelIntegrationId
      if (integrationCheckbox.checked) state.hiddenLabelIntegrationIds.delete(value)
      else state.hiddenLabelIntegrationIds.add(value)
    }

    if (integrationObjectTypeCheckbox) {
      const value = integrationObjectTypeCheckbox.dataset.viewerLabelIntegrationObjectType
      if (integrationObjectTypeCheckbox.checked) state.hiddenLabelIntegrationObjectTypes.delete(value)
      else state.hiddenLabelIntegrationObjectTypes.add(value)
    }

    syncPlacementVisibility()
    updateHotspots()
    setActiveButton()
  })

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return
    let closed = false

    if (state.labelsMenuOpen) {
      setLabelsMenuOpen(false)
      closed = true
    }
    if (state.referenceLayerMenuOpen) {
      setReferenceLayerMenuOpen(false)
      closed = true
    }
    if (state.exportMenuOpen) {
      setExportMenuOpen(false)
      closed = true
    }

    if (closed) setActiveButton()
  })

  hydratePlacementPresetIcons()

  bindEnhancedSelectChange(nodeMappingIntegrationSelect, () => {
    populateObjectTypeSelect(nodeMappingIntegrationSelect, nodeMappingObjectTypeSelect)
    populateExternalObjectSelect([], nodeMappingExternalObjectSelect)
    fetchNodeMappingCollection({
      integrationSelect: nodeMappingIntegrationSelect,
      objectTypeSelect: nodeMappingObjectTypeSelect,
      externalObjectSelect: nodeMappingExternalObjectSelect
    })
  })

  bindEnhancedSelectChange(nodeMappingObjectTypeSelect, () => {
    fetchNodeMappingCollection({
      integrationSelect: nodeMappingIntegrationSelect,
      objectTypeSelect: nodeMappingObjectTypeSelect,
      externalObjectSelect: nodeMappingExternalObjectSelect
    })
  })

  nodeMappingForm?.addEventListener("submit", (event) => {
    event.preventDefault()
    saveSelectedNodeMapping()
  })

  nodeMappingSubmitButton?.addEventListener("click", (event) => {
    event.preventDefault()
    saveSelectedNodeMapping()
  })

  bindEnhancedSelectChange(placementNodeMappingIntegrationSelect, () => {
    populateObjectTypeSelect(placementNodeMappingIntegrationSelect, placementNodeMappingObjectTypeSelect)
    populateExternalObjectSelect([], placementNodeMappingExternalObjectSelect)
    fetchNodeMappingCollection({
      integrationSelect: placementNodeMappingIntegrationSelect,
      objectTypeSelect: placementNodeMappingObjectTypeSelect,
      externalObjectSelect: placementNodeMappingExternalObjectSelect
    })
  })

  bindEnhancedSelectChange(placementNodeMappingObjectTypeSelect, () => {
    fetchNodeMappingCollection({
      integrationSelect: placementNodeMappingIntegrationSelect,
      objectTypeSelect: placementNodeMappingObjectTypeSelect,
      externalObjectSelect: placementNodeMappingExternalObjectSelect
    })
  })

  placementNodeMappingForm?.addEventListener("submit", (event) => {
    event.preventDefault()
    saveSelectedPlacementNodeMapping()
  })

  placementNodeMappingSubmitButton?.addEventListener("click", (event) => {
    event.preventDefault()
    saveSelectedPlacementNodeMapping()
  })

  searchInput?.addEventListener("input", () => {
    renderSearchResults()
  })

  searchInput?.addEventListener("focus", () => {
    renderSearchResults()
  })

  searchInput?.addEventListener("keydown", (event) => {
    const results = filteredSearchEntries()
    if (event.key === "Escape") {
      hideSearchResults()
      searchInput.blur()
      return
    }

    if (results.length === 0) return

    const currentIndex = results.findIndex((entry) => searchEntryId(entry) === state.activeSearchResultId)

    if (event.key === "ArrowDown") {
      event.preventDefault()
      const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % results.length
      state.activeSearchResultId = searchEntryId(results[nextIndex])
      renderSearchResults()
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      const nextIndex = currentIndex <= 0 ? results.length - 1 : currentIndex - 1
      state.activeSearchResultId = searchEntryId(results[nextIndex])
      renderSearchResults()
      return
    }

    if (event.key === "Enter") {
      event.preventDefault()
      const entry = results[currentIndex >= 0 ? currentIndex : 0]
      focusSearchEntry(entry)
    }
  })

  searchResults?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-search-entry-id]")
    if (!button) return

    const entry = state.searchEntries.find((candidate) => searchEntryId(candidate) === button.dataset.searchEntryId)
    focusSearchEntry(entry)
  })

  labelsLayer?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-hotspot-placement-id]")
    if (!button) return

    event.preventDefault()
    event.stopPropagation()

    const placement = state.placementsById.get(button.dataset.hotspotPlacementId)
    if (!placement) return

    if (state.mappingMode) {
      setPanelMode("place")
      selectPlacement(placement)
      closePopup()
      setFeedback(`Selected ${placement.thing.name}.`, "success")
      return
    }

    selectPlacement(placement)
    state.popupEntry = { kind: "placement", ...placement }
    renderPopup()
  })

  mappingList?.addEventListener("click", (event) => {
    const button = event.target.closest("[data-node-key]")
    if (!button) return

    const object = state.nodesByKey.get(button.dataset.nodeKey)
    if (object) {
      selectNodeObject(object)
      focusOnNodeObject(object)
      setFeedback(`Focused ${selectedObjectLabel()}.`, "success")
    }
  })

  nodeMappingsList?.addEventListener("click", (event) => {
    const removeButton = event.target.closest("[data-node-mapping-remove]")
    if (removeButton) {
      removeNodeMapping(removeButton.dataset.nodeMappingRemove)
    }
  })

  placementNodeMappingsList?.addEventListener("click", (event) => {
    const removeButton = event.target.closest("[data-placement-node-mapping-remove]")
    if (removeButton) {
      removeNodeMapping(removeButton.dataset.placementNodeMappingRemove)
    }
  })

  placementList?.addEventListener("click", (event) => {
    const deleteButton = event.target.closest("[data-delete-placement-id]")
    if (deleteButton) {
      event.preventDefault()
      event.stopPropagation()
      void confirmAndRemovePlacement(deleteButton.dataset.deletePlacementId)
      return
    }

    const button = event.target.closest("[data-placement-id]")
    if (!button) return

    const placement = state.placementsById.get(button.dataset.placementId)
    if (placement) {
      selectPlacement(placement)
      focusOnPlacement(placement)
      setFeedback(`Focused ${placement.thing.name}.`, "success")
    }
  })

  navigationPointList?.addEventListener("click", (event) => {
    const deleteButton = event.target.closest("[data-delete-navigation-point-id]")
    if (deleteButton) {
      event.preventDefault()
      event.stopPropagation()
      void removeNavigationPoint(deleteButton.dataset.deleteNavigationPointId)
      return
    }

    const button = event.target.closest("[data-navigation-point-id]")
    if (!button) return

    const navigationPoint = state.navigationPointsById.get(button.dataset.navigationPointId)
    if (navigationPoint) {
      selectNavigationPoint(navigationPoint)
      focusOnNavigationPoint(navigationPoint)
      setFeedback(`Focused ${navigationPointLabel(navigationPoint)}.`, "success")
    }
  })

  popupClose?.addEventListener("click", () => {
    closePopup()
    clearSelectedObject()
  })
  popupRefresh?.addEventListener("click", () => {
    refreshPopupLiveDetails()
  })

  const handleDocumentClick = (event) => {
    if (!searchRoot?.contains(event.target)) hideSearchResults()
    if (labelsMenu && state.labelsMenuOpen && !labelsMenu.contains(event.target)) {
      setLabelsMenuOpen(false)
      setActiveButton()
    }
    if (referenceLayerMenu && state.referenceLayerMenuOpen && !referenceLayerMenu.contains(event.target)) {
      setReferenceLayerMenuOpen(false)
      setActiveButton()
    }
    if (exportMenu && state.exportMenuOpen && !exportMenu.contains(event.target)) {
      setExportMenuOpen(false)
      setActiveButton()
    }
  }

  document.addEventListener("click", handleDocumentClick)
  const handleDocumentKeyDown = (event) => {
    if (event.key === "Escape" && state.photo360) {
      closePhoto360Viewer()
    }
  }
  document.addEventListener("keydown", handleDocumentKeyDown)

  renderer.domElement.addEventListener("pointermove", handlePointerMove)
  renderer.domElement.addEventListener("pointerdown", handlePointerDown)
  renderer.domElement.addEventListener("pointerup", () => {
    state.pointerDown = null
    state.pointerDragged = false
    state.captureAlignmentDragging = null
    controls.enabled = true
    renderer.domElement.style.cursor = state.mappingMode ? "crosshair" : "default"
  })
  renderer.domElement.addEventListener("pointerleave", () => {
    state.pointerDown = null
    state.pointerDragged = false
    state.captureAlignmentDragging = null
    controls.enabled = true
    clearHoveredObject()
    renderer.domElement.style.cursor = state.mappingMode ? "crosshair" : "default"
  })
  renderer.domElement.addEventListener("click", trySelectFromPointer)
  renderer.domElement.addEventListener("dblclick", tryLaunchPhoto360FromPointer)
  renderer.domElement.addEventListener("wheel", handlePhoto360Wheel, { passive: false, capture: true })
  controls.addEventListener("change", () => {
    if (isOrthographic2DMode(state.viewMode)) {
      state.ortho2DZoom = orthographicCamera.zoom
    }
  })

  const updateShellHeight = () => {
    if (!shell) return

    const heightMode = shell.dataset.viewerShellHeightMode || "viewport"

    if (heightMode === "inline") {
      const shellWidth = shell.clientWidth || element.clientWidth || 0
      const minHeight = window.innerWidth <= 960 ? 360 : 520
      const maxHeight = window.innerWidth <= 960 ? 560 : 860
      const preferredHeight = clampNumber(shellWidth * (9 / 16), minHeight, maxHeight)
      shell.style.setProperty("--plan-viewer-shell-height", `${Math.round(preferredHeight)}px`)
      return
    }

    const toolbarHeight = toolbar?.offsetHeight || 0
    const viewportHeight = Math.max(window.innerHeight - toolbarHeight - 24, 320)
    shell.style.setProperty("--plan-viewer-shell-height", `${viewportHeight}px`)
  }

  const resize = () => {
    updateShellHeight()
    const width = element.clientWidth || 1
    const height = element.clientHeight || 1
    perspectiveCamera.aspect = width / height
    perspectiveCamera.updateProjectionMatrix()
    if (isOrthographic2DMode(state.viewMode)) {
      configureOrthographicCamera()
    } else {
      orthographicCamera.updateProjectionMatrix()
    }
    renderer.setSize(width, height, false)
    shell?.style.setProperty("--plan-viewer-toolbar-offset", `${toolbar?.offsetHeight || 0}px`)
  }

  const resizeObserver = new ResizeObserver(resize)
  resizeObserver.observe(element)
  window.addEventListener("resize", resize)
  resize()

  const animate = () => {
    if (!document.body.contains(element)) {
      resizeObserver.disconnect()
      window.removeEventListener("resize", resize)
      controls.dispose()
      renderer.dispose()
      clearSelectionHelper()
      clearSelectionMarker()
      clearHoverHelper()
      clearHoverMarker()
      clearPlacementObjects()
      clearNavigationPointObjects()
      clearCaptureAlignmentObjects()
      Array.from(state.referenceLayerOverlaysById.keys()).forEach((referenceLayerId) => disposeCablingPlanOverlay(referenceLayerId))
      disposePhoto360Mesh()
      removePhoto360Overlay()
      clearHotspots()
      embedBridge.destroy()
      if (state.feedbackTimer) {
        window.clearTimeout(state.feedbackTimer)
        state.feedbackTimer = null
      }
      document.removeEventListener("click", handleDocumentClick)
      document.removeEventListener("keydown", handleDocumentKeyDown)
      return
    }

    if (state.cameraAnimation) {
      const elapsed = performance.now() - state.cameraAnimation.startedAt
      const linearProgress = clampNumber(elapsed / state.cameraAnimation.durationMs, 0, 1)
      const easedProgress = 1 - ((1 - linearProgress) ** 3)

      camera.position.lerpVectors(state.cameraAnimation.fromCamera, state.cameraAnimation.toCamera, easedProgress)
      controls.target.lerpVectors(state.cameraAnimation.fromTarget, state.cameraAnimation.toTarget, easedProgress)

      if (linearProgress >= 1) {
        camera.position.copy(state.cameraAnimation.toCamera)
        controls.target.copy(state.cameraAnimation.toTarget)
        state.cameraAnimation = null
      }
    }

    controls.update()
    updateHotspots()
    updatePhoto360Overlay()
    renderer.render(scene, camera)
    requestAnimationFrame(animate)
  }

  const loader = new GLTFLoader()
  void (async () => {
    try {
      let modelLoadedFromCache = false
      if (element.dataset.modelUrl) {
        updateLoadingMessage("Fetching model from secure storage...")
        const { arrayBuffer: modelArrayBuffer, cacheHit } = await fetchCachedArrayBuffer(element.dataset.modelUrl, {
          onCacheHit: () => updateLoadingMessage("Loading cached model..."),
          onCacheMiss: () => updateLoadingMessage("Downloading building geometry...")
        })
        modelLoadedFromCache = cacheHit
        updateLoadingMessage(cacheHit ? "Restoring 3D scene..." : "Unpacking 3D model...")
        const gltf = await parseGltfFromArrayBuffer(loader, modelArrayBuffer, element.dataset.modelUrl)
        if (!cacheHit) updateLoadingMessage("Building scene materials...")
        const model = gltf.scene
        state.model = model
        state.modelBasePositionY = model.position.y
        state.modelGroundSnapOffsetY = measureGroundSnapOffsetY(model)

        model.traverse((object) => {
          if (object === model) return

          const key = object.userData.dtNodeKey || object.userData.external_key || object.userData.externalKey || buildNodeKey(object, model)
          object.userData.dtNodeKey = key
          object.userData.external_key = key
          state.nodesByKey.set(key, object)
        })

        scene.add(model)
      }

      if (!modelLoadedFromCache) updateLoadingMessage("Positioning camera and labels...")
      refreshModelFraming()
      applyViewerSettingsToScene({ refreshView: false, rebuildPlacements: false })
      applyDefaultView()
      await ensureVisibleReferenceLayerOverlays()
      renderInspector()
      setFeedback(
        state.model
          ? "Viewer ready. Turn on mapping mode to tag or place objects."
          : "No GLB uploaded. Reference files and 360 captures are still available.",
        "neutral"
      )
      removeLoadingPreview()
      animate()

      // The viewer is usable once the scene is framed and rendering; load mapping-side data in the background.
      removeLoadingState()
      embedBridge.notifyReady(embedSnapshot())

      const startupRefreshes = await Promise.allSettled([
        refreshMappingsFromServer(),
        refreshNodeMappingsFromServer(),
        refreshPlacementsFromServer(),
        refreshNavigationPointsFromServer(),
        refreshCaptureSessionAlignmentsFromServer()
      ])

      const startupErrors = [
        "Could not load mapping data",
        "Could not load integration mapping data",
        "Could not load placed object data",
        "Could not load navigation point data",
        "Could not load capture session alignment data"
      ]

      startupRefreshes.forEach((result, index) => {
        if (result.status === "rejected") {
          const message = result.reason instanceof Error ? result.reason.message : String(result.reason)
          setFeedback(`${startupErrors[index]}: ${message}`, "error")
        }
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error("Plan viewer startup failed", error)
      embedBridge.notifyError(errorMessage, { stage: "startup" })
      removeLoadingState()
      createOverlay(
        element,
        "plan-viewer-error",
        `Could not load ${element.dataset.modelName || "the model file"}: ${errorMessage}`
      )
    }
  })()
}
